import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { usePermissions } from '@/lib/permissions';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { ChartRenderer } from '@/components/report-builder/ChartRenderer';
import { TableRenderer } from '@/components/report-builder/TableRenderer';
import { AdvancedSqlPanel } from '@/components/explore/AdvancedSqlPanel';
import { SqlExamplesPanel } from '@/components/explore/SqlExamplesPanel';
import { ReportConversationDialog } from '@/components/explore/ReportConversationDialog';
import { RouteExplainDialog } from '@/components/explore/RouteExplainDialog';
import { ReviewQueuePanel } from '@/components/explore/ReviewQueuePanel';
import {
  Search, Sparkles, Loader2, ArrowRight, BarChart3, Table, FileText, TrendingUp,
  Bell, MessageSquare, Save, Share2, ChevronDown, ChevronRight, Clock, Info, Zap, Code, Hash, Play, Database, BookOpen, LayoutDashboard, Plus, HelpCircle, CalendarClock, X, Square, Compass, Microscope, Eraser, History as HistoryIcon, Pencil, Check, Flag, ClipboardCheck, Trash2
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { pivotForChart, ROLE_PROMPTS } from '@/components/explore/exploreUtils';
import { resolveLabel } from '@/lib/fieldLabel';
import { ExploreMessage } from '@/components/explore/ExploreMessage';
import { AsOfPopover } from '@/components/explore/AsOfPopover';
import { AiFormulaDialog } from '@/components/semantic-models/AiFormulaDialog';
import { ConditionBuilder } from '@/components/ConditionBuilder';
import { parseVizCommand } from '@/lib/exploreCommands';
import { SlashCommandMenu } from '@/components/explore/SlashCommandMenu';
import html2canvas from 'html2canvas';
import InvestigatePanel from '@/components/investigate/InvestigatePanel';
import { StartDiscussionDialog } from '@/components/DiscussionDialog';
import { EndorsementBadge } from '@/components/EndorsementBadge';
import { BadgeCheck, ShieldCheck, Volume2, VolumeX, Mic, Download } from 'lucide-react';
import VoiceCopilot from '@/components/explore/VoiceCopilot';
import ExportFrameworkDialog from '@/components/ExportFrameworkDialog';
import { useWorkspaceTitle } from '@/contexts/WorkspaceContext';
import { isChimeMuted, setChimeMuted } from '@/lib/chime';
import { getTerm, useTerms, useBrandName } from '@/lib/branding';
import ProcessHelp from '@/components/ProcessHelp';

export default function ExplorePage() {
  const navigate = useNavigate();
  useTerms();
  const { user } = useAuth();
  const { can: canPerm } = usePermissions();
  const canSqlRead = canPerm('ai_copilot:sql_read');
  const [searchParams, setSearchParams] = useSearchParams();
  const [question, setQuestion] = useState('');
  const [loading, setLoading] = useState(false);
  // Abort an in-flight analysis (Stop button): abortRef cancels the fetch(es),
  // cancelledRef tells late-resolving handlers (incl. the WS path) to bail.
  const abortRef = useRef(null);
  const cancelledRef = useRef(false);
  const lastQuestionRef = useRef('');   // last submitted question, for Stop → Re-ask
  const [sessionId, setSessionId] = useState('');
  const [messages, setMessages] = useState([]);
  // "/" slash-command palette (AI Explore): open state, the text typed after
  // the slash, and a small recently-used list for smart suggestions.
  const [slashOpen, setSlashOpen] = useState(false);
  const [slashQuery, setSlashQuery] = useState('');
  const [slashRecent, setSlashRecent] = useState([]);
  // Saved view presets (per-user, server-side) + the "save preset" dialog.
  const [vizPresets, setVizPresets] = useState([]);
  const [presetDialog, setPresetDialog] = useState({ open: false });
  const [dashboardPicker, setDashboardPicker] = useState({ open: false });
  const [alertDialog, setAlertDialog] = useState({ open: false });
  const [routeExplain, setRouteExplain] = useState({ open: false, rd: null });
  const [addPosition, setAddPosition] = useState('bottom');
  const [addTitle, setAddTitle] = useState('');
  const [models, setModels] = useState([]);
  const [selectedModel, setSelectedModel] = useState('');
  const [voiceOpen, setVoiceOpen] = useState(false);
  const brandName = useBrandName();
  const [voiceFeatureEnabled, setVoiceFeatureEnabled] = useState(true);
  const [navPhrases, setNavPhrases] = useState([]);
  const voiceEnabled = voiceFeatureEnabled && (typeof window !== 'undefined') && window.localStorage.getItem('inzara_voice_experimental') !== 'off';
  useEffect(() => { api.voiceConfig().then(c => { setVoiceFeatureEnabled(c?.enabled !== false); setNavPhrases(c?.nav_phrases || []); }).catch(() => {}); }, []);
  const [sessions, setSessions] = useState([]);
  const [investigations, setInvestigations] = useState([]);
  const [shareTarget, setShareTarget] = useState(null);
  const [shareEmail, setShareEmail] = useState('');
  const [sharing, setSharing] = useState(false);
  const [sessionsLoading, setSessionsLoading] = useState(true);
  const [contextOpen, setContextOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historySearch, setHistorySearch] = useState('');
  const [renamingId, setRenamingId] = useState(null);
  const [renameDraft, setRenameDraft] = useState('');
  const [deletingId, setDeletingId] = useState(null);
  const deleteSession = async (sid) => {
    try {
      await api.deleteExploreSession(sid);
      setSessions(prev => prev.filter(s => s.session_id !== sid));
      if (sid === sessionId) newSession();
      toast.success('Chat deleted');
    } catch (e) { toast.error(e.message || 'Could not delete chat'); }
    setDeletingId(null);
  };
  const saveRename = async (sid) => {
    const t = renameDraft.trim();
    try {
      await api.renameExploreSession(sid, t);
      setSessions(prev => prev.map(s => s.session_id === sid ? { ...s, title: t } : s));
    } catch (e) { toast.error(e.message || 'Could not rename chat'); }
    setRenamingId(null); setRenameDraft('');
  };
  const refreshSessions = useCallback(() => {
    api.exploreSessions().then(s => setSessions(s || [])).catch(() => {});
  }, []);
  const [analysisContext, setAnalysisContext] = useState(null);
  const reloadAnalysisContext = useCallback(() => {
    if (selectedModel) api.getAnalysisContext(selectedModel).then(setAnalysisContext).catch(() => setAnalysisContext(null));
  }, [selectedModel]);
  useEffect(() => {
    if (contextOpen && selectedModel) {
      api.getAnalysisContext(selectedModel).then(setAnalysisContext).catch(() => setAnalysisContext(null));
    }
     
  }, [contextOpen, selectedModel]);
  // Inline persona editing (model + role) from the Analysis Context panel.
  const canEditModelPersona = canPerm('semantic_models:write');
  const canEditRolePersona = canPerm('roles:write');
  const canCertify = canPerm('measures:certify');
  const [clearingCache, setClearingCache] = useState(false);
  const clearExploreCache = async () => {
    setClearingCache(true);
    try {
      const r = await api.clearExploreCache(selectedModel);
      toast.success(r.cleared ? `Cleared ${r.cleared} cached answer${r.cleared === 1 ? '' : 's'} — new asks are fresh` : 'AI answer cache was already clear');
    } catch (e) { toast.error(e.message || 'Could not clear the AI answer cache'); }
    finally { setClearingCache(false); }
  };
  const [editingPersona, setEditingPersona] = useState(null); // null | 'model' | 'role'
  const [personaDraft, setPersonaDraft] = useState('');
  const [savingPersona, setSavingPersona] = useState(false);
  const startEditPersona = (which, current) => { setEditingPersona(which); setPersonaDraft(current || ''); };
  const savePersona = async () => {
    setSavingPersona(true);
    try {
      if (editingPersona === 'model') {
        await api.updateSemanticModel(selectedModel, { ai_persona: personaDraft });
      } else if (editingPersona === 'role') {
        await api.updateRole(analysisContext?.role_persona?.role, { ai_persona: personaDraft });
      }
      toast.success('Persona updated');
      setEditingPersona(null);
      reloadAnalysisContext();
    } catch (err) { toast.error(err.message || 'Failed to update persona'); }
    finally { setSavingPersona(false); }
  };
  const [allFields, setAllFields] = useState([]);
  const [findings, setFindings] = useState([]);
  const [aiExportOpen, setAiExportOpen] = useState(false);
  const [summaryLoading, setSummaryLoading] = useState(false);

  // Assemble the AI² analysis export context from the current conversation.
  const buildAiExportContext = useCallback(() => {
    const lastAssistant = [...messages].reverse().find(m => m.role === 'assistant');
    const r = lastAssistant?.result || {};
    const q = lastAssistant?.question || [...messages].reverse().find(m => m.role === 'user')?.content || question || 'Analysis';
    const rows = Array.isArray(lastAssistant?.liveData) ? lastAssistant.liveData : (Array.isArray(r.sql_data) ? r.sql_data : []);
    const columns = rows.length ? Object.keys(rows[0]) : [];
    const visuals = rows.length ? [{
      id: 'ai_result', title: r.chart_type ? `${r.chart_type} — ${q}`.slice(0, 60) : 'Analysis Result',
      type: r.output_type || 'table', columns, rows, total_rows: rows.length,
      selector: '[data-testid="explore-latest-answer"]',
    }] : [];
    const findingLines = (findings || []).map(f => f.title ? `${f.title}${f.summary ? ' — ' + f.summary : ''}` : f.summary).filter(Boolean);
    const filters = [];
    if (analysisContext?.role_persona?.role) filters.push({ label: 'Persona', value: analysisContext.role_persona.role });
    return {
      title: (q || 'AI Analysis').slice(0, 80),
      defaultFilename: (q || 'ai-analysis').slice(0, 60),
      filters,
      asOf: null,
      narrative: {
        question: q,
        summary: lastAssistant?.content || r.answer || '',
        findings: findingLines,
        recommendations: (r.recommendations || []).map(x => typeof x === 'string' ? x : (x.text || x.title)).filter(Boolean),
        root_causes: (r.root_causes || r.drivers || []).map(x => typeof x === 'string' ? x : (x.text || x.title)).filter(Boolean),
        methodology: r.methodology || '',
      },
      governance: { certified: [], lineage: [], disclosure: '' },
      currentVisualId: visuals[0]?.id,
      selectedVisualIds: visuals.map(v => v.id),
      visuals,
      pageSelector: '[data-testid="explore-latest-answer"]',
    };
  }, [messages, findings, analysisContext, question]);

  const [summary, setSummary] = useState(null);
  const [modelChosen, setModelChosen] = useState(false);
  const [findingsSearch, setFindingsSearch] = useState('');
  const [anomalySuggestions, setAnomalySuggestions] = useState([]);
  const [anomalyLoading, setAnomalyLoading] = useState(false);
  const [rcaResult, setRcaResult] = useState(null);
  const [rcaLoading, setRcaLoading] = useState(false);
  const [investigateLoading, setInvestigateLoading] = useState(false);
  const [fullInvestigateOpen, setFullInvestigateOpen] = useState(false);
  const [fullInvestigateTarget, setFullInvestigateTarget] = useState(null);
  const [discDialog, setDiscDialog] = useState({ open: false, msg: null, point: null, screenshot: '' });
  const [modelEditedAt, setModelEditedAt] = useState(0);
  const startDiscussion = (msg, point = null, screenshot = '') => setDiscDialog({ open: true, msg, point, screenshot });
  const [discIndex, setDiscIndex] = useState({});
  useEffect(() => { api.getDiscussionIndex().then(r => setDiscIndex(r?.by_session || {})).catch(() => {}); }, []);
  const startFullInvestigation = (msg) => {
    const r = msg?.result || {};
    const metric = r.measures?.[0] || r.alert_suggestion?.metric;
    const label = allFields.find(f => f.name === metric)?.description || (metric ? metric.replace(/_/g, ' ') : 'this metric');
    setFullInvestigateTarget({
      metric,
      model_id: selectedModel,
      question: msg?.content || r.answer ? `Investigate: ${(msg?.userQuestion || r.answer || label)}`.slice(0, 200) : undefined,
      insight_title: `Root-cause: ${label}`,
      insight_summary: (r.answer || '').slice(0, 400),
      source: 'explore',
      autoStart: true,
    });
    setFullInvestigateOpen(true);
  };
  const [exploreQueryView, setExploreQueryView] = useState(null);
  const [aiRawView, setAiRawView] = useState(null);
  // Sticky filters (Phase 4b) — filters the user has pinned so they
  // survive every AI Explore refinement regardless of what the LLM
  // returns. Keyed as `${field}|${operator}`. Persisted per-session in
  // localStorage so a reload preserves them.
  const [pinnedFilters, setPinnedFilters] = useState([]);
  const [droppedPinnedNote, setDroppedPinnedNote] = useState(null);
  // Diff-of-intent — populated after every AI response with any fields
  // that vanished vs. the previous turn AND that the user's message did
  // not mention. Cleared when the user acknowledges or moves on.
  const [diffOfIntent, setDiffOfIntent] = useState(null);
  // AI credit check
  const [aiCredits, setAiCredits] = useState(null);
  // Collaborative session state
  const [sharedToken, setSharedToken] = useState(searchParams.get('shared') || '');
  const [isSharedSession, setIsSharedSession] = useState(false);
  const [presence, setPresence] = useState({});
  const [rcaExpanded, setRcaExpanded] = useState(new Set([1, 2, 3]));
  const messagesEndRef = useRef(null);
  const summaryRef = useRef(null);
  const inputRef = useRef(null);
  const wsRef = useRef(null);
  const [wsConnected, setWsConnected] = useState(false);
  const [typingUsers, setTypingUsers] = useState({});
  // Ref tracking the latest selectedModel for stable access inside long-lived WS handlers
  const selectedModelRef = useRef('');
  useEffect(() => { selectedModelRef.current = selectedModel; }, [selectedModel]);


  // Reflect the chosen model in the URL so each model's Explore is its own
  // Workbench session (?model=<id>). Replace (no history spam). Also persist it
  // so global search + "Open in Explore" default to the SAME model as here.
  const [pendingSuggestions, setPendingSuggestions] = useState(0);
  useEffect(() => {
    if (!selectedModel) return;
    localStorage.setItem('inzara.exploreModel', selectedModel);
    api.countTeachSuggestions(selectedModel).then(r => setPendingSuggestions(r?.count || 0)).catch(() => setPendingSuggestions(0));
    if (searchParams.get('model') === selectedModel) return;
    const next = new URLSearchParams(searchParams);
    next.set('model', selectedModel);
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedModel]);

  // Persist the active server session id in the URL (?sid=) so navigating away
  // and back — or a hard refresh — restores the exact conversation instead of
  // resetting to a blank Explore. The Workbench remembers this URL for the tab.
  useEffect(() => {
    if (!sessionId || isSharedSession) return;
    if (searchParams.get('s') === sessionId) return;
    const next = new URLSearchParams(searchParams);
    next.set('s', sessionId);
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  // Label this AI Explore workspace tab with the active model name.
  const _wsModelName = models.find(m => m.model_id === selectedModel)?.name;
  const [chimeMuted, setChimeMutedState] = useState(isChimeMuted());
  // Once the user asks their first question, label the Workbench chip with it;
  // before that, fall back to the model name.
  const _wsFirstQuestion = messages.find(m => m.role === 'user' && m.content)?.content;
  useWorkspaceTitle(
    _wsFirstQuestion
      ? _wsFirstQuestion.slice(0, 80)
      : (modelChosen && _wsModelName ? `Explore · ${_wsModelName}` : 'AI Explore')
  );

  // (Explore conversations auto-persist as sessions, and the Workbench keeps
  // them alive — so navigating away is safe and no longer prompts. This lets
  // users work elsewhere while the AI answers in the background.)

  useEffect(() => {
    // A sample-dataset wizard redirected here — re-show its success toast.
    const t = sessionStorage.getItem('sampleSetupToast');
    if (t) { toast.success(t); sessionStorage.removeItem('sampleSetupToast'); }
  }, []);

  // Load pinned filters from localStorage when the session changes.
  useEffect(() => {
    if (!sessionId) { setPinnedFilters([]); return; }
    try {
      const raw = localStorage.getItem(`inzara.pinnedFilters.${sessionId}`);
      setPinnedFilters(raw ? JSON.parse(raw) : []);
    } catch (e) { setPinnedFilters([]); /* corrupted JSON — reset */ }
  }, [sessionId]);

  // Persist pinned filters whenever they change.
  useEffect(() => {
    if (!sessionId) return;
    try {
      localStorage.setItem(`inzara.pinnedFilters.${sessionId}`, JSON.stringify(pinnedFilters));
    } catch (e) { /* ignore quota */ }
  }, [pinnedFilters, sessionId]);

  const filterKey = (f) => `${f.field}|${f.operator}`;
  // Merge pinned filters on top of LLM-returned filters. Pinned wins on
  // conflict (same field+operator) so a user pin can never be silently
  // overwritten by a follow-up turn.
  const mergePinnedFilters = (llmFilters) => {
    const arr = Array.isArray(llmFilters) ? [...llmFilters] : [];
    const existing = new Set(arr.map(filterKey));
    const dropped = [];
    for (const p of pinnedFilters) {
      if (existing.has(filterKey(p))) continue;
      arr.push(p);
      dropped.push(p);
    }
    return { merged: arr, dropped };
  };

  // Role-aware prompts (fallback)
  const userRole = user?.title?.toLowerCase() || user?.role || 'default';
  const fallbackPrompts = ROLE_PROMPTS[Object.keys(ROLE_PROMPTS).find(k => userRole.includes(k))] || ROLE_PROMPTS.default;

  // Dynamic suggestions per model
  const [dynamicSuggestions, setDynamicSuggestions] = useState([]);
  const [countStarters, setCountStarters] = useState([]);
  const [showAdvancedSql, setShowAdvancedSql] = useState(false);
  const [showSqlExamples, setShowSqlExamples] = useState(false);
  // Refresh the pending-suggestions badge whenever the SQL Library panel closes
  // (via X, Esc, or outside-click) so an approval/reject reflects immediately.
  useEffect(() => {
    if (!showSqlExamples && selectedModel) {
      api.countTeachSuggestions(selectedModel).then(r => setPendingSuggestions(r?.count || 0)).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showSqlExamples]);
  const [advancedSqlSeed, setAdvancedSqlSeed] = useState('');
  const openAdvancedSql = useCallback((seed) => { setAdvancedSqlSeed(seed || ''); setShowAdvancedSql(true); }, []);
  const isAdmin = ['admin', 'super_admin'].includes(user?.role);
  const isReviewer = ['admin', 'super_admin', 'tenant_admin', 'owner', 'steward', 'data_steward'].includes(user?.role);
  const [showReport, setShowReport] = useState(false);
  const [showReviewQueue, setShowReviewQueue] = useState(false);
  const [reviewOpenCount, setReviewOpenCount] = useState(0);
  useEffect(() => {
    if (!isReviewer || !selectedModel) return;
    api.listReviewCases(selectedModel).then(r => setReviewOpenCount(r?.open_count || 0)).catch(() => {});
  }, [isReviewer, selectedModel, showReviewQueue, showReport]);

  // After an answer renders, fetch 4 LLM follow-up questions (best-effort) and
  // attach them to the message so they show as clickable pills under the chart.
  const followUpFetchedRef = useRef(new Set());
  useEffect(() => {
    const li = messages.length - 1;
    const last = messages[li];
    if (!last || last.role !== 'assistant' || !last.result || isSharedSession) return;
    const r = last.result;
    if (Array.isArray(r.follow_up_suggestions) && r.follow_up_suggestions.length > 0) return;
    if (!['chart', 'table', 'kpi', 'forecast'].includes(r.output_type)) return;
    if (!r.answer && !(r.measures && r.measures.length)) return;
    const key = `${li}:${(r.answer || '').slice(0, 40)}`;
    if (followUpFetchedRef.current.has(key)) return;
    followUpFetchedRef.current.add(key);
    api.exploreFollowUps({ question: last.question || last.content || '', answer: r.answer || '', dimensions: r.dimensions || [], measures: r.measures || [] })
      .then(res => {
        const sugg = res?.suggestions || [];
        if (!sugg.length) return;
        setMessages(prev => prev.map((m, i) => (i === li ? { ...m, result: { ...m.result, follow_up_suggestions: sugg } } : m)));
      })
      .catch(() => {});
  }, [messages, isSharedSession]);

  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  const suggestedPrompts = dynamicSuggestions.length > 0 ? dynamicSuggestions : fallbackPrompts;

  // Endorsement preference — push certified models to the top of the picker
  // and optionally hide unendorsed ones. Persisted so power-users can pin
  // "Certified only" as the default behavior.
  const [preferEndorsed, setPreferEndorsed] = useState(() => localStorage.getItem('exploreOnlyEndorsed') === '1');
  // "As of" date for to-date (YTD/QTD/MTD/ITD) answers in this session — a past
  // date shifts every to-date/as-of value the AI returns. Empty = today.
  const [exploreAsOfDate, setExploreAsOfDate] = useState('');
  // "Measured against" — which date COLUMN the to-date window runs on. Empty =
  // the model's default date field. Set together with the as-of date via the
  // just-in-time prompt on the first to-date question of a session.
  const [exploreAsOfField, setExploreAsOfField] = useState('');
  const [modelDateFields, setModelDateFields] = useState([]); // [{name,label}]
  const [modelDefaultDateField, setModelDefaultDateField] = useState('');
  const [fiscalStartMonth, setFiscalStartMonth] = useState(1);
  const [asOfPromptOpen, setAsOfPromptOpen] = useState(false);
  const [asOfResumeTick, setAsOfResumeTick] = useState(0);
  const [asOfPinned, setAsOfPinned] = useState(false);
  const [aiMeasureOpen, setAiMeasureOpen] = useState(false);
  const [aiMeasureSeed, setAiMeasureSeed] = useState('');
  const openAiMeasure = useCallback(() => {
    const lastUser = [...messages].reverse().find(m => m.role === 'user');
    setAiMeasureSeed(lastUser?.content || '');
    setAiMeasureOpen(true);
  }, [messages]);
  const pendingAskRef = useRef(null); // question stashed while the prompt is open
  const asOfPinnedRef = useRef(false); // true once the session's as-of is chosen
  const asOfDateRef = useRef('');
  const asOfFieldRef = useRef('');
  const forceClarifyRef = useRef(null); // {assumption} → next ask forces the exact-field clarify
  useEffect(() => { asOfDateRef.current = exploreAsOfDate; }, [exploreAsOfDate]);
  useEffect(() => { asOfFieldRef.current = exploreAsOfField; }, [exploreAsOfField]);
  const togglePreferEndorsed = () => {
    const next = !preferEndorsed;
    setPreferEndorsed(next);
    localStorage.setItem('exploreOnlyEndorsed', next ? '1' : '0');
  };
  // Answer Quality Filter — when on, a low-quality answer (from the AI's
  // self-critique) is auto re-asked once for a stronger response.
  const [autoImprove, setAutoImprove] = useState(() => localStorage.getItem('exploreAutoImprove') === '1');
  const autoImproveRef = useRef(autoImprove);
  useEffect(() => { autoImproveRef.current = autoImprove; }, [autoImprove]);
  const toggleAutoImprove = () => {
    const next = !autoImprove;
    setAutoImprove(next);
    localStorage.setItem('exploreAutoImprove', next ? '1' : '0');
    toast.info(next ? 'Weak answers will be auto-improved' : 'Auto-improve off');
  };

  // Certified-only view — hides ad-hoc/mixed answers behind a reveal so users
  // can glance at only governed, certified results. Purely client-side.
  const [certifiedOnly, setCertifiedOnly] = useState(() => localStorage.getItem('exploreCertifiedOnly') === '1');
  const [revealedAdhoc, setRevealedAdhoc] = useState(() => new Set());
  const toggleCertifiedOnly = () => {
    const next = !certifiedOnly;
    setCertifiedOnly(next);
    localStorage.setItem('exploreCertifiedOnly', next ? '1' : '0');
    if (next) setRevealedAdhoc(new Set());
    toast.info(next ? 'Showing certified answers only — ad-hoc results are hidden' : 'Showing all answers');
  };

  const endorseRank = (m) => {
    const lvl = m?.endorsement?.level || 'none';
    if (lvl === 'certified') return 0;
    if (lvl === 'promoted')  return 1;
    return 2;
  };
  const visibleModels = (() => {
    let out = [...models];
    if (preferEndorsed) {
      // Strict filter — only endorsed models are visible.
      out = out.filter(m => ['certified', 'promoted'].includes(m?.endorsement?.level));
    }
    // Stable sort: endorsed first regardless of `preferEndorsed`, so even
    // when "Any" is selected, certified content surfaces above unranked.
    out.sort((a, b) => endorseRank(a) - endorseRank(b));
    return out;
  })();
  const endorsedCount = models.filter(m => ['certified', 'promoted'].includes(m?.endorsement?.level)).length;

  useEffect(() => {
    Promise.all([api.getSemanticModels().catch(() => []), api.exploreSessions().catch(() => []), api.exploreFindings().catch(() => [])])
      .then(([m, s, f]) => {
        setModels(m); setSessions(s); setFindings(f);
        api.exploreInvestigations().then(r => setInvestigations(r.investigations || [])).catch(() => {});
        if (m.length > 0) {
          const urlModel = searchParams.get('model');
          const stored = localStorage.getItem('inzara.exploreModel');
          const initial = (urlModel && m.some(x => x.model_id === urlModel)) ? urlModel
            : (stored && m.some(x => x.model_id === stored)) ? stored
            : m[0].model_id;
          setSelectedModel(initial);
        }
        // Load AI credits
        if (user?.tenant_id) api.getTenantAiCredits(user.tenant_id).then(c => setAiCredits(c)).catch(() => {});
        // Check for auto-ask from Home page Quick Insights
        const autoAsk = sessionStorage.getItem('exploreAutoAsk');
        if (autoAsk) {
          sessionStorage.removeItem('exploreAutoAsk');
          try {
            const { model_id, question: q } = JSON.parse(autoAsk);
            if (model_id && m.some(mm => mm.model_id === model_id)) {
              setSelectedModel(model_id);
              setModelChosen(true);
              setQuestion(q);
              setTimeout(() => ask(q), 500);
            }
          } catch { /* ignore */ }
        }
      })
      .finally(() => setSessionsLoading(false));
  }, []);

  // Hydrate an Explore session from a discussion deep-link
  // (/ai-explore?discussion=<id>). We fetch the discussion server-side,
  // pull its captured model_id + filters + prompt out of `data_context`
  // and `report_state`, seed the pinned filters so they can't be lost on
  // follow-up, then auto-run the original prompt.
  const discussionParam = searchParams.get('discussion');
  useEffect(() => {
    if (!discussionParam) return;
    let cancelled = false;
    (async () => {
      try {
        const d = await api.getDiscussion(discussionParam);
        if (cancelled) return;
        const dc = d.data_context || {};
        const rs = d.report_state || {};
        if (rs.source !== 'explore') return;
        if (dc.model_id) {
          setSelectedModel(dc.model_id);
          setModelChosen(true);
        }
        if (Array.isArray(dc.filters_applied) && dc.filters_applied.length > 0) {
          setPinnedFilters(dc.filters_applied);
        }
        const prompt = rs.prompt || d.question || '';
        if (prompt) {
          setQuestion(prompt);
          // Small delay so state (model/filters) commits before ask() runs.
          setTimeout(() => { if (!cancelled) ask(prompt); }, 500);
        }
        toast.success('Restored view from discussion');
      } catch (e) {
        toast.error('Could not restore discussion context');
      }
    })();
    return () => { cancelled = true; };
  }, [discussionParam]);

  // Load shared session from URL param
  useEffect(() => {
    if (!sharedToken) return;
    const loadShared = async () => {
      try {
        const s = await api.getSharedSession(sharedToken);
        setSessionId(s.session_id);
        setSelectedModel(s.model_id || '');
        setModelChosen(true);
        setIsSharedSession(true);
        setPresence(s.presence || {});
        // Hydrate messages with live data
        const modelId = s.model_id;
        const msgs = s.messages || [];
        const hydrated = await Promise.all(msgs.map(async (msg) => {
          if (msg.role === 'assistant' && msg.result?.sql_mode) {
            return { ...msg, liveData: Array.isArray(msg.result.sql_data) ? msg.result.sql_data : (msg.liveData || []) };
          }
          if (msg.role === 'assistant' && msg.result?.dimensions?.length > 0 && modelId) {
            try {
              const dims = msg.result.dimensions || [];
              const msrs = msg.result.measures || [];
              const dateGrains = msg.result.date_grains || null;
              const filters = Array.isArray(msg.result.filters_applied) ? msg.result.filters_applied : [];
              const queryRes = await api.executeQuery({ model_id: modelId, dimensions: dims, measures: msrs, filters, limit: 200, date_grains: dateGrains, measure_overrides: msg.result.measure_aggregations || {}, calculations: msg.result.calculations || [], as_of_date: exploreAsOfDate || undefined });
              return { ...msg, liveData: queryRes?.data || null };
            } catch { return msg; }
          }
          return msg;
        }));
        setMessages(hydrated);
      } catch (err) { toast.error('Failed to load shared session'); }
    };
    loadShared();
  }, [sharedToken]);

  // WebSocket connection for real-time collaboration
  useEffect(() => {
    if (!isSharedSession || !sessionId) return;
    
    // Cookies are sent automatically during WebSocket handshake
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsUrl = `${protocol}//${window.location.host}/ws/explore/${sessionId}`;
    
    try {
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;
      
      ws.onopen = () => {
        setWsConnected(true);
        // Send ping every 30s to keep alive
        const pingInterval = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ type: 'ping' }));
        }, 30000);
        ws._pingInterval = pingInterval;
      };
      
      ws.onmessage = async (event) => {
        const data = JSON.parse(event.data);
        
        if (data.type === 'presence') {
          setPresence(data.presence || {});
        } else if (data.type === 'typing') {
          setTypingUsers(prev => ({ ...prev, [data.user.id]: data.typing ? data.user.name : null }));
          if (data.typing) {
            setTimeout(() => setTypingUsers(prev => ({ ...prev, [data.user.id]: null })), 5000);
          }
        } else if (data.type === 'new_message') {
          setMessages(prev => [...prev, data.message]);
        } else if (data.type === 'ai_response') {
          // If the user hit Stop while waiting on the shared-session socket,
          // drop the late answer and stay idle.
          if (cancelledRef.current) { cancelledRef.current = false; setLoading(false); return; }
          // Hydrate with live data — read latest selectedModel via ref to avoid stale closure
          const msg = data.message;
          const currentModel = selectedModelRef.current;
          if (msg.result?.dimensions?.length > 0 && currentModel) {
            try {
              const dims = msg.result.dimensions || [];
              const msrs = msg.result.measures || [];
              const dateGrains = msg.result.date_grains || null;
              const filters = Array.isArray(msg.result.filters_applied) ? msg.result.filters_applied : [];
              const queryRes = await api.executeQuery({ model_id: currentModel, dimensions: dims, measures: msrs, filters, limit: 200, date_grains: dateGrains, measure_overrides: msg.result.measure_aggregations || {}, calculations: msg.result.calculations || [], as_of_date: exploreAsOfDate || undefined });
              setMessages(prev => [...prev, { ...msg, liveData: queryRes?.data || null }]);
            } catch { setMessages(prev => [...prev, msg]); }
          } else {
            setMessages(prev => [...prev, msg]);
          }
        }
      };
      
      ws.onclose = () => {
        setWsConnected(false);
        clearInterval(ws._pingInterval);
      };
      ws.onerror = () => setWsConnected(false);
      
      return () => {
        clearInterval(ws._pingInterval);
        ws.close();
        wsRef.current = null;
      };
    } catch {
      setWsConnected(false);
    }
  }, [isSharedSession, sessionId]);

  // Fallback polling (only if WebSocket isn't connected)
  useEffect(() => {
    if (!isSharedSession || !sharedToken || wsConnected) return;
    const poll = setInterval(async () => {
      try {
        const s = await api.getSharedSession(sharedToken);
        setPresence(s.presence || {});
        const serverMsgCount = (s.messages || []).length;
        if (serverMsgCount > messages.length) {
          const newMsgs = (s.messages || []).slice(messages.length);
          const modelId = s.model_id;
          const hydrated = await Promise.all(newMsgs.map(async (msg) => {
            if (msg.role === 'assistant' && msg.result?.dimensions?.length > 0 && modelId) {
              try {
                const dims = msg.result.dimensions || [];
                const msrs = msg.result.measures || [];
                const dateGrains = msg.result.date_grains || null;
                const queryRes = await api.executeQuery({ model_id: modelId, dimensions: dims, measures: msrs, limit: 200, date_grains: dateGrains, measure_overrides: msg.result.measure_aggregations || {}, calculations: msg.result.calculations || [], as_of_date: exploreAsOfDate || undefined });
                return { ...msg, liveData: queryRes?.data || null };
              } catch { return msg; }
            }
            return msg;
          }));
          setMessages(prev => [...prev, ...hydrated]);
        }
      } catch { /* ignore */ }
    }, 3000);
    return () => clearInterval(poll);
  }, [isSharedSession, sharedToken, wsConnected, messages.length]);


  useEffect(() => {
    if (!selectedModel) return;
    // New model → clear the pinned as-of so the prompt re-asks for it once.
    setExploreAsOfDate(''); setExploreAsOfField(''); setAsOfPinned(false); asOfPinnedRef.current = false;
    api.getSemanticModelFull(selectedModel).then(m => {
      const f = [];
      const byName = {};
      // Skip invisible aggregate bindings — their attributes carry raw physical
      // names (display_name === column) with no business_name, which would
      // shadow the canonical field's curated label. Prefer the attribute that
      // actually has a business_name when the same field appears twice.
      (m.entities || []).forEach(e => {
        if (e.aggregation_role === 'aggregate') return;
        (e.attributes || []).forEach(a => {
          const existing = byName[a.name];
          if (!existing) { byName[a.name] = a; f.push(a); }
          else if (!existing.business_name && a.business_name) {
            const i = f.indexOf(existing);
            if (i >= 0) f[i] = a;
            byName[a.name] = a;
          }
        });
      });
      // Calculated/derived measures live at the top level (not on an entity).
      // Include them so they resolve by name (labels, save-to-insight name
      // normalization) and appear in the measure picker.
      (m.calculated_measures || m.calc_measures || m.measures || []).forEach(cm => {
        if (!cm || !cm.name || byName[cm.name]) return;
        const rec = { ...cm, semantic_type: 'measure', is_calculated: true };
        byName[cm.name] = rec;
        f.push(rec);
      });
      setAllFields(f);
      // Governed date fields (Model Date Defaults) — default first, then allowed
      // alternates. Drives the "Measured against" control in the as-of prompt.
      const dnames = []; let defField = '';
      // Resolve the friendly label from the field's own model metadata first
      // (business_name → display_name), matching the backend precedence; fall
      // back to the allFields record, then the physical name only as last resort.
      const attrByName = {};
      (m.entities || []).forEach(e => (e.attributes || []).forEach(a => { if (a && a.name && !attrByName[a.name]) attrByName[a.name] = a; }));
      const labelFor = (nm) => {
        const a = attrByName[nm];
        const ff = f.find(x => x.name === nm);
        return (a?.business_name) || (a?.display_name) || (ff?.business_name) || (ff?.display_name) || nm;
      };
      [...(m.entities || [])].sort((a, b) => (a.entity_type === 'fact' ? -1 : 0) - (b.entity_type === 'fact' ? -1 : 0)).forEach(e => {
        if (e.default_date_field && !dnames.includes(e.default_date_field)) { dnames.push(e.default_date_field); if (!defField) defField = e.default_date_field; }
        (e.allowed_date_fields || []).forEach(x => { if (x && !dnames.includes(x)) dnames.push(x); });
      });
      setModelDateFields(dnames.map(nm => ({ name: nm, label: labelFor(nm) })));
      setModelDefaultDateField(defField);
      setFiscalStartMonth(Number(m.fiscal_year_start_month) || 1);
    }).catch(() => {});
    // Fetch dynamic suggestions for this model
    setSuggestionsLoading(true);
    api.exploreSuggestions({ model_id: selectedModel }).then(res => {
      if (res.suggestions?.length > 0) setDynamicSuggestions(res.suggestions);
      setCountStarters(res.count_starters || []);
    }).catch(() => {}).finally(() => setSuggestionsLoading(false));
  }, [selectedModel, modelChosen]);

  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);
  // Keep the history panel current as new turns land (a fresh thread appears there).
  useEffect(() => { if (historyOpen) refreshSessions(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [messages.length]);
  useEffect(() => { if (summary) summaryRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }); }, [summary]);

   
  // ── Deterministic view/query handler (no AI) ─────────────────────────
  // Persist a per-message viz_state change (toolbar tweaks). Best-effort.
  const handleVizChange = useCallback((mIdx, patch) => {
    let mergedForPersist = null;
    setMessages(prev => prev.map((m, i) => {
      if (i !== mIdx) return m;
      const merged = { ...(m.viz_state || {}), ...patch };
      mergedForPersist = merged;
      return { ...m, viz_state: merged };
    }));
    if (sessionId && mergedForPersist) {
      api.updateMessageViz(sessionId, mIdx, mergedForPersist).catch(() => {});
    }
  }, [sessionId]);

  // Try to satisfy a typed follow-up locally (chart tweak or mechanical
  // re-query). Returns true when handled — the caller then skips the AI.
  const tryDeterministicCommand = useCallback(async (prompt) => {
    const lastResultMsg = [...messages].reverse().find(m => m.role === 'assistant' && m.result);
    // A follow-up on a forecast (e.g. "add line of business", "break down by …",
    // "make it monthly") must re-run the forecast engine, not a plain re-query —
    // let it fall through to ask() → the backend forecast continuation path.
    if (lastResultMsg?.result?.output_type === 'forecast') return false;
    // SQL-warehouse results carry raw SQL column names, so a SEMANTIC re-query
    // can't refine them — those follow-ups fall through to ask() (SQL path). But
    // a pure VIZ change (chart type, legend, colours …) just re-renders the rows
    // we already have, so it must stay client-side and never re-plan the SQL.
    const lastAssistant = [...messages].reverse().find(
      m => m.role === 'assistant' && m.result && (m.result.dimensions?.length || m.result.chart_type)
    );
    if (!lastAssistant) return false;
    const r = lastAssistant.result;
    // SQL-warehouse results carry raw SQL column names (e.g. a date alias "YEAR"),
    // so a SEMANTIC re-query can't refine them — any add/remove/re-ask on a
    // SQL-mode result MUST fall through to ask() (the SQL path). Derive the flag
    // from BOTH the most-recent result and the exact result being refined so it
    // survives across viz/table toggles and never leaks into executeQuery.
    const isSqlMode = !!(r?.sql_mode) || !!(lastResultMsg?.result?.sql_mode);
    const currentDims = (r.dimensions || []).map(n => ({ name: n, label: n }));
    // A chart may colour by a field carried in `color_by` (or a prior viz patch)
    // rather than in `dimensions` — include it so "colour by <that field>" is
    // recognised as an instant re-colour of an already-plotted field (no AI).
    const _colorByField = lastAssistant.viz_state?.colorBy || r.color_by;
    if (_colorByField && !currentDims.some(d => d.name === _colorByField)) {
      currentDims.push({ name: _colorByField, label: _colorByField });
    }
    const currentMeasures = (r.measures || []).map(n => ({ name: n, label: n }));
    // Pass business/display names AND saved synonyms so a swap like "swap X with
    // state" resolves instantly when a field carries the synonym "state" — no AI.
    const _fieldOpt = (f) => ({ name: f.name, label: f.business_name || f.display_name || f.label || f.name, business_name: f.business_name, display_name: f.display_name, synonyms: f.synonyms || [] });
    const availableDims = (allFields || []).filter(f => f.semantic_type !== 'measure' && !f.is_calculated).map(_fieldOpt);
    const availableMeasures = (allFields || []).filter(f => f.semantic_type === 'measure' || f.is_calculated).map(_fieldOpt);
    const dateField = (r.dimensions || []).find(d => /date|year|month|day|week|quarter|_dt$|book_end/i.test(d)) || null;
    // The user question that produced this result (added to messages BEFORE
    // this deterministic check runs) — used to build a natural rewrite for swaps.
    const lastQuestion = [...messages].reverse().find(m => m.role === 'user' && m.content)?.content || '';

    const cmd = parseVizCommand(prompt, {
      hasResult: true, hasDateDim: !!dateField, lastQuestion,
      availableDims, availableMeasures, currentDims, currentMeasures,
      currentChartType: (lastAssistant.viz_state?.chartType) || r.chart_type || r.output_type || '',
    });
    if (!cmd) return false;
    // A3 — a presentation request we can't apply: say so + what we CAN do,
    // instead of silently ignoring it or sending it to the AI.
    if (cmd.kind === 'cannot') {
      const userMsg = { role: 'user', content: prompt, author: user?.name || '', author_id: user?.user_id };
      setMessages(prev => [...prev, userMsg, { role: 'assistant', content: cmd.message || "I can't apply that to this chart.", result: { output_type: 'text', answer: cmd.message || "I can't apply that to this chart." }, _cannotApply: true }]);
      setQuestion('');
      toast(cmd.label || "Can't apply that", { description: cmd.message });
      return true;
    }
    // SQL-mode answers: only pure viz changes are handled locally; any re-query
    // or re-ask goes back to the AI (which has the full SQL session context).
    if (isSqlMode && cmd.kind !== 'viz') return false;
    // A swap we couldn't resolve deterministically → let the caller re-ask the
    // AI a naturalized version of the question instead of the literal command.
    if (cmd.kind === 'reask') return { reask: cmd.prompt, label: cmd.label };

    const userMsg = { role: 'user', content: prompt, author: user?.name || '', author_id: user?.user_id };

    if (cmd.kind === 'viz') {
      if (!Array.isArray(lastAssistant.liveData) || lastAssistant.liveData.length === 0) return false;
      const viz_state = { ...(lastAssistant.viz_state || {}), ...cmd.vizPatch };
      const card = {
        role: 'assistant', content: `↳ ${cmd.label}`,
        result: { ...r }, liveData: lastAssistant.liveData,
        debugQuery: lastAssistant.debugQuery, viz_state, _viz_variant: true,
      };
      setMessages(prev => [...prev, userMsg, card]);
      setQuestion('');
      toast(`View updated · ${cmd.label}`, {
        action: { label: 'Pin to dashboard', onClick: () => saveAsInsight(card.result, viz_state) },
      });
      if (sessionId) {
        api.persistVizTurn(sessionId, {
          user_content: prompt,
          assistant: { content: card.content, result: card.result, viz_state },
        }).catch(() => {});
      }
      return true;
    }

    // ── re-query ──
    const qp = cmd.queryPatch;
    let dimensions = [...(r.dimensions || [])];
    let measures = [...(r.measures || [])];
    const date_grains = { ...(r.date_grains || {}) };
    if (qp.dateGrain && dateField) date_grains[dateField] = qp.dateGrain;
    if (qp.addField) {
      if (qp.addKind === 'measure') { if (!measures.includes(qp.addField)) measures.push(qp.addField); }
      else if (!dimensions.includes(qp.addField)) dimensions.push(qp.addField);
    }
    if (qp.removeField) {
      dimensions = dimensions.filter(d => d !== qp.removeField);
      measures = measures.filter(m => m !== qp.removeField);
    }
    const limit = qp.limit || 200;
    if (dimensions.length === 0 && measures.length === 0) return false;

    setMessages(prev => [...prev, userMsg]);
    setQuestion('');
    setLoading(true);
    try {
      const filters = Array.isArray(r.filters_applied) ? r.filters_applied : [];
      const queryRes = await api.executeQuery({
        model_id: selectedModel, dimensions, measures, filters, limit,
        date_grains, measure_overrides: r.measure_aggregations || {}, calculations: r.calculations || [],
      });
      const newResult = { ...r, dimensions, measures, date_grains };
      const card = {
        role: 'assistant', content: `↳ ${cmd.label}`, result: newResult,
        liveData: queryRes?.data || [], debugQuery: queryRes?._debug_query || null,
        viz_state: { ...(lastAssistant.viz_state || {}), ...(cmd.vizPatch || {}) }, _viz_variant: true,
        // Show the "Swapped X → Y" confirmation chip for a deterministic swap.
        ...(/^Swapped\b/.test(cmd.label || '') ? { _swapNote: cmd.label } : {}),
      };
      setMessages(prev => [...prev, card]);
      toast(`Re-queried · ${cmd.label}`, {
        action: { label: 'Pin to dashboard', onClick: () => saveAsInsight(card.result, card.viz_state) },
      });
      if (sessionId) {
        api.persistVizTurn(sessionId, {
          user_content: prompt,
          assistant: { content: card.content, result: newResult, viz_state: card.viz_state },
        }).catch(() => {});
      }
    } catch (err) {
      setMessages(prev => [...prev, { role: 'assistant', content: '', queryError: err.message, result: {} }]);
    } finally {
      setLoading(false);
    }
    return true;
  }, [messages, allFields, user, sessionId, selectedModel]);

  // Grain Picker — flip a result's date grain (day/week/month/quarter/year) on
  // the business date without re-asking the AI. Deterministic re-query of the
  // same fields; appends a new result card (consistent with typed "by month").
  const changeGrain = useCallback(async (msg, grain) => {
    const r = msg?.result;
    if (!r) return;
    // SQL-mode results aren't re-queryable via the semantic engine — re-ask
    // the SQL path with an explicit grain instruction instead.
    if (r.sql_mode) { ask(`${msg.question || 'that'} by ${grain}`); return; }
    const dims = r.dimensions || [];
    const dateField = dims.find(d => /date|year|month|day|week|quarter|_dt$|book_end/i.test(d));
    if (!dateField) return;
    const date_grains = { ...(r.date_grains || {}), [dateField]: grain };
    setLoading(true);
    try {
      const filters = Array.isArray(r.filters_applied) ? r.filters_applied : [];
      const queryRes = await api.executeQuery({
        model_id: selectedModel, dimensions: dims, measures: r.measures || [], filters, limit: 200,
        date_grains, measure_overrides: r.measure_aggregations || {}, calculations: r.calculations || [],
      });
      const newResult = { ...r, date_grains };
      const card = {
        role: 'assistant', content: `↳ By ${grain}`, result: newResult,
        liveData: queryRes?.data || [], debugQuery: queryRes?._debug_query || null,
        viz_state: { ...(msg.viz_state || {}) }, _viz_variant: true,
      };
      setMessages(prev => [...prev, card]);
      toast(`Re-grained · by ${grain}`, {
        action: { label: 'Pin to dashboard', onClick: () => saveAsInsight(card.result, card.viz_state) },
      });
      if (sessionId) {
        api.persistVizTurn(sessionId, {
          user_content: `by ${grain}`,
          assistant: { content: card.content, result: newResult, viz_state: card.viz_state },
        }).catch(() => {});
      }
    } catch (err) {
      setMessages(prev => [...prev, { role: 'assistant', content: '', queryError: err.message, result: {} }]);
    } finally {
      setLoading(false);
    }
  }, [selectedModel, sessionId]);

  // Thumbs-down rerun: render the revised answer as a NORMAL result card (chart
  // or table) so the user can visually compare, not just a text/plan note.
  const appendRevisedResult = useCallback(async (result, reason, originalQuestion) => {
    if (!result) return;
    let liveData = Array.isArray(result.sql_data) ? result.sql_data
      : (Array.isArray(result.data) ? result.data : []);
    let debugQuery = result.sql ? { sql: result.sql } : null;
    // A plan-only (non-SQL) revision has no rows yet — execute it so the chart
    // has data to render.
    if (!result.sql_mode && liveData.length === 0 && selectedModel
        && ((result.dimensions?.length || 0) + (result.measures?.length || 0) > 0)) {
      try {
        const qr = await api.executeQuery({
          model_id: selectedModel, dimensions: result.dimensions || [], measures: result.measures || [],
          filters: Array.isArray(result.filters_applied) ? result.filters_applied : [],
          limit: result.limit ? Number(result.limit) : 200,
          date_grains: result.date_grains || {}, measure_overrides: result.measure_aggregations || {},
          calculations: result.calculations || [],
        });
        liveData = qr?.data || [];
        debugQuery = qr?._debug_query || debugQuery;
      } catch (_) { /* fall back to narrative-only card */ }
    }
    setMessages(prev => [...prev, {
      role: 'assistant', content: result.answer || '', result, liveData, debugQuery,
      queryError: result.error || null, revisedFromFeedback: reason || 'your feedback', _revised: true,
      question: result.question || originalQuestion || '',
    }]);
  }, [selectedModel]);


  // ── Feature A: just-in-time "as of" prompt for to-date questions ──
  const isToDateQuestion = useCallback((q) => {
    const s = (q || '').toLowerCase();
    if (/\b(ytd|qtd|mtd|itd)\b/.test(s)) return true;
    if (/\b(year|quarter|month|inception)\s*[- ]?to[- ]?date\b/.test(s)) return true;
    if (/\bto[- ]date\b/.test(s)) return true;
    if (/\bthis (year|quarter|month) so far\b/.test(s)) return true;
    return false;
  }, []);
  const questionNamesDate = useCallback((q) => {
    const s = (q || '').toLowerCase();
    // An explicit date (3/31, 2024-03-31, "march 31") or a named year skips the prompt.
    if (/\b\d{4}-\d{1,2}-\d{1,2}\b/.test(s)) return true;
    if (/\b\d{1,2}\/\d{1,2}(\/\d{2,4})?\b/.test(s)) return true;
    if (/\bas of\b/.test(s)) return true;
    if (/\b(19|20)\d{2}\b/.test(s)) return true;
    if (/\b(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)[a-z]*\s+\d/.test(s)) return true;
    return false;
  }, []);
  const resolvedAsOfLabel = useCallback(() => {
    const d = exploreAsOfDate ? new Date(exploreAsOfDate + 'T00:00:00') : new Date();
    const dateStr = d.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
    const fieldName = exploreAsOfField || modelDefaultDateField;
    const fieldLabel = (modelDateFields.find(x => x.name === fieldName) || {}).label || fieldName;
    const fiscalNote = fiscalStartMonth && fiscalStartMonth !== 1 ? ` (fiscal year starts ${new Date(2000, fiscalStartMonth - 1, 1).toLocaleDateString(undefined, { month: 'long' })})` : ' (fiscal year starts January)';
    return { dateStr, fieldLabel, fiscalNote };
  }, [exploreAsOfDate, exploreAsOfField, modelDefaultDateField, modelDateFields, fiscalStartMonth]);

  const confirmAsOf = (dateValue, fieldValue) => {
    const d = dateValue || '';
    const fld = fieldValue || '';
    asOfDateRef.current = d; asOfFieldRef.current = fld;
    setExploreAsOfDate(d); setExploreAsOfField(fld);
    asOfPinnedRef.current = true;
    setAsOfPinned(true);
    setAsOfPromptOpen(false);
    if (pendingAskRef.current) setAsOfResumeTick(t => t + 1);
  };
  // Resume the stashed question once the as-of choice is applied.
  useEffect(() => {
    if (asOfResumeTick && pendingAskRef.current) {
      const q = pendingAskRef.current; pendingAskRef.current = null;
      ask(q);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [asOfResumeTick]);

  const ask = useCallback(async (q, feedbackReason, autoImproveAttempt = false) => {
    const prompt = q || question;
    if (!prompt.trim()) return;
    // "help" → show an in-chat capabilities guide (no AI request, no credits).
    if (!autoImproveAttempt && !feedbackReason) {
      const _p = prompt.trim().toLowerCase().replace(/[?.!]/g, '');
      if (_p === 'help' || _p === 'what can you do' || _p === 'what can i do' || _p === 'commands') {
        setMessages(prev => [...prev,
          { role: 'user', content: prompt, author: user?.name || '', author_id: user?.user_id },
          { role: 'assistant', _help: true }]);
        setQuestion('');
        return;
      }
    }
    // Deterministic view/query commands are handled locally — no AI request,
    // no credit spend, instant. Falls through to the AI on no match.
    let aiQuestion = prompt;
    let swapNote = null;
    if (!autoImproveAttempt && !feedbackReason) {
      try {
        const det = await tryDeterministicCommand(prompt);
        if (det === true) {
          // A local view/query command applied (e.g. spoken "make it a bar
          // chart"). Announce completion so the voice panel stops "thinking"
          // and confirms aloud; the chart already updated inline in the thread.
          window.dispatchEvent(new CustomEvent('explore:answer-ready', {
            detail: { model_id: selectedModel, session: sessionId || '', question: prompt, answer: 'Done — updated the chart.' },
          }));
          return;
        }
        // A swap we couldn't resolve locally → keep the user's original bubble
        // but ask the AI the naturalized rewrite (e.g. "... by state and year").
        if (det && det.reask) { aiQuestion = det.reask; swapNote = det.label || null; }
      } catch (e) { /* fall through to AI on any parse/exec error */ }
    }
    // Feature A — just-in-time "as of" prompt: the first time a session gets a
    // to-date question with no pinned date and no date named in the question,
    // ask for the as-of date (and, when the model has alternates, the date
    // column) before running. Later to-date questions reuse the pinned choice.
    if (!autoImproveAttempt && !feedbackReason && !asOfPinnedRef.current
        && isToDateQuestion(prompt) && !questionNamesDate(prompt)) {
      pendingAskRef.current = prompt;
      setAsOfPromptOpen(true);
      return;
    }
    // Check AI credits
    if (aiCredits && !aiCredits.allowed) {
      toast.error('AI credits exhausted for this month. Contact your administrator to increase the limit.');
      return;
    }
    setLoading(true);
    cancelledRef.current = false;
    lastQuestionRef.current = prompt;
    const _ac = new AbortController();
    abortRef.current = _ac;
    if (autoImproveAttempt) {
      // Replace the weak answer with an "improving…" placeholder in-place —
      // don't add another user turn.
      setMessages(prev => [...prev, { role: 'assistant', _improving: true }]);
    } else {
      setMessages(prev => [...prev, { role: 'user', content: prompt, author: user?.name || '', author_id: user?.user_id }]);
      setQuestion('');
    }
    
    // Send typing=false
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'typing', typing: false }));
    }
    
    try {
      // A non-date "we assumed X" chip re-asks the base question and forces the
      // exact-field clarify. Consume the flag for this single ask.
      const _force = forceClarifyRef.current; forceClarifyRef.current = null;
      // Build the feedback/self-improve guidance ONCE so BOTH the WebSocket and
      // HTTP paths carry it — previously the WS path dropped it, so a thumbs-down
      // "Submit & Retry" just re-ran the same question and ignored the reason.
      let errorContext = null;
      if (autoImproveAttempt) {
        errorContext = 'Your previous answer scored low on the internal quality self-check. Reason more carefully, ground every claim in the actual data, avoid unsupported assumptions, and return a stronger, higher-confidence answer to the same question.';
      } else if (feedbackReason) {
        errorContext = `The user rejected the previous response and said: "${feedbackReason}". This is important — do NOT repeat the same answer. Address their specific objection, and never substitute a different metric or calculation for what was asked.`;
      }
      // Use WebSocket if connected to shared session
      if (wsConnected && wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ type: 'ask', question: aiQuestion, as_of_date: asOfDateRef.current || undefined, as_of_field: asOfFieldRef.current || undefined, force_clarify: _force ? true : undefined, assumption: _force?.assumption || undefined, error_context: errorContext || undefined }));
        // AI response will come via WebSocket onmessage
        return;
      }
      // Build request — include feedback as error_context if this is a retry after thumbs-down
      const askPayload = { question: aiQuestion, session_id: sessionId, model_id: selectedModel };
      if (asOfDateRef.current) askPayload.as_of_date = asOfDateRef.current;
      if (asOfFieldRef.current) askPayload.as_of_field = asOfFieldRef.current;
      if (_force) { askPayload.force_clarify = true; if (_force.assumption) askPayload.assumption = _force.assumption; }
      if (errorContext) askPayload.error_context = errorContext;
      // Use shared endpoint if in a shared session (polling fallback)
      const res = isSharedSession && sharedToken
        ? await api.sharedSessionAsk(sharedToken, { question: aiQuestion })
        : await api.exploreAsk(askPayload, { signal: _ac.signal });
      if (cancelledRef.current) return;
      setSessionId(res.session_id || sessionId);
      const result = res.result || {};

      // Announce completion so a background chime + toast can fire if the user
      // has navigated away while the AI was working. `speak` is a voice-friendly
      // rendering: clarify questions include their options so Barge-In can ask
      // them aloud and wait for the user's spoken answer.
      const _baseAnswer = result.answer || result.narrative || (result.cannot_calculate ? "I can't calculate that from this model." : '');
      let _speak = _baseAnswer;
      const _clarifyOpts = (result.output_type === 'clarify' && Array.isArray(result.clarification_options))
        ? result.clarification_options.filter(o => o && o.label) : [];
      if (result.output_type === 'clarify') {
        const _labels = _clarifyOpts.map(o => o.label);
        _speak = (result.answer || 'I need a quick clarification.') + (_labels.length ? ` Your options are: ${_labels.join(', ')}. Which would you like?` : '');
      } else if (result.output_type === 'forecast' && result.forecast) {
        const fc = result.forecast;
        _speak = _baseAnswer || `Forecast ready for ${fc.measure_label || 'the metric'}${fc.breakdown_label ? ` by ${fc.breakdown_label}` : ''} over the next ${fc.horizon || 6} periods — it's on screen.`;
      } else if (!_speak) {
        _speak = "Here's your chart.";
      }
      window.dispatchEvent(new CustomEvent('explore:answer-ready', {
        detail: {
          model_id: selectedModel, session: res.session_id || sessionId || '', question: aiQuestion,
          answer: _baseAnswer, speak: _speak, clarify: _clarifyOpts,
        },
      }));

      // LLM-generated SQL and return the data inline (result.sql_data). Use it
      // directly as liveData and skip the semantic executeQuery step (whose
      // plan-compiler doesn't apply to raw SQL column output).
      if (result.sql_mode) {
        const sqlMsg = {
          role: 'assistant', content: result.answer || '', result,
          liveData: Array.isArray(result.sql_data) ? result.sql_data : [],
          debugQuery: result.sql ? { sql: result.sql } : null,
          queryError: result.error || null, question: aiQuestion,
          revisedFromFeedback: feedbackReason || undefined,
        };
        setMessages(prev => [...prev, sqlMsg]);
        setLoading(false);
        inputRef.current?.focus();
        return;
      }

      // ITD calc-measure answers carry their own authoritative value + source
      // (summary vs canonical) in result.data/answer. Never re-execute a live
      // query for them — doing so would override the as-of value with the
      // default/latest and attach a misleading route badge.
      if (result.answered_by_itd) {
        const itdMsg = {
          role: 'assistant', content: result.answer || '', result,
          liveData: Array.isArray(result.data) ? result.data : [],
          debugQuery: null, queryError: null, question: aiQuestion,
          revisedFromFeedback: feedbackReason || undefined,
        };
        setMessages(prev => [...prev, itdMsg]);
        setLoading(false);
        inputRef.current?.focus();
        return;
      }

      // Auto-execute query if dimensions + measures are returned
      let liveData = null;
      let debugQuery = null;
      let queryError = null;
      if (selectedModel && (result.dimensions?.length > 0 || result.measures?.length > 0)) {
        try {
          const dims = result.dimensions || [];
          const msrs = result.measures || [];
          const queryLimit = result.limit ? Number(result.limit) : 200;
          const sortBy = result.sort_by || null;
          const sortDir = result.sort_order || 'desc';
          const dateGrains = result.date_grains || null;
          // Forward the AI-generated filters (e.g. "exclude year 2025" → [{field:'year',operator:'not_equals',value:2025}])
          // Without this the query engine ignores filter intent even though the narrative claims the filter was applied.
          // Then layer sticky/pinned filters on top so they survive every refinement — the client is
          // authoritative for pinned filters even if the LLM drops them from its returned config.
          const llmFilters = Array.isArray(result.filters_applied) ? result.filters_applied : [];
          const { merged: filters, dropped: pinnedRestored } = mergePinnedFilters(llmFilters);
          // Reflect the effective filter set back onto the result so the
          // ExploreMessage renders exactly what the query engine ran with.
          result.filters_applied = filters;
          if (pinnedRestored.length > 0) {
            setDroppedPinnedNote({ filters: pinnedRestored, at: Date.now() });
          } else {
            setDroppedPinnedNote(null);
          }
          // ── Diff-of-intent detection (Iter B) ─────────────────────
          // Find the previous assistant chart config (if any) and flag
          // any dimensions/measures/filters that vanished this turn
          // WITHOUT the user's message referencing them. Sticky-filter
          // re-injection has already run above, so we only diff the
          // llmFilters we actually saw (not the pinned re-adds).
          const prevResult = [...messages].reverse().find(m => m.result)?.result;
          if (prevResult) {
            const promptLower = prompt.toLowerCase();
            const nameMentioned = (name) => {
              if (!name) return false;
              const n = String(name).toLowerCase();
              // Strip common punctuation to be forgiving about phrasing.
              const clean = promptLower.replace(/[_\-.]/g, ' ');
              return clean.includes(n) || clean.includes(n.replace(/_/g, ' '));
            };
            const filterKeyStr = (f) => `${f.field}|${f.operator}|${JSON.stringify(f.value)}`;
            const droppedDims = (prevResult.dimensions || []).filter(d =>
              !(dims || []).includes(d) && !nameMentioned(d));
            const droppedMsrs = (prevResult.measures || []).filter(m =>
              !(msrs || []).includes(m) && !nameMentioned(m));
            const prevFilterSet = new Set((prevResult.filters_applied || []).map(filterKeyStr));
            const nowFilterSet = new Set(llmFilters.map(filterKeyStr));
            const droppedFilters = (prevResult.filters_applied || []).filter(f =>
              prevFilterSet.has(filterKeyStr(f)) && !nowFilterSet.has(filterKeyStr(f))
              && !nameMentioned(f.field));
            const total = droppedDims.length + droppedMsrs.length + droppedFilters.length;
            if (total > 0) {
              setDiffOfIntent({
                previous: prevResult,
                droppedDims, droppedMsrs, droppedFilters,
                at: Date.now(),
              });
            } else {
              setDiffOfIntent(null);
            }
          }
          const queryRes = await api.executeQuery({ model_id: selectedModel, dimensions: dims, measures: msrs, filters, limit: queryLimit, sort_by: sortBy, sort_dir: sortDir, date_grains: dateGrains, measure_overrides: result.measure_aggregations || {}, calculations: result.calculations || [] }, { signal: _ac.signal });
          if (cancelledRef.current) return;
          liveData = queryRes?.data || null;
          debugQuery = queryRes?._debug_query || null;
          // Self-correction on ZERO ROWS — a query can succeed yet return no
          // rows when the chosen fields/joins eliminate all data (e.g. an
          // entity count bridged through a fact whose FK is unpopulated).
          // Treat that like an error so the same one-shot AI retry (below)
          // re-plans with different-but-valid fields. Skipped when a filter
          // was applied (a truly empty filtered result is a valid answer).
          if (Array.isArray(liveData) && liveData.length === 0
              && (!filters || filters.length === 0) && !autoImproveAttempt) {
            const zeroErr = new Error('The query executed successfully but returned 0 rows. The chosen fields or join path likely eliminated all data. Re-plan using different but valid fields from the same/joinable entity group (for an entity count, use count_distinct of the natural-key identifier).');
            zeroErr._zeroRow = true;
            throw zeroErr;
          }
        } catch (qErr) {
          console.warn('Query failed, auto-retrying with error context:', qErr.message);
          // ── Part 3: Validation pass — retry AI with error context ──
          try {
            const retryRes = await api.exploreAsk({
              question: aiQuestion,
              session_id: res.session_id || sessionId,
              model_id: selectedModel,
              error_context: `The previous response used dimensions=${JSON.stringify(result.dimensions)} and measures=${JSON.stringify(result.measures)}, but the query engine returned this error: "${qErr.message}". Please pick different fields that belong to the same entity group and retry.`
            }, { signal: _ac.signal });
            if (cancelledRef.current) return;
            const retryResult = retryRes?.result || {};
            if (retryResult.dimensions?.length > 0 || retryResult.measures?.length > 0) {
              try {
                const rDims = retryResult.dimensions || [];
                const rMsrs = retryResult.measures || [];
                const rLimit = retryResult.limit ? Number(retryResult.limit) : 200;
                const rSortBy = retryResult.sort_by || null;
                const rSortDir = retryResult.sort_order || 'desc';
                const rDateGrains = retryResult.date_grains || null;
                const llmRFilters = Array.isArray(retryResult.filters_applied) ? retryResult.filters_applied : [];
                const { merged: rFilters } = mergePinnedFilters(llmRFilters);
                retryResult.filters_applied = rFilters;
                const retryQueryRes = await api.executeQuery({ model_id: selectedModel, dimensions: rDims, measures: rMsrs, filters: rFilters, limit: rLimit, sort_by: rSortBy, sort_dir: rSortDir, date_grains: rDateGrains, measure_overrides: retryResult.measure_aggregations || {}, calculations: retryResult.calculations || [] }, { signal: _ac.signal });
                if (cancelledRef.current) return;
                // Retry succeeded — use corrected result
                liveData = retryQueryRes?.data || null;
                debugQuery = retryQueryRes?._debug_query || null;
                Object.assign(result, retryResult);
                queryError = null;
              } catch (retryQErr) {
                queryError = retryQErr.message || 'Query execution failed after retry';
              }
            } else {
              queryError = qErr.message || 'Query execution failed';
            }
          } catch {
            queryError = qErr.message || 'Query execution failed';
          }
        }
      }

      // Decide whether to auto-improve: only on the first pass, when the
      // filter is on, the answer came back low-quality, and we're not in a
      // shared/websocket session or a thumbs-down retry.
      const shouldAutoImprove = !autoImproveAttempt
        && autoImproveRef.current
        && result._low_confidence === true
        && !feedbackReason
        && !isSharedSession;

      const finalMsg = { role: 'assistant', content: result.answer || '', result, liveData, debugQuery, queryError, question: aiQuestion, revisedFromFeedback: feedbackReason || undefined };
      if (autoImproveAttempt) finalMsg._auto_improved = true;
      if (swapNote) finalMsg._swapNote = swapNote;

      if (shouldAutoImprove) {
        // Kick off one improvement pass; ask() adds the "improving…" placeholder.
        ask(aiQuestion, undefined, true);
      } else if (autoImproveAttempt) {
        // Replace the trailing "improving…" placeholder with the improved answer.
        setMessages(prev => {
          const copy = [...prev];
          if (copy.length && copy[copy.length - 1]?._improving) copy[copy.length - 1] = finalMsg;
          else copy.push(finalMsg);
          return copy;
        });
      } else {
        setMessages(prev => [...prev, finalMsg]);
      }
    } catch (err) {
      // User cancelled (Stop) — no error UI; just drop any dangling placeholder.
      if (cancelledRef.current || err?.name === 'AbortError') {
        setMessages(prev => (autoImproveAttempt && prev.length && prev[prev.length - 1]?._improving) ? prev.slice(0, -1) : prev);
      } else {
        // On an auto-improve failure, keep the placeholder from dangling.
        setMessages(prev => {
          const errMsg = { role: 'assistant', content: 'Analysis failed. Please try a different question.', error: true };
          if (autoImproveAttempt && prev.length && prev[prev.length - 1]?._improving) {
            const copy = [...prev]; copy[copy.length - 1] = errMsg; return copy;
          }
          return [...prev, errMsg];
        });
        toast.error(err.message);
      }
    } finally { abortRef.current = null; setLoading(false); inputRef.current?.focus(); }
  }, [question, aiCredits, wsConnected, isSharedSession, sharedToken, sessionId, selectedModel, tryDeterministicCommand]);

  // Stop an in-flight analysis so the user can fix their question and re-ask.
  const stop = useCallback(() => {
    cancelledRef.current = true;
    try { abortRef.current?.abort(); } catch { /* already settled */ }
    abortRef.current = null;
    setLoading(false);
    // Remove a dangling "improving…" placeholder if one is showing.
    setMessages(prev => (prev.length && prev[prev.length - 1]?._improving) ? prev.slice(0, -1) : prev);
    // One-tap "Re-ask": bring the question back into the input (focused) so the
    // user can fix a typo and resend — or just press enter to run it again.
    toast('Analysis stopped', {
      action: lastQuestionRef.current ? {
        label: 'Re-ask',
        onClick: () => { setQuestion(lastQuestionRef.current || ''); setTimeout(() => inputRef.current?.focus(), 0); },
      } : undefined,
    });
    inputRef.current?.focus();
  }, []);

  // Deep-link from Slack ("Open in Cloverleaf"): ?q=<question>&run=1 pre-fills
  // the question and auto-runs it once a model is ready.
  const autoRanRef = useRef(false);
  useEffect(() => {
    if (autoRanRef.current) return;
    const q = searchParams.get('q');
    if (q && selectedModel) {
      autoRanRef.current = true;
      setQuestion(q);
      const sp = new URLSearchParams(searchParams);
      sp.delete('q'); sp.delete('run');
      setSearchParams(sp, { replace: true });
      if (searchParams.get('run') !== '0') ask(q);
    }
  }, [selectedModel, searchParams, ask, setSearchParams]);


  // Refresh a result's data by re-running the SAME query (no AI) — updates the
  // card in place. Useful when reopening a session and the warehouse has newer data.
  const rerunQuery = useCallback(async (msg) => {
    const r = msg?.result || {};
    // SQL-mode results are refreshed by re-running the SQL, in place.
    if (r.sql) {
      setLoading(true);
      try {
        const res = await api.exploreSqlExecute({ model_id: selectedModel, sql: r.sql });
        const rows = res?.data || res?.rows || [];
        setMessages(prev => prev.map(m => m === msg
          ? { ...m, liveData: rows, _syncedAt: Date.now() }
          : m));
        toast.success('Query refreshed');
      } catch (e) {
        toast.error(e.message || 'Could not refresh the query');
      } finally { setLoading(false); }
      return;
    }
    if (!selectedModel || !(r.dimensions?.length || r.measures?.length || r.sql)) { toast.error('Nothing to rerun for this result'); return; }
    setLoading(true);
    try {
      const filters = Array.isArray(r.filters_applied) ? r.filters_applied : [];
      const queryRes = await api.executeQuery({
        model_id: selectedModel,
        dimensions: r.dimensions || [], measures: r.measures || [], filters,
        limit: r.limit ? Number(r.limit) : 200,
        sort_by: r.sort_by || null, sort_dir: r.sort_order || 'desc',
        date_grains: r.date_grains || null,
        measure_overrides: r.measure_aggregations || {}, calculations: r.calculations || [],
      });
      setMessages(prev => prev.map(m => m === msg
        ? { ...m, liveData: queryRes?.data || [], debugQuery: queryRes?._debug_query || m.debugQuery, _syncedAt: Date.now() }
        : m));
      toast.success('Query refreshed');
    } catch (e) {
      toast.error(e.message || 'Could not refresh the query');
    } finally { setLoading(false); }
  }, [selectedModel]);

  // Re-ask the AI the ORIGINAL question that produced this result card (spends
  // an AI credit; appends a fresh answer). Finds the preceding user message.
  const reaskAI = useCallback((msg, idx) => {
    let q = '';
    const start = (typeof idx === 'number' ? idx : messages.length) - 1;
    for (let i = start; i >= 0; i--) {
      if (messages[i]?.role === 'user' && messages[i]?.content) { q = messages[i].content; break; }
    }
    if (!q) { toast.error('Could not find the original question to re-ask'); return; }
    ask(q);
  }, [messages, ask]);


  const loadSession = async (sid) => {
    try {
      const s = await api.exploreSession(sid);
      setSessionId(sid);
      setRcaResult(null); setRcaLoading(false); setRcaExpanded(new Set([1, 2, 3]));
      const modelId = s.model_id || selectedModel;
      setSelectedModel(modelId);
      setModelChosen(true);

      // Re-execute queries to populate liveData for assistant messages
      const msgs = s.messages || [];
      const hydratedMessages = await Promise.all(msgs.map(async (msg) => {
        // SQL/warehouse answers carry their rows inline in result.sql_data —
        // restore straight from there (executeQuery is semantic-only).
        if (msg.role === 'assistant' && msg.result?.sql_mode) {
          return { ...msg, liveData: Array.isArray(msg.result.sql_data) ? msg.result.sql_data : (msg.liveData || []) };
        }
        if (msg.role === 'assistant' && msg.result?.dimensions?.length > 0 && modelId) {
          try {
            const dims = msg.result.dimensions || [];
            const msrs = msg.result.measures || [];
            const queryLimit = msg.result.limit ? Number(msg.result.limit) : 200;
            const sortBy = msg.result.sort_by || (msrs[0] || null);
            const sortDir = msg.result.sort_order || 'desc';
            const dateGrains = msg.result.date_grains || null;
            const filters = Array.isArray(msg.result.filters_applied) ? msg.result.filters_applied : [];
            const queryRes = await api.executeQuery({ model_id: modelId, dimensions: dims, measures: msrs, filters, limit: queryLimit, sort_by: sortBy, sort_dir: sortDir, date_grains: dateGrains, measure_overrides: msg.result.measure_aggregations || {}, calculations: msg.result.calculations || [], as_of_date: exploreAsOfDate || undefined });
            return { ...msg, liveData: queryRes?.data || null };
          } catch { return msg; }
        }
        return msg;
      }));
      setMessages(hydratedMessages);
    } catch (err) { toast.error(err.message); }
  };

  // Investigation Sharing — give a teammate a direct link to reopen this
  // investigation and keep drilling from the same context.
  const submitShare = async () => {
    if (!shareTarget || !shareEmail.trim()) return;
    setSharing(true);
    try {
      const r = await api.shareInvestigation(shareTarget.session_id, shareEmail.trim());
      toast.success(`Shared with ${r.name || r.shared_with} — they'll get a link to reopen it.`);
      setShareTarget(null); setShareEmail('');
    } catch (e) {
      toast.error(e.message || 'Could not share investigation');
    } finally { setSharing(false); }
  };

  // Resume an existing session from the URL (?s=<sessionId>) — e.g. the "View"
  // action on the "answer ready" toast after navigating away. Jumps back into
  // the SAME analysis instead of starting a new one. Runs once, when models are
  // available and we're not in a shared/discussion flow.
  const restoredFromUrlRef = useRef(false);
  useEffect(() => {
    if (restoredFromUrlRef.current) return;
    if (isSharedSession || discussionParam) return;
    const sid = searchParams.get('s');
    if (!sid || sessionId === sid) return;
    if (!models || models.length === 0) return;
    if (messages.length > 0) return;
    restoredFromUrlRef.current = true;
    setModelChosen(true);
    loadSession(sid);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [models, searchParams, isSharedSession, discussionParam]);
  // it whenever the session context changes so a new/other session never shows
  // the previous session's root-cause panel.
  const resetRca = () => { setRcaResult(null); setRcaLoading(false); setRcaExpanded(new Set([1, 2, 3])); };
  const newSession = () => {
    setSessionId(''); setMessages([]); setSummary(null); resetRca();
    // A brand-new thread must NOT inherit the previous thread's pinned as-of
    // date — reset it so it re-asks (the old thread keeps its own choice).
    setExploreAsOfDate(''); setExploreAsOfField(''); setAsOfPinned(false);
    asOfPinnedRef.current = false; asOfDateRef.current = ''; asOfFieldRef.current = '';
    // Fresh thread — drop the restored session id from the URL so this Explore
    // starts blank (the old thread keeps its own ?s= in the Workbench).
    if (selectedModel) {
      const next = new URLSearchParams(searchParams);
      next.set('model', selectedModel);
      next.delete('s');
      setSearchParams(next);
    }
  };

  // If the URL carries a session id (from a prior navigation, a Workbench pill
  // click, or a refresh), rehydrate that exact conversation so the user lands
  // where they left off instead of a blank Explore. Runs on every searchParams
  // change (SPA navigations don't remount the page), guarded to avoid reloading
  // the session that's already open.
  useEffect(() => {
    const sid = searchParams.get('s');
    if (sid && sid !== sessionId) loadSession(sid);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const formatValue = (val) => typeof val === 'number' ? val.toLocaleString(undefined, { maximumFractionDigits: 2 }) : (typeof val === 'object' && val !== null ? JSON.stringify(val) : String(val ?? ''));

  const buildVisualPayload = (result, vizState = {}) => {
    const vs = vizState || {};
    // Honor the table signal from all three sources: an explicit viz toggle
    // (vs.outputType), the LLM's output_type decision (result.output_type ==='table',
    // the natural "as a table" path ExploreMessage renders at line ~2569), else chart.
    const wantsTable = vs.outputType === 'table'
      || (!vs.outputType && !vs.chartType && result.output_type === 'table' && (!result.chart_type || result.chart_type === 'table'));
    const chartType = wantsTable ? 'table' : (vs.chartType || result.chart_type || 'bar_chart');
    const sqlMode = !!result.sql_mode;
    const _norm = (s) => String(s || '').toLowerCase().replace(/[^a-z0-9]/g, '');
    const toFieldName = (m) => {
      if (!m) return m;
      const nm = _norm(m);
      const f = (allFields || []).find(ff =>
        ff.name === m || _norm(ff.name) === nm
        || _norm(ff.business_name) === nm || _norm(ff.display_name) === nm || _norm(ff.label) === nm);
      return f ? f.name : m;
    };
    // SQL-mode results carry RAW SQL column names as their dimensions/measures,
    // and the saved snapshot rows are keyed by those exact names. Remapping them
    // to semantic field names (toFieldName) makes rowDims/colDim/valueMeasures no
    // longer match the snapshot keys → the dashboard chart/table reads undefined
    // and renders all zeros. Keep raw names for SQL-mode visuals.
    const mapName = sqlMode ? (m => m) : toFieldName;
    const dims = (result.dimensions || []).map(mapName);
    const msrs = (result.measures || []).map(mapName);
    const xAxis = result.x_axis ? mapName(result.x_axis) : null;
    const isDateName = (d) => {
      const f = (allFields || []).find(ff => ff.name === d);
      const dt = String(f?.data_type || '').toLowerCase();
      const nm = String(d || '').toLowerCase();
      return dt.includes('date') || dt.includes('time') || nm.includes('month') || nm.includes('date') || nm.includes('year') || nm.includes('quarter') || nm.includes('week');
    };
    let colorBy = result.color_by ? mapName(result.color_by) : null;
    // A date/time field must NEVER be the colour/series — it belongs on the
    // axis. Backends occasionally return a date in color_by, which renders as a
    // useless rainbow of one colour per date. Strip it unconditionally here so
    // the date falls back to the axis below.
    if (colorBy && isDateName(colorBy)) colorBy = null;
    // Mirror the Explore chart's color/series derivation so a "by <dim> over <date>"
    // chart keeps its series dimension when added to a dashboard: with two dims where
    // one is a date, color by the non-date dim.
    if (!colorBy && dims.length === 2) {
      const dateDim = dims.find(isDateName);
      if (dateDim) colorBy = dims.find(d => d !== dateDim) || null;
    }
    if (colorBy && !dims.includes(colorBy)) dims.push(colorBy);
    let rowDims, colDim;
    if (colorBy && dims.includes(colorBy)) { rowDims = dims.filter(d => d !== colorBy); colDim = colorBy; }
    else if (wantsTable) {
      // Tables have no pivot/series channel: every dimension is a row grouping,
      // so keep them ALL on rows rather than dropping the extras.
      rowDims = dims; colDim = '';
    }
    else if (xAxis && dims.length >= 2) {
      // Chart with an x-axis and extra dims: one non-axis dim becomes the
      // series (colDim); every remaining dim (including the axis) stays on rows
      // so no field is silently dropped from the drop zones.
      colDim = dims.find(d => d !== xAxis) || '';
      rowDims = dims.filter(d => d !== colDim);
    }
    else { rowDims = dims; colDim = ''; }
    const measure0 = msrs[0] || '';
    const sortBy = vs.sort ? (vs.sort.by === 'measure' ? measure0 : (rowDims[0] || dims[0] || null)) : (result.sort_by || null);
    const sortDir = vs.sort ? (vs.sort.dir || 'desc') : (result.sort_order || 'asc');
    const queryLimit = result.limit ? Number(result.limit) : 1000;
    const effTopN = vs.topN || (result.limit && Number(result.limit) < 100 ? Number(result.limit) : null);
    const topNConfig = effTopN ? { enabled: true, n: Number(effTopN), direction: sortDir === 'asc' ? 'bottom' : 'top', by: sortBy || measure0 || '' } : undefined;
    const perVisual = {
      ...(topNConfig ? { topN: topNConfig } : {}),
      ...(vs.palette ? { palette: vs.palette } : {}),
      ...(vs.colors ? { themeColors: vs.colors } : {}),
      ...(vs.orientation ? { orientation: vs.orientation } : {}),
      ...(vs.barLayout ? { barLayout: vs.barLayout } : {}),
      ...(vs.dataLabels ? { dataLabels: true, showLabels: true } : {}),
      ...(vs.refLine && vs.refLine.mode && vs.refLine.mode !== 'none' ? { refLine: vs.refLine } : {}),
      ...(result.date_interpretation?.was_defaulted ? { dateInterpretation: result.date_interpretation } : {}),
      // Carry the grand total onto dashboard tables so the headline total an
      // Explore table shows doesn't vanish once pinned (TableRenderer reads cfg.grandTotals).
      ...(chartType === 'table' ? { grandTotals: true } : {}),
      // Multi-dimension tables also get per-group subtotals so each group totals
      // (TableRenderer groups by the first row dim when cfg.subtotals is on).
      ...(chartType === 'table' && rowDims.length >= 2 ? { subtotals: true } : {}),
    };
    const hasPerVisual = Object.keys(perVisual).length > 0;
    // Turn the Explore answer's applied filters (e.g. State = "California", or a
    // date/amount RANGE) into EDITABLE per-visual slicers on the dashboard, instead
    // of silently baking them into the query as hidden fixed filters (or dropping
    // ranges entirely). Only ordinary semantic answers (not SQL-mode snapshots,
    // which render from their own frozen data) are converted. The stored slicer
    // value shape matches what buildVisualFilters expands:
    //   equality/in → raw scalar/array; date range → {dk:'range'|'relative'};
    //   numeric range → {nk:'num', min, max}.
    const FAR_PAST = '1900-01-01', FAR_FUTURE = '2999-12-31';
    const EQ_OPS = ['equals', '=', 'eq', 'in', 'is', 'in_list', 'contains'];
    const GTE_OPS = ['gte', 'gt', '>', '>=', 'greater_than', 'greater_than_or_equal', 'after', 'on_or_after'];
    const LTE_OPS = ['lte', 'lt', '<', '<=', 'less_than', 'less_than_or_equal', 'before', 'on_or_before'];
    const toSlicerValue = (f, isDate) => {
      const op = String(f.operator || 'equals').toLowerCase();
      if (EQ_OPS.includes(op)) return f.value;
      if (isDate) {
        if (op === 'relative') return { dk: 'relative', preset: f.preset || f.value };
        if (op === 'between' && Array.isArray(f.value)) return { dk: 'range', start: f.value[0], endExcl: f.value[1] };
        if (GTE_OPS.includes(op)) return { dk: 'range', start: f.value, endExcl: FAR_FUTURE };
        if (LTE_OPS.includes(op)) return { dk: 'range', start: FAR_PAST, endExcl: f.value };
        return null;
      }
      if (op === 'between' && Array.isArray(f.value)) return { nk: 'num', min: f.value[0], max: f.value[1] };
      if (GTE_OPS.includes(op)) return { nk: 'num', min: f.value };
      if (LTE_OPS.includes(op)) return { nk: 'num', max: f.value };
      return null;
    };
    const slicerFilters = (!sqlMode && Array.isArray(result.filters_applied))
      ? result.filters_applied
          .filter(f => f && f.field
            && f.value !== undefined && f.value !== null && f.value !== ''
            && !(Array.isArray(f.value) && f.value.length === 0))
          .map(f => {
            const field = mapName(f.field);
            const value = toSlicerValue(f, isDateName(field));
            return value == null || value === '' ? null : { field, value };
          })
          .filter(Boolean)
      : [];
    const title = result.answer?.slice(0, 60) || 'Visual';
    return { chartType, dims, msrs, rowDims, colDim, sortBy, sortDir, queryLimit, topNConfig, perVisual, hasPerVisual, slicerFilters, title,
      asOfDate: exploreAsOfDate || undefined, asOfField: (exploreAsOfField || modelDefaultDateField) || undefined,
      sqlMode, sql: result.sql || '', sqlData: sqlMode ? (result.sql_data || []) : null, sqlColumns: sqlMode ? (result.columns || []) : null };
  };

  // Human-readable toast note for the editable slicers a pinned answer carries,
  // so viewers notice (and know they can change) a filter that became a slicer.
  const slicerToastDescription = (slicerFilters = []) => {
    if (!slicerFilters.length) return undefined;
    const label = (name) => {
      const f = (allFields || []).find(x => x.name === name);
      return f ? (f.business_name || f.display_name || f.label || name) : String(name || '').replace(/_/g, ' ');
    };
    const names = slicerFilters.map(f => label(f.field)).join(', ');
    const n = slicerFilters.length;
    return `${n} editable slicer${n > 1 ? 's' : ''} added — ${names}. Dashboard viewers can change ${n > 1 ? 'them' : 'it'}.`;
  };

  const saveAsInsight = async (result, vizState = {}, customTitle = '', opts = {}) => {
    try {
      const vs = vizState || {};
      const p = buildVisualPayload(result, vs);
      const named = (customTitle || '').trim();
      const visualTitle = (named || p.title || 'Visual').slice(0, 80);
      const reportName = named || `Explore: ${result.answer?.slice(0, 50) || 'Analysis'}`;
      const r = await api.createReport({ name: reportName, description: result.narrative || '', visualization_type: p.chartType });
      const visualId = 'v_' + Date.now();
      const slicerDims = p.slicerFilters.map(f => f.field);
      const slicerSelections = {};
      p.slicerFilters.forEach(f => { slicerSelections[visualId + '_' + f.field] = f.value; });
      const hasSlicers = slicerDims.length > 0;
      const updateData = {
        name: reportName,
        description: result.narrative || '',
        visualization_type: p.chartType,
        query_definition: { model_id: selectedModel, dimensions: p.dims, measures: p.msrs, filters: [], sort_by: p.sortBy, sort_dir: p.sortDir, limit: p.queryLimit, column_dimension: p.colDim, calculations: result.calculations || [], measure_overrides: result.measure_aggregations || {}, ...(p.asOfDate ? { as_of_date: p.asOfDate, as_of_field: p.asOfField } : {}) },
        config: {
          visuals: [{ id: visualId, vizType: p.chartType, title: visualTitle, rowDims: p.rowDims, colDim: p.colDim, valueMeasures: p.msrs, filterDims: slicerDims,
            ...(p.asOfDate ? { asOfDate: p.asOfDate } : {}),
            ...(p.sqlMode ? { sqlMode: true, sql: p.sql, sqlData: p.sqlData, sqlColumns: p.sqlColumns } : {}) }],
          showGrid: vs.showGrid !== false,
          showLegend: vs.showLegend !== false,
          ...(hasSlicers ? { slicerSelections } : {}),
          ...(p.asOfDate ? { globalFilterValues: { __asof__: p.asOfDate } } : {}),
          ...(p.hasPerVisual ? { visualConfigs: { [visualId]: p.perVisual } } : {})
        }
      };
      await api.updateReport(r.report_id, updateData);
      try {
        await api.createVisual({
          name: visualTitle || 'Explore Visual', description: result.narrative || '',
          model_id: selectedModel, viz_type: p.chartType, row_dims: p.rowDims,
          col_dim: p.colDim, value_measures: p.msrs,
          viz_config: p.hasPerVisual ? p.perVisual : {}, top_n: p.topNConfig || null,
        });
      } catch { /* ignore */ }
      sessionStorage.setItem('exploreInsightConfig', JSON.stringify(updateData));
      if (opts.silent) { return r.report_id; }
      toast.success(`Saved as ${getTerm('insights', 'singular')}`, { description: slicerToastDescription(p.slicerFilters) });
      navigate(`/dashboards/builder?id=${r.report_id}&fromExplore=true`);
    } catch (err) { toast.error(err.message); if (opts.silent) throw err; }
  };

  // Save just the visual (no dashboard/report, no navigation) — save + toast.
  const saveAsVisual = async (result, vizState = {}) => {
    if (!selectedModel) { toast.error('Select a model first'); return; }
    try {
      const p = buildVisualPayload(result, vizState || {});
      const visualTitle = (p.title || 'Visual').slice(0, 80);
      await api.createVisual({
        name: visualTitle || 'Explore Visual', description: result.narrative || '',
        model_id: selectedModel, viz_type: p.chartType, row_dims: p.rowDims,
        col_dim: p.colDim, value_measures: p.msrs,
        viz_config: p.hasPerVisual ? p.perVisual : {}, top_n: p.topNConfig || null,
      });
      toast.success('Saved as visual');
    } catch (err) { toast.error(err.message); }
  };

  // Pin the current result's primary metric to the KPI Monitor board (per-user, persisted).
  const pinToKpi = async (result, vizState = {}) => {
    const p = buildVisualPayload(result, vizState || {});
    const measure = (p.msrs && p.msrs[0]) || '';
    if (!measure) { toast.error('No metric to pin on this result'); return; }
    if (!selectedModel) { toast.error('Select a model first'); return; }
    try {
      const label = (result.answer || measure).slice(0, 60);
      const res = await api.createKpiPin({ model_id: selectedModel, measure, label });
      toast.success(res?.already_pinned ? 'Already on your KPI Monitor' : 'Pinned to KPI Monitor', {
        action: { label: 'View', onClick: () => navigate('/kpi-monitor') },
      });
    } catch (err) { toast.error(err.message); }
  };

  // Open the "add this visual to an existing dashboard" picker (same model only).
  const openAddToDashboard = async (result, vizState = {}) => {
    setAddPosition('bottom');
    setAddTitle((result.answer || '').slice(0, 60) || 'Visual');
    try {
      const all = await api.getReports();
      const list = (all || [])
        .filter(rep => (rep.query_definition?.model_id || rep.model_id) === selectedModel)
        .sort((a, b) => new Date(b.updated_at || b.modified_at || b.created_at || 0) - new Date(a.updated_at || a.modified_at || a.created_at || 0));
      setDashboardPicker({ open: true, result, vizState, dashboards: list, saving: false });
    } catch (err) { toast.error(err.message); }
  };

  // Show the in-chat capabilities panel (same as typing "help").
  const showHelp = () => {
    setMessages(prev => [...prev, { role: 'assistant', _help: true }]);
  };

  // ── "/" slash-command palette ──────────────────────────────────────────
  // Context for the menu: the most-recent assistant result decides which
  // presentation actions are valid and which dimensions "Colour by …" lists.
  const buildSlashCtx = useCallback(() => {
    const last = [...messages].reverse().find(
      m => m.role === 'assistant' && m.result && (m.result.dimensions?.length || m.result.chart_type)
    );
    if (!last) return { hasChart: false, dims: [], chartType: '' };
    const r = last.result;
    const vs = last.viz_state || {};
    const chartType = (vs.outputType === 'table') ? 'table' : (vs.chartType || r.chart_type || r.output_type || '');
    const labelFor = (n) => {
      const f = (allFields || []).find(x => x.name === n);
      return f ? (f.business_name || f.display_name || f.label || n) : n;
    };
    const dims = (r.dimensions || []).map(n => ({ name: n, label: labelFor(n) }));
    return { hasChart: !!(r.dimensions?.length || r.chart_type), dims, chartType, presets: vizPresets };
  }, [messages, allFields, vizPresets]);

  // Load the user's saved presets once (they persist across devices/sessions).
  const refreshPresets = useCallback(async () => {
    try { const res = await api.listVizPresets(); setVizPresets(res?.presets || []); } catch (_) { /* non-fatal */ }
  }, []);
  useEffect(() => { refreshPresets(); }, [refreshPresets]);

  // Detect a leading "/" in either input → open the palette and track the
  // text typed after the slash (so it filters as you type).
  const handleSlashInput = useCallback((val) => {
    if (typeof val === 'string' && val.startsWith('/')) {
      setSlashOpen(true);
      setSlashQuery(val.slice(1));
    } else if (slashOpen) {
      setSlashOpen(false);
      setSlashQuery('');
    }
  }, [slashOpen]);

  const rememberSlash = useCallback((kind, payload) => {
    setSlashRecent(prev => [{ kind, payload }, ...prev.filter(r => !(r.kind === kind && String(r.payload) === String(payload)))].slice(0, 4));
  }, []);

  const closeSlash = useCallback(() => { setSlashOpen(false); setSlashQuery(''); }, []);

  // Menu → plain-English command: run it through the deterministic pipeline.
  const applySlashCmd = useCallback((payload) => {
    closeSlash();
    setQuestion('');
    rememberSlash('apply', payload);
    ask(payload);
  }, [ask, closeSlash, rememberSlash]);

  // Menu → parameterised command (e.g. "top ", "target line at "): pre-fill the
  // input so the user types the value, then presses Enter to apply.
  const fillSlashCmd = useCallback((payload) => {
    closeSlash();
    setQuestion(payload);
    setTimeout(() => inputRef.current?.focus(), 0);
  }, [closeSlash]);

  // Undo the most recent client-side view change (chart tweak / re-query card).
  const undoLastViz = useCallback(() => {
    setMessages(prev => {
      const idx = [...prev].map((m, i) => ({ m, i })).reverse().find(({ m }) => m._viz_variant)?.i;
      if (idx == null) { toast('Nothing to undo'); return prev; }
      const next = prev.slice(0, idx);
      // Also drop the user command bubble that produced it, if it's right before.
      if (next.length && next[next.length - 1].role === 'user') next.pop();
      toast('Undid last change');
      return next;
    });
  }, []);

  // Download the most-recent chart's rows as CSV.
  const exportCsv = useCallback((rows, name = 'chart-data') => {
    if (!Array.isArray(rows) || rows.length === 0) { toast('No data to export'); return; }
    const cols = Array.from(rows.reduce((s, r) => { Object.keys(r || {}).forEach(k => s.add(k)); return s; }, new Set()));
    const esc = (v) => {
      if (v == null) return '';
      const s = String(v);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const csv = [cols.join(','), ...rows.map(r => cols.map(c => esc(r[c])).join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `${name}.csv`; a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
    toast('Exported CSV');
  }, []);

  // Snapshot the most-recent rendered chart to a PNG (last capture node in DOM).
  const exportPng = useCallback(async () => {
    const nodes = document.querySelectorAll('.explore-chart-capture');
    const node = nodes[nodes.length - 1];
    if (!node) { toast('No chart to export'); return; }
    try {
      const canvas = await html2canvas(node, { backgroundColor: null, scale: 2, logging: false });
      const url = canvas.toDataURL('image/png');
      const a = document.createElement('a');
      a.href = url; a.download = 'chart.png'; a.click();
      toast('Exported PNG');
    } catch (err) {
      toast.error('Could not export image');
    }
  }, []);

  // Menu → non-chart actions (/table, /pin, /undo, /explain, /ask, export, presets).
  const actionSlashCmd = useCallback((payload) => {
    closeSlash();
    setQuestion('');
    rememberSlash('action', payload);
    const lastIdx = [...messages].map((m, i) => ({ m, i })).reverse()
      .find(({ m }) => m.role === 'assistant' && m.result && (m.result.dimensions?.length || m.result.chart_type))?.i;
    const last = lastIdx != null ? messages[lastIdx] : null;
    const lastVizState = last ? (last.viz_state || {}) : {};
    if (payload === 'table') {
      if (lastIdx != null) handleVizChange(lastIdx, { outputType: 'table' });
      else toast('No chart to show as a table yet');
    } else if (payload === 'pin') {
      if (last) saveAsInsight(last.result, lastVizState);
      else toast('Nothing to pin yet');
    } else if (payload === 'undo') {
      undoLastViz();
    } else if (payload === 'explain') {
      const q = [...messages].reverse().find(m => m.role === 'user' && m.content)?.content;
      if (q) runInvestigate(q); else showHelp();
    } else if (payload === 'ask') {
      setTimeout(() => inputRef.current?.focus(), 0);
    } else if (payload === 'export-csv') {
      exportCsv(last?.liveData || [], 'ai-explore-data');
    } else if (payload === 'export-png') {
      exportPng();
    } else if (payload === 'preset-save') {
      if (last) {
        // Capture the EFFECTIVE look — merge the current chart type so a preset
        // is meaningful even when the user hasn't tweaked anything yet.
        const eff = { ...lastVizState };
        if (!eff.chartType && last.result?.chart_type) eff.chartType = last.result.chart_type;
        setPresetDialog({ open: true, vizState: eff, name: '' });
      } else toast('Adjust a chart first, then save it as a preset');
    } else if (payload.startsWith('preset-apply:')) {
      const id = payload.slice('preset-apply:'.length);
      const p = vizPresets.find(x => x.preset_id === id);
      if (p && lastIdx != null) { handleVizChange(lastIdx, p.viz_state || {}); toast(`Applied preset · ${p.name}`); }
      else toast('Could not apply that preset');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages, closeSlash, rememberSlash, handleVizChange, saveAsInsight, undoLastViz, vizPresets, exportCsv, exportPng]);

  // Save the current chart look as a reusable preset.
  const savePreset = useCallback(async () => {
    const name = (presetDialog.name || '').trim();
    if (!name) { toast.error('Give the preset a name'); return; }
    try {
      await api.createVizPreset(name, presetDialog.vizState || {});
      await refreshPresets();
      setPresetDialog({ open: false });
      toast.success(`Saved preset · ${name}`);
    } catch (err) { toast.error(err.message || 'Could not save preset'); }
  }, [presetDialog, refreshPresets]);


  // Append the current visual to a chosen existing dashboard.
  const confirmAddToDashboard = async (reportId, force = false) => {
    const { result, vizState } = dashboardPicker;
    setDashboardPicker(dp => ({ ...dp, saving: reportId, dupWarn: null }));
    try {
      // Forecast results have no dims/measures — pin a snapshot the viewer shows
      // instantly, with a Refresh button (forecastMeta) to recompute on demand.
      if (result?.output_type === 'forecast' && result?.forecast) {
        const fcst = result.forecast;
        const rep = await api.getReport(reportId);
        const cfg = rep.config || {};
        const visuals = Array.isArray(cfg.visuals) ? [...cfg.visuals] : [];
        const visualId = 'v_' + Date.now();
        const newVisual = {
          id: visualId, vizType: 'forecast',
          title: (addTitle || `${fcst.measure_label} · Forecast${fcst.breakdown_label ? ` by ${fcst.breakdown_label}` : ''}`).slice(0, 80),
          rowDims: [], colDim: null, valueMeasures: [], filterDims: [], _reportPageIndex: 0,
          forecastSnapshot: fcst,
          forecastMeta: { model_id: fcst.model_id, source_question: fcst.source_question, context_question: fcst.context_question, unit: fcst.unit, horizon: fcst.horizon, top_n: fcst.top_n },
        };
        if (addPosition === 'top') visuals.unshift(newVisual); else visuals.push(newVisual);
        let pageVisuals = cfg.pageVisuals;
        if (pageVisuals && typeof pageVisuals === 'object' && !Array.isArray(pageVisuals)) {
          const firstPage = (cfg.pages && cfg.pages[0]?.id) || Object.keys(pageVisuals)[0] || 'page_1';
          const arr = [...(pageVisuals[firstPage] || [])];
          if (addPosition === 'top') arr.unshift(newVisual); else arr.push(newVisual);
          pageVisuals = { ...pageVisuals, [firstPage]: arr };
        }
        let gridLayouts = cfg.gridLayouts;
        if (gridLayouts && Array.isArray(gridLayouts.lg)) {
          const lg = gridLayouts.lg;
          const nextY = lg.reduce((m, l) => Math.max(m, (l.y || 0) + (l.h || 4)), 0);
          gridLayouts = { ...gridLayouts, lg: [...lg, { i: visualId, x: 0, y: nextY, w: 12, h: 6, minW: 4, minH: 4 }] };
        }
        await api.updateReport(reportId, {
          name: rep.name, description: rep.description || '',
          visualization_type: rep.visualization_type || 'line',
          query_definition: rep.query_definition || { model_id: selectedModel },
          parameters: rep.parameters || [],
          config: { ...cfg, visuals, ...(pageVisuals ? { pageVisuals } : {}), ...(gridLayouts ? { gridLayouts } : {}) },
        });
        setDashboardPicker({ open: false });
        const publish = async () => {
          const tId = toast.loading('Publishing to dashboard…');
          try {
            await api.promoteReport(reportId, 'test');
            await api.promoteReport(reportId, 'production');
            toast.success('Published — the forecast is now live on the dashboard', { id: tId, action: { label: 'View', onClick: () => navigate(`/dashboards/view?id=${reportId}`) } });
          } catch (e) { toast.error(e.message || 'Could not publish to dashboard', { id: tId }); }
        };
        toast.success('Forecast added to dashboard', {
          action: { label: 'Publish to dashboard', onClick: publish },
          cancel: { label: 'Open builder', onClick: () => navigate(`/dashboards/builder?id=${reportId}`) },
          duration: 10000,
        });
        return;
      }
      const p = buildVisualPayload(result, vizState || {});
      const rep = await api.getReport(reportId);
      const cfg = rep.config || {};
      const visuals = Array.isArray(cfg.visuals) ? [...cfg.visuals] : [];
      // Duplicate guard: warn (don't block) if a matching visual already exists
      // on this dashboard. Match by row dims + column dim + measures + chart type.
      const sig = (v) => JSON.stringify([
        [...(v.rowDims || [])].sort(),
        v.colDim || '',
        [...(v.valueMeasures || [])].sort(),
        v.vizType || '',
      ]);
      const newSig = sig({ rowDims: p.rowDims, colDim: p.colDim, valueMeasures: p.msrs, vizType: p.chartType });
      const existingVisuals = [...visuals, ...Object.values(cfg.pageVisuals || {}).flat()];
      const dup = existingVisuals.find(v => sig(v) === newSig);
      if (dup && !force) {
        setDashboardPicker(dp => ({ ...dp, saving: false, dupWarn: { reportId, name: rep.name, title: dup.title || '' } }));
        return;
      }
      const visualId = 'v_' + Date.now();
      const slicerDims = p.slicerFilters.map(f => f.field);
      // _reportPageIndex:0 → lands on the first page in multi-page reports.
      const newVisual = { id: visualId, vizType: p.chartType, title: (addTitle || p.title).slice(0, 80), rowDims: p.rowDims, colDim: p.colDim, valueMeasures: p.msrs, filterDims: slicerDims, _reportPageIndex: 0,
        ...(p.asOfDate ? { asOfDate: p.asOfDate } : {}),
        ...(p.sqlMode ? { sqlMode: true, sql: p.sql, sqlData: p.sqlData, sqlColumns: p.sqlColumns } : {}) };
      if (addPosition === 'top') visuals.unshift(newVisual); else visuals.push(newVisual);
      // The VIEWER renders config.pageVisuals[pageId] when present (not top-level
      // config.visuals), so append there too or the visual won't show on open.
      let pageVisuals = cfg.pageVisuals;
      if (pageVisuals && typeof pageVisuals === 'object' && !Array.isArray(pageVisuals)) {
        const firstPage = (cfg.pages && cfg.pages[0]?.id) || Object.keys(pageVisuals)[0] || 'page_1';
        const arr = [...(pageVisuals[firstPage] || [])];
        if (addPosition === 'top') arr.unshift(newVisual); else arr.push(newVisual);
        pageVisuals = { ...pageVisuals, [firstPage]: arr };
      }
      const visualConfigs = { ...(cfg.visualConfigs || {}) };
      if (p.hasPerVisual) visualConfigs[visualId] = p.perVisual;
      // Pre-seed per-visual slicer selections so the Explore filters become
      // editable slicers on just THIS tile (not a hidden fixed query filter and
      // not a dashboard-wide filter that would narrow other tiles).
      const slicerSelections = { ...(cfg.slicerSelections || {}) };
      p.slicerFilters.forEach(f => { slicerSelections[visualId + '_' + f.field] = f.value; });
      // Grid dashboards render via react-grid-layout from cfg.gridLayouts. A new
      // visual missing from that saved layout renders at a 1x1 default (tiny &
      // inoperable), so append a full-width layout item (or prepend for "top").
      let gridLayouts = cfg.gridLayouts;
      if (gridLayouts && Array.isArray(gridLayouts.lg)) {
        const lg = gridLayouts.lg;
        if (addPosition === 'top') {
          const shifted = lg.map(l => ({ ...l, y: (l.y || 0) + 4 }));
          gridLayouts = { ...gridLayouts, lg: [{ i: visualId, x: 0, y: 0, w: 12, h: 4, minW: 3, minH: 2 }, ...shifted] };
        } else {
          const nextY = lg.reduce((m, l) => Math.max(m, (l.y || 0) + (l.h || 4)), 0);
          gridLayouts = { ...gridLayouts, lg: [...lg, { i: visualId, x: 0, y: nextY, w: 12, h: 4, minW: 3, minH: 2 }] };
        }
      }
      const qd = rep.query_definition || {};
      // For SQL-mode visuals the dims/measures are raw SQL column names that
      // don't belong in the report's semantic query_definition — the visual
      // renders from its own snapshot. Only merge for semantic visuals.
      const mergedDims = p.sqlMode ? (qd.dimensions || []) : Array.from(new Set([...(qd.dimensions || []), ...p.dims]));
      const mergedMsrs = p.sqlMode ? (qd.measures || []) : Array.from(new Set([...(qd.measures || []), ...p.msrs]));
      const updateData = {
        name: rep.name,
        description: rep.description || '',
        visualization_type: rep.visualization_type || p.chartType,
        query_definition: { ...qd, model_id: selectedModel, dimensions: mergedDims, measures: mergedMsrs, column_dimension: qd.column_dimension || (p.sqlMode ? '' : p.colDim), ...(qd.as_of_date || p.asOfDate ? { as_of_date: qd.as_of_date || p.asOfDate, as_of_field: qd.as_of_field || p.asOfField } : {}) },
        parameters: rep.parameters || [],
        config: { ...cfg, visuals, ...(pageVisuals ? { pageVisuals } : {}), ...(gridLayouts ? { gridLayouts } : {}), ...(Object.keys(visualConfigs).length ? { visualConfigs } : {}), ...(Object.keys(slicerSelections).length ? { slicerSelections } : {}) },
      };
      await api.updateReport(reportId, updateData);
      setDashboardPicker({ open: false });
      toast.success('Added to dashboard', { description: slicerToastDescription(p.slicerFilters), action: { label: 'Open dashboard', onClick: () => navigate(`/dashboards/builder?id=${reportId}`) } });
    } catch (err) {
      setDashboardPicker(dp => ({ ...dp, saving: false }));
      toast.error(err.message);
    }
  };


  const createAlert = async (result) => {
    // Open an inline alert editor on the insight (no navigation away). Prefill a
    // starting condition from the AI suggestion or the result's first measure.
    let rules = [];
    if (result.alert_suggestion) {
      rules = [{ metric: result.alert_suggestion.metric, operator: result.alert_suggestion.operator || 'gt', threshold: result.alert_suggestion.threshold ?? '' }];
    } else if (result.measures?.length > 0) {
      rules = [{ metric: result.measures[0], operator: 'gt', threshold: '' }];
    } else {
      toast.info('No measures available to create an alert from');
      return;
    }
    const firstLabel = allFields.find(f => f.name === rules[0].metric)?.description || (rules[0].metric || '').replace(/_/g, ' ');
    setAlertDialog({
      open: true, saving: false, result,
      name: `Alert: ${firstLabel}`,
      severity: 'medium', check_frequency: 'daily',
      conditions: { logic: 'AND', rules },
    });
  };

  const submitAlert = async () => {
    const d = alertDialog;
    const rules = (d.conditions?.rules || []);
    if (!rules.length || !rules.some(r => (r.metric && r.threshold !== '' && r.threshold != null) || (r.rules || []).some(n => n.metric && n.threshold !== '' && n.threshold != null))) {
      toast.error('Add at least one complete condition');
      return;
    }
    // Backend evaluates the full AND/OR tree; the top-level metric/operator/threshold
    // mirror the first rule for legacy compatibility + notification text.
    const firstLeaf = rules.find(r => r.metric) || (rules.find(r => r.rules)?.rules || [])[0] || {};
    setAlertDialog(a => ({ ...a, saving: true }));
    try {
      await api.createAlert({
        name: d.name || 'Alert',
        metric: firstLeaf.metric,
        operator: firstLeaf.operator || 'gt',
        threshold: Number(firstLeaf.threshold) || 0,
        conditions: d.conditions,
        model_id: selectedModel,
        check_frequency: d.check_frequency || 'daily',
        recipients: [user?.email || ''],
        severity: d.severity || 'medium',
      });
      toast.success(`✓ Alert "${d.name || 'Alert'}" saved`, {
        description: `We'll check ${d.check_frequency || 'daily'} and notify ${user?.email || 'you'} when it triggers.`,
      });
      setAlertDialog({ open: false });
    } catch (err) {
      toast.error(err.message);
      setAlertDialog(a => ({ ...a, saving: false }));
    }
  };

  const removeFinding = async (id) => {
    try { await api.deleteFinding(id); setFindings(prev => prev.filter(f => f.finding_id !== id)); toast.success('Finding removed'); }
    catch (err) { toast.error(err.message); }
  };

  const generateExecSummary = async () => {
    if (!sessionId) { toast.error('Start a session first'); return; }
    setSummaryLoading(true);
    try {
      const res = await api.generateSummary({ session_id: sessionId });
      setSummary(res.summary);
    } catch (err) { toast.error(err.message); }
    finally { setSummaryLoading(false); }
  };

  // Governed diagnostic ("Investigate why") — deterministic, privacy-safe.
  // Appends the governed answer as a real message in the transcript so it lives
  // in the conversation (drivers, tested/ruled-out, validated findings).
  const runInvestigate = async (q, measure) => {
    const prompt = (q || question || '').trim();
    if (!prompt) return;
    setInvestigateLoading(true);
    setMessages(prev => [...prev, { role: 'user', content: prompt, author: user?.name || '', author_id: user?.user_id }]);
    try {
      const res = await api.exploreInvestigate({ question: prompt, model_id: selectedModel, session_id: sessionId || undefined, measure: measure || undefined });
      if (res.session_id && !sessionId) setSessionId(res.session_id);
      const result = res.result || {};
      setMessages(prev => [...prev, { role: 'assistant', content: result.answer || '', result, question: prompt }]);
    } catch (err) {
      toast.error(err.message);
      setMessages(prev => [...prev, { role: 'assistant', content: 'Investigation failed. Please try again.', error: true }]);
    } finally { setInvestigateLoading(false); }
  };

  const resetInvestigation = async () => {
    if (!sessionId) return;
    try { await api.resetInvestigation(sessionId); toast.success('Investigation reset — the next question starts fresh'); }
    catch (err) { toast.error(err.message); }
  };

  const runRCA = async (q) => {
    const prompt = q || question;
    if (!prompt.trim()) return;
    setRcaLoading(true);
    setRcaResult(null);
    try {
      const res = await api.exploreRCA({ question: prompt, model_id: selectedModel });
      setRcaResult(res.rca);
      // Also auto-execute queries for each step
      for (const step of (res.rca?.steps || [])) {
        if (step.dimensions_to_query?.length > 0 && step.measures_to_query?.length > 0) {
          try {
            const qr = await api.executeQuery({ model_id: selectedModel, dimensions: step.dimensions_to_query, measures: step.measures_to_query, limit: 50 });
            step._liveData = qr?.data || [];
          } catch { /* ignore */ }
        }
      }
      setRcaResult({ ...res.rca });
    } catch (err) { toast.error(err.message); }
    finally { setRcaLoading(false); }
  };

  // Replace raw field identifiers (e.g. DIRECT_WRITTEN_PREMIUM) in AI-generated
  // RCA text with their curated metadata display names (e.g. Written Premium).
  const humanizeFields = (text) => {
    if (!text || typeof text !== 'string' || !(allFields || []).length) return text;
    let out = text;
    for (const f of allFields) {
      const raw = f?.name;
      if (!raw || !/[A-Z_]/.test(raw) || raw.length < 3) continue;
      if (out.indexOf(raw) === -1) continue;
      const label = resolveLabel(allFields, raw);
      if (!label || label === raw) continue;
      out = out.replace(new RegExp(`(^|[^\\w])${raw.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}(?![\\w])`, 'g'), `$1${label}`);
    }
    return out;
  };

  // Voice keyword command: "investigate this" → quick root-cause, read aloud.
  const voiceInvestigate = async (topic) => {
    const last = [...messages].reverse().find(m => m.result);
    const metric = last?.result?.measures?.[0];
    const clean = String(topic || '').replace(/^(can you |could you |please |hey \w+ )?(investigate|look into|dig into|find out why|root cause)( this| that| it)?/i, '').trim();
    const q = clean.length > 6 ? clean : `Why did ${metric ? resolveLabel(allFields, metric) : (last?.userQuestion || 'this metric')} change?`;
    setRcaLoading(true); setRcaResult(null);
    try {
      const res = await api.exploreRCA({ question: q, model_id: selectedModel });
      setRcaResult({ ...res.rca });
      for (const step of (res.rca?.steps || [])) {
        if (step.dimensions_to_query?.length > 0 && step.measures_to_query?.length > 0) {
          try {
            const qr = await api.executeQuery({ model_id: selectedModel, dimensions: step.dimensions_to_query, measures: step.measures_to_query, limit: 50 });
            step._liveData = qr?.data || [];
          } catch { /* ignore */ }
        }
      }
      setRcaResult({ ...res.rca });
      const spoken = [res.rca?.summary, res.rca?.recommendation ? `Recommendation: ${res.rca.recommendation}` : '']
        .filter(Boolean).join(' ') || 'I ran a quick investigation — the breakdown is on screen.';
      window.dispatchEvent(new CustomEvent('explore:answer-ready', {
        detail: { model_id: selectedModel, session: sessionId || '', question: q, answer: spoken, speak: humanizeFields(spoken) },
      }));
    } catch (e) {
      const s = 'I could not complete the investigation.';
      window.dispatchEvent(new CustomEvent('explore:answer-ready', { detail: { answer: s, speak: s } }));
      toast.error(e.message);
    } finally { setRcaLoading(false); }
  };

  // Voice keyword command: "alert me if this changes" → create a sentinel on the
  // current metric WITHOUT navigating away (keeps the voice conversation alive).
  const voiceCreateAlert = async () => {
    const last = [...messages].reverse().find(m => m.result);
    const r = last?.result || {};
    const metric = r.alert_suggestion?.metric || r.measures?.[0];
    if (!metric) {
      const s = "I couldn't find a metric on screen to set an alert on.";
      window.dispatchEvent(new CustomEvent('explore:answer-ready', { detail: { answer: s, speak: s } }));
      return;
    }
    const label = allFields.find(f => f.name === metric)?.description || metric.replace(/_/g, ' ');
    try {
      await api.createAlert({
        name: `Alert: ${label}`, metric,
        operator: r.alert_suggestion?.operator || 'gt', threshold: r.alert_suggestion?.threshold || 0,
        check_frequency: 'daily', recipients: [user?.email || ''], severity: 'medium',
      });
      toast.success(`Alert created for ${label}`, { action: { label: 'Customize', onClick: () => navigate('/alerts') } });
      const s = `Done — I've set up a daily alert on ${label}. You can fine-tune it on the Alerts page.`;
      window.dispatchEvent(new CustomEvent('explore:answer-ready', { detail: { model_id: selectedModel, question: `alert on ${label}`, answer: s, speak: s } }));
    } catch (e) {
      const s = 'I could not create the alert.';
      window.dispatchEvent(new CustomEvent('explore:answer-ready', { detail: { answer: s, speak: s } }));
      toast.error(e.message);
    }
  };

  // Voice keyword: "go to investigations" / "open dashboards" → navigate.
  const voiceNavigate = (text) => {
    const t = String(text || '').toLowerCase();
    // Admin-defined custom phrases win (matched even without a nav verb).
    for (const np of navPhrases) {
      const p = String(np?.phrase || '').toLowerCase().trim();
      if (p && np.path && t.includes(p)) { navigate(np.path); return np.path; }
    }
    if (!/\b(go to|open|navigate to|take me to|switch to)\b/.test(t)) return null;
    const routes = [
      [/(investigation)/, '/investigations'],
      [/(kpi|monitor)/, '/kpi-monitor'],
      [/(dashboard|report)/, '/dashboards'],
      [/(alert|sentinel)/, '/alerts'],
      [/(recommendation)/, '/recommendations'],
      [/(executive|exec summary|summaries)/, '/executive-summaries'],
      [/(discussion)/, '/discussions'],
      [/(marketplace)/, '/marketplace'],
      [/(semantic|model)/, '/models'],
      [/(home|dashboard home)/, '/home'],
    ];
    for (const [re, path] of routes) { if (re.test(t)) { navigate(path); return path; } }
    return null;
  };

  // Voice keyword: "save this to a dashboard" → save current result (no nav).
  const voiceSaveToDashboard = async () => {
    const last = [...messages].reverse().find(m => m.result && m.result.output_type && m.result.output_type !== 'clarify');
    const r = last?.result;
    if (!r) {
      const s = "There's nothing on screen to save yet — ask a question first.";
      window.dispatchEvent(new CustomEvent('explore:answer-ready', { detail: { answer: s, speak: s } }));
      return;
    }
    try {
      const title = (r.answer || 'Analysis').slice(0, 50);
      const id = await saveAsInsight(r, last.vizState || {}, '', { silent: true });
      toast.success('Saved to a new dashboard', { action: { label: 'Open', onClick: () => navigate(`/dashboards/builder?id=${id}&fromExplore=true`) } });
      const s = `Saved to a new dashboard called "${title}". You'll find it under Dashboards and Reports.`;
      window.dispatchEvent(new CustomEvent('explore:answer-ready', { detail: { model_id: selectedModel, question: 'save to dashboard', answer: s, speak: s } }));
    } catch (e) {
      const s = 'I could not save that to a dashboard.';
      window.dispatchEvent(new CustomEvent('explore:answer-ready', { detail: { answer: s, speak: s } }));
    }
  };

  // Voice keyword: "summarize this" → generate + read a short recap.
  const voiceSummarize = async () => {
    if (!sessionId) {
      const s = 'Ask a question first, then I can summarize the findings.';
      window.dispatchEvent(new CustomEvent('explore:answer-ready', { detail: { answer: s, speak: s } }));
      return;
    }
    setSummaryLoading(true);
    try {
      const res = await api.generateSummary({ session_id: sessionId });
      setSummary(res.summary);
      const sm = res.summary || {};
      const spoken = [sm.title, sm.key_finding, sm.recommendation ? `Recommendation: ${sm.recommendation}` : '']
        .filter(Boolean).join('. ') || 'Here is a summary of your findings on screen.';
      window.dispatchEvent(new CustomEvent('explore:answer-ready', { detail: { model_id: selectedModel, question: 'summarize', answer: spoken, speak: humanizeFields(spoken) } }));
    } catch (e) {
      const s = 'I could not generate a summary.';
      window.dispatchEvent(new CustomEvent('explore:answer-ready', { detail: { answer: s, speak: s } }));
      toast.error(e.message);
    } finally { setSummaryLoading(false); }
  };


  const shareCurrentSession = async () => {
    if (!sessionId) { toast.error('No active session'); return; }
    try {
      const res = await api.shareSession(sessionId);
      const url = `${window.location.origin}/ai-copilot?shared=${res.share_token}`;
      await navigator.clipboard.writeText(url);
      toast.success('Link copied to clipboard');
    } catch (err) { toast.error(err.message); }
  };

  // Shared "as of" chip + just-in-time popover — rendered on BOTH the landing
  // input and the follow-up input (mutually-exclusive views), so the first
  // to-date question always gets a visible prompt.
  const asOfControl = (
    <div className="relative flex items-center gap-1.5 mt-1.5">
      {(asOfPinned || exploreAsOfDate) && (() => {
        const r = resolvedAsOfLabel();
        return (
          <button
            onClick={() => setAsOfPromptOpen(o => !o)}
            className="flex items-center gap-1 text-[10px] text-foreground/80 rounded-full border border-primary/30 bg-primary/10 px-2 py-0.5 hover:border-primary/50 transition-colors"
            title="Change the as-of date / date field for to-date answers"
            data-testid="explore-as-of-chip">
            <CalendarClock className="w-3 h-3 text-primary" />
            As of {exploreAsOfDate ? r.dateStr : 'Today'}{modelDateFields.length > 1 ? ` · on ${r.fieldLabel}` : ''}
            <ChevronDown className="w-3 h-3 opacity-60" />
          </button>
        );
      })()}
      {!(asOfPinned || exploreAsOfDate) && (
        <button
          onClick={() => setAsOfPromptOpen(o => !o)}
          className="flex items-center gap-1 text-[10px] text-muted-foreground hover:text-foreground transition-colors"
          title="Set an as-of date for to-date (YTD/QTD/MTD/ITD) answers"
          data-testid="explore-as-of-set">
          <CalendarClock className="w-3 h-3" /> Set “as of” date
        </button>
      )}
      {asOfPromptOpen && (
        <AsOfPopover
          asOfDate={exploreAsOfDate}
          asOfField={exploreAsOfField || modelDefaultDateField}
          dateFields={modelDateFields}
          defaultField={modelDefaultDateField}
          fiscalStartMonth={fiscalStartMonth}
          onConfirm={confirmAsOf}
          onClose={() => { setAsOfPromptOpen(false); if (pendingAskRef.current) { const q = pendingAskRef.current; pendingAskRef.current = null; asOfPinnedRef.current = true; setAsOfPinned(true); setTimeout(() => ask(q), 0); } }}
        />
      )}
    </div>
  );

  const aiMeasureDialog = (
    <AiFormulaDialog
      open={aiMeasureOpen}
      onOpenChange={setAiMeasureOpen}
      modelId={selectedModel}
      allFields={allFields}
      textFields={(allFields || []).filter(f => ['string', 'text', 'varchar', 'character'].includes(String(f.data_type || '').toLowerCase()))}
      dateFields={(allFields || []).filter(f => String(f.data_type || '').toLowerCase().includes('date'))}
      initialDescription={aiMeasureSeed}
      onApproved={() => { toast.success('Measure created — ask your question again to use it'); }}
    />
  );
  if (!modelChosen && messages.length === 0) {
    return (
      <div className="h-full overflow-y-auto" data-testid="explore-model-select">
        <div className="min-h-full flex flex-col items-center justify-center px-6 py-10">
        <div className="max-w-lg w-full text-center">
          <Sparkles className="w-10 h-10 text-primary mx-auto mb-4" />
          <h1 className="text-3xl font-heading font-bold tracking-tight text-foreground mb-2">{getTerm('ai_explore')}</h1>
          <p className="text-sm text-muted-foreground mb-4">Choose a data model to start your analysis</p>
          <div className="flex justify-center mb-4">
            <ProcessHelp
              testId="explore-help"
              title="AI Explore"
              intro="Ask questions of your data in plain language and get governed, model-grounded answers."
              steps={[
                'Pick a certified data model to ground answers and reduce hallucination risk.',
                'Type or tap the mic to ask, e.g. “show me a measure by a dimension this year”.',
                'Investigate a result for root-cause, forecast it, change the chart, or save it to a dashboard.',
                'Prefer endorsed sources to restrict answers to vetted, certified models.',
              ]}
            />
          </div>
          {/* Endorsement preference toggle — pushes Certified models to the top */}
          {endorsedCount > 0 && (
            <div className="flex items-center justify-center mb-6">
              <button
                onClick={togglePreferEndorsed}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-[11px] font-medium transition-all border ${preferEndorsed ? 'bg-emerald-500/15 border-emerald-500/40 text-emerald-700 dark:text-emerald-300' : 'bg-muted/20 border-border/30 text-muted-foreground hover:bg-muted/30'}`}
                data-testid="prefer-endorsed-toggle"
                title="Only show Certified / Promoted models — reduces hallucination risk by grounding answers in vetted sources"
              >
                <ShieldCheck className="w-3.5 h-3.5" />
                {preferEndorsed ? 'Showing only endorsed' : `Prefer endorsed sources (${endorsedCount})`}
              </button>
            </div>
          )}
          <div className="grid grid-cols-1 gap-3">
            {visibleModels.map(m => (
              <button key={m.model_id} onClick={() => { setSelectedModel(m.model_id); setModelChosen(true); }}
                data-testid={`explore-model-${m.model_id}`}
                className="glass-card rounded-2xl p-5 text-left hover:border-primary/30 hover:shadow-lg transition-all group">
                <div className="flex items-center gap-3">
                  <div className="p-2.5 rounded-xl bg-primary/10 border border-primary/20 group-hover:bg-primary/15"><FileText className="w-5 h-5 text-primary" /></div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="text-sm font-heading font-semibold text-foreground truncate">{m.name}</h3>
                      <EndorsementBadge endorsement={m.endorsement} size="sm" showLabel={false} />
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5">{m.description || `${m.status || 'active'} model`}</p>
                  </div>
                  {m?.endorsement?.level === 'certified' && (
                    <BadgeCheck className="w-4 h-4 text-emerald-400 flex-shrink-0" title="Certified — vetted single source of truth" />
                  )}
                </div>
              </button>
            ))}
            {visibleModels.length === 0 && !sessionsLoading && (
              <p className="text-sm text-muted-foreground py-8">
                {preferEndorsed
                  ? <>No endorsed models available. <button onClick={togglePreferEndorsed} className="text-primary hover:underline">Show all models</button>.</>
                  : 'No models available. Create a semantic model first.'}
              </p>
            )}
            {sessionsLoading && <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 text-primary animate-spin" /></div>}
          </div>
        </div>
        </div>
      </div>
    );
  }

  // Rendered ONCE at a stable fragment position in every return below, so the
  // VoiceCopilot instance (and any live Barge-In session) survives the
  // landing→workspace switch instead of unmounting on the first question.
  const voiceDock = (
    <>
      <VoiceCopilot open={voiceOpen} onClose={() => setVoiceOpen(false)} modelId={selectedModel} onAsk={(t) => ask(t)} onInvestigate={voiceInvestigate} onAlert={voiceCreateAlert} onNavigate={voiceNavigate} onSave={voiceSaveToDashboard} onSummarize={voiceSummarize} />
      <ExportFrameworkDialog open={aiExportOpen} onOpenChange={setAiExportOpen} mode="ai" resourceId={selectedModel} resourceName="AI Analysis" resourceType="ai_analysis" getContext={buildAiExportContext} />
    </>
  );

  // Shared modal dialogs — rendered in BOTH the landing and workspace returns
  // (Create Alert + Save-preset are triggered from result cards, which only
  // exist in the workspace view, so they MUST render there too).
  const alertDialogJsx = (
    <Dialog open={!!alertDialog.open} onOpenChange={(o) => { if (!o) setAlertDialog({ open: false }); }}>
      <DialogContent className="max-w-lg" data-testid="create-alert-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Bell className="w-4 h-4 text-amber-500" /> Create alert</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">Name</label>
            <Input value={alertDialog.name || ''} onChange={(e) => setAlertDialog(a => ({ ...a, name: e.target.value }))} className="h-8 text-sm" data-testid="alert-name-input" />
          </div>
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">Conditions</label>
            <ConditionBuilder
              conditions={alertDialog.conditions || { logic: 'AND', rules: [] }}
              onChange={(c) => setAlertDialog(a => ({ ...a, conditions: c }))}
              fields={allFields}
            />
          </div>
          <div className="flex items-center gap-3">
            <div className="flex-1">
              <label className="text-xs text-muted-foreground mb-1 block">Severity</label>
              <Select value={alertDialog.severity || 'medium'} onValueChange={(v) => setAlertDialog(a => ({ ...a, severity: v }))}>
                <SelectTrigger className="h-8 text-sm" data-testid="alert-severity-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="critical">Critical</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex-1">
              <label className="text-xs text-muted-foreground mb-1 block">Check frequency</label>
              <Select value={alertDialog.check_frequency || 'daily'} onValueChange={(v) => setAlertDialog(a => ({ ...a, check_frequency: v }))}>
                <SelectTrigger className="h-8 text-sm" data-testid="alert-frequency-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="hourly">Hourly</SelectItem>
                  <SelectItem value="daily">Daily</SelectItem>
                  <SelectItem value="weekly">Weekly</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <Button variant="ghost" size="sm" onClick={() => setAlertDialog({ open: false })} data-testid="alert-cancel-btn">Cancel</Button>
            <Button size="sm" onClick={submitAlert} disabled={alertDialog.saving} data-testid="alert-create-btn">
              {alertDialog.saving ? 'Creating…' : 'Create alert'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );

  const presetDialogJsx = (
    <Dialog open={!!presetDialog.open} onOpenChange={(o) => { if (!o) setPresetDialog({ open: false }); }}>
      <DialogContent className="max-w-sm" data-testid="save-preset-dialog">
        <DialogHeader>
          <DialogTitle>Save view as preset</DialogTitle>
        </DialogHeader>
        <p className="text-xs text-muted-foreground -mt-1">Save this chart's look (type, colours, labels, scale, formatting…) and reapply it to any answer with one tap from the <span className="font-mono">/</span> menu.</p>
        <Input
          autoFocus
          value={presetDialog.name || ''}
          onChange={e => setPresetDialog(p => ({ ...p, name: e.target.value }))}
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); savePreset(); } }}
          placeholder="e.g. Millions, no legend, avg line"
          data-testid="preset-name-input"
        />
        <div className="flex justify-end gap-2 mt-1">
          <Button variant="ghost" onClick={() => setPresetDialog({ open: false })} data-testid="preset-cancel-btn">Cancel</Button>
          <Button onClick={savePreset} data-testid="preset-save-btn">Save preset</Button>
        </div>
      </DialogContent>
    </Dialog>
  );


  // Landing state
  if (messages.length === 0) {
    return (
      <>
      <div className="flex flex-col h-full" data-testid="explore-page">
        {isAdmin && showAdvancedSql && selectedModel && (
          <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 backdrop-blur-sm p-4 overflow-auto" data-testid="advanced-sql-overlay" onClick={() => setShowAdvancedSql(false)}>
            <div className="w-full max-w-3xl mt-12" onClick={(e) => e.stopPropagation()}>
              <AdvancedSqlPanel modelId={selectedModel} initialQuestion={advancedSqlSeed || question} onClose={() => setShowAdvancedSql(false)} />
            </div>
          </div>
        )}
        {canSqlRead && showSqlExamples && selectedModel && (
          <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 backdrop-blur-sm p-4 overflow-auto" data-testid="sql-examples-overlay" onClick={() => setShowSqlExamples(false)}>
            <div className="w-full max-w-3xl mt-12" onClick={(e) => e.stopPropagation()}>
              <SqlExamplesPanel modelId={selectedModel} onClose={() => setShowSqlExamples(false)} />
            </div>
          </div>
        )}
        <div className="flex-1 flex flex-col items-center justify-center px-6 max-w-3xl mx-auto w-full">
          <div className="text-center mb-8">
            <h1 className="text-4xl font-heading font-bold tracking-tight text-foreground mb-2">Explore your data</h1>
            <p className="text-base text-muted-foreground">Ask questions, analyze results, and turn findings into insights.</p>
          </div>

          {/* Main prompt */}
          <div className="w-full relative mb-6">
            <div className="flex items-center gap-2 mb-2">
              <Select value={selectedModel} onValueChange={setSelectedModel}>
                <SelectTrigger className="w-48 h-8 text-xs rounded-lg bg-muted/30 border-border/30"><SelectValue placeholder="Select model" /></SelectTrigger>
                <SelectContent>{visibleModels.map(m => (
                  <SelectItem key={m.model_id} value={m.model_id} className="text-xs">
                    <span className="flex items-center gap-1.5">
                      {m?.endorsement?.level === 'certified' && <BadgeCheck className="w-3 h-3 text-emerald-400" />}
                      {m?.endorsement?.level === 'promoted' && <ShieldCheck className="w-3 h-3 text-amber-400" />}
                      {m.name}
                    </span>
                  </SelectItem>
                ))}</SelectContent>
              </Select>
              <button
                onClick={toggleAutoImprove}
                className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[11px] font-medium transition-all border ${autoImprove ? 'bg-emerald-500/15 border-emerald-500/40 text-emerald-700 dark:text-emerald-300' : 'bg-muted/20 border-border/30 text-muted-foreground hover:bg-muted/30'}`}
                data-testid="auto-improve-toggle"
                title="When on, answers that score low on the AI's quality self-check are automatically re-asked once for a stronger response."
              >
                <Sparkles className="w-3 h-3" /> {autoImprove ? 'Auto-improve on' : 'Auto-improve weak answers'}
              </button>
              <button
                onClick={toggleCertifiedOnly}
                className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[11px] font-medium transition-all border ${certifiedOnly ? 'bg-emerald-500/15 border-emerald-500/40 text-emerald-700 dark:text-emerald-300' : 'bg-muted/20 border-border/30 text-muted-foreground hover:bg-muted/30'}`}
                data-testid="certified-only-toggle"
                title="When on, ad-hoc (uncertified) answers are hidden behind a reveal so you only see governed, certified results at a glance."
              >
                <ShieldCheck className="w-3 h-3" /> {certifiedOnly ? 'Certified only' : 'Show all answers'}
              </button>
              {canCertify && selectedModel && (
                <button
                  onClick={clearExploreCache}
                  disabled={clearingCache}
                  className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[11px] font-medium transition-all border bg-muted/20 border-border/30 text-muted-foreground hover:bg-muted/30 disabled:opacity-50"
                  data-testid="clear-ai-cache-btn"
                  title="Clear cached AI answers for this model so metric/model changes show on the next ask (instead of waiting out the 6-hour cache)."
                >
                  {clearingCache ? <Loader2 className="w-3 h-3 animate-spin" /> : <Eraser className="w-3 h-3" />} Clear AI cache
                </button>
              )}
            </div>
            <div className="flex items-center gap-2">
              <div className="relative flex-1">
                {slashOpen && (
                  <SlashCommandMenu query={slashQuery} context={buildSlashCtx()} recent={slashRecent}
                    onApply={applySlashCmd} onFill={fillSlashCmd} onAction={actionSlashCmd} onClose={closeSlash} />
                )}
                <input ref={inputRef} value={question} onChange={e => { setQuestion(e.target.value); handleSlashInput(e.target.value); }} onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { if (slashOpen) return; e.preventDefault(); ask(); } }}
                  placeholder="Ask about your data..."
                  className="w-full h-14 pl-5 pr-14 rounded-2xl bg-muted/30 border border-border/50 text-base text-foreground placeholder:text-muted-foreground/70 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary/50 transition-all"
                  data-testid="explore-input" />
                <button onClick={() => (loading ? stop() : ask())} disabled={!loading && !question.trim()} className={`absolute right-2 top-1/2 -translate-y-1/2 p-2.5 rounded-xl transition-all disabled:opacity-40 ${loading ? 'bg-destructive text-white hover:bg-destructive/90' : 'bg-primary text-primary-foreground hover:bg-primary/90'}`} data-testid={loading ? 'explore-stop-btn' : 'explore-ask-btn'} title={loading ? 'Stop analyzing' : 'Ask'}>
                  {loading ? <Square className="w-5 h-5 fill-current" /> : <ArrowRight className="w-5 h-5" />}
                </button>
              </div>
              {voiceEnabled && (
                <button onClick={() => setVoiceOpen(true)} className="shrink-0 p-2.5 rounded-xl bg-primary/10 text-primary hover:bg-primary/20 transition-all" title={`Talk to ${brandName} (experimental)`} data-testid="voice-fab">
                  <Mic className="w-5 h-5" />
                </button>
              )}
            </div>
            <div className="flex justify-between items-center mt-1.5">
              {asOfControl}
              <button onClick={showHelp} className="flex items-center gap-1 text-[10px] text-muted-foreground hover:text-primary transition-colors" data-testid="explore-help-btn" title="See everything you can do in AI Explore">
                <HelpCircle className="w-3 h-3" /> What can I do here?
              </button>
            </div>
          </div>

          {/* Suggested prompts */}
          <div className="w-full mb-8">
            {canSqlRead && (
              <div className="mb-4 flex items-center gap-4">
                <button onClick={() => setShowSqlExamples(true)} className="text-xs text-sky-700 dark:text-sky-300/80 hover:text-sky-800 dark:hover:text-sky-300 flex items-center gap-1.5 transition-colors" data-testid="sql-examples-toggle">
                  <BookOpen className="w-3.5 h-3.5" /> SQL Library <span className="text-[10px] text-muted-foreground">(train the AI)</span>
                  {pendingSuggestions > 0 && (
                    <span className="ml-1 inline-flex items-center justify-center min-w-[16px] h-4 px-1 rounded-full bg-amber-500 text-[9px] font-bold text-black" title={`${pendingSuggestions} suggestion${pendingSuggestions > 1 ? 's' : ''} awaiting approval`} data-testid="sql-suggestions-badge">{pendingSuggestions}</span>
                  )}
                </button>
              </div>
            )}
            <p className="text-xs text-muted-foreground mb-3">Suggested questions{suggestionsLoading ? <Loader2 className="w-3 h-3 inline ml-1.5 animate-spin" /> : ''}</p>
            <div className="grid grid-cols-2 gap-2">
              {suggestedPrompts.map((p, i) => (
                <button key={`prompt-${i}-${p.slice(0, 30)}`} onClick={() => { setQuestion(p); setTimeout(() => ask(p), 100); }} className="text-left p-3 rounded-xl border border-border/30 hover:border-primary/30 hover:bg-primary/5 text-xs text-muted-foreground hover:text-foreground transition-all" data-testid={`suggested-${i}`}>
                  <Sparkles className="w-3 h-3 text-primary/50 inline mr-1.5" />{p}
                </button>
              ))}
            </div>
          </div>

          {/* Worth Investigating */}
          <div className="w-full mb-6">
            {anomalySuggestions.length === 0 && !anomalyLoading ? (
              <button onClick={() => {
                setAnomalyLoading(true);
                Promise.all([api.exploreAnomalies({ model_id: selectedModel }).catch(() => ({ anomalies: [] })), api.exploreAutoTrends({ model_id: selectedModel }).catch(() => ({ trends: [] }))])
                  .then(([anom, trends]) => setAnomalySuggestions([...(anom.anomalies || []), ...(trends.trends || []).map(t => ({ ...t, _isTrend: true }))]))
                  .finally(() => setAnomalyLoading(false));
              }} className="w-full p-3 rounded-xl border border-amber-500/20 hover:border-amber-500/40 hover:bg-amber-500/5 text-xs text-amber-400 transition-all flex items-center justify-center gap-2" data-testid="load-investigations-btn">
                <Bell className="w-3.5 h-3.5" /> Scan for Issues Worth Investigating
              </button>
            ) : anomalyLoading ? (
              <div className="flex items-center gap-2 py-3 text-xs text-muted-foreground justify-center"><Loader2 className="w-3 h-3 animate-spin" /> Scanning for anomalies and trends...</div>
            ) : (
              <div>
                <p className="text-xs text-muted-foreground mb-2 flex items-center gap-1"><Bell className="w-3 h-3 text-amber-400" /> Worth investigating</p>
                <div className="space-y-1.5">
                  {anomalySuggestions.slice(0, 4).map((a, i) => (
                    <button key={`anom-${a.title || i}-${i}`} onClick={() => { setQuestion(a.query_prompt); setTimeout(() => ask(a.query_prompt), 100); }}
                      className="w-full text-left p-3 rounded-xl border border-amber-500/20 bg-amber-500/5 hover:border-amber-500/40 hover:bg-amber-500/10 transition-all" data-testid={`anomaly-${i}`}>
                      <p className="text-xs font-medium text-foreground">{a.title}</p>
                      <p className="text-[10px] text-muted-foreground mt-0.5">{a.description}</p>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Investigations (Trusted-AI Phase B) — reopen a governed investigation and keep drilling. Shown for ALL models on the landing so any past investigation can be reopened/shared. */}
          {investigations.length > 0 && (
            <div className="w-full mb-6" data-testid="investigations-history">
              <p className="text-xs text-muted-foreground mb-2 flex items-center gap-1.5"><Compass className="w-3 h-3 text-violet-500" /> Investigations</p>
              <div className="space-y-1">
                {investigations.slice(0, 6).map(iv => (
                  <div key={iv.session_id} className="w-full flex items-center gap-1 rounded-lg border border-violet-500/20 hover:bg-violet-500/5 transition-colors">
                    <button onClick={() => loadSession(iv.session_id)} className="flex-1 min-w-0 text-left px-3 py-2 text-xs flex items-center gap-2" data-testid={`investigation-${iv.session_id}`}>
                      <Microscope className="w-3 h-3 flex-shrink-0 text-violet-500" />
                      <span className="truncate text-foreground">{iv.title || iv.summary || 'Investigation'}</span>
                      <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-violet-500/10 text-violet-600 dark:text-violet-300 flex-shrink-0">{iv.focus_label}</span>
                      <span className="text-[9px] text-muted-foreground/70 flex-shrink-0">{iv.turn_count || 1} turn{(iv.turn_count || 1) > 1 ? 's' : ''}</span>
                      <span className="text-[9px] text-muted-foreground/70 ml-auto flex-shrink-0">{iv.updated_at ? new Date(iv.updated_at).toLocaleDateString() : ''}</span>
                    </button>
                    <button onClick={() => { setShareTarget(iv); setShareEmail(''); }} title="Share with a teammate"
                      className="flex-shrink-0 p-1.5 mr-1 rounded-md text-muted-foreground hover:text-violet-600 dark:hover:text-violet-300 hover:bg-violet-500/10" data-testid={`investigation-share-${iv.session_id}`}>
                      <Share2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Recent sessions */}
          {sessions.filter(s => s.model_id === selectedModel).length > 0 && (
            <div className="w-full mb-6">
              <p className="text-xs text-muted-foreground mb-2">Recent sessions</p>
              <div className="space-y-1">
                {sessions.filter(s => s.model_id === selectedModel).slice(0, 5).map(s => {
                  const firstQ = s.first_question || (s.messages || []).find(m => m.role === 'user')?.content;
                  const label = (firstQ || 'Session').slice(0, 80);
                  return (
                  <button key={s.session_id} onClick={() => loadSession(s.session_id)} className="w-full text-left px-3 py-2 rounded-lg hover:bg-muted/30 text-xs text-muted-foreground hover:text-foreground transition-colors flex items-center gap-2" data-testid={`recent-session-${s.session_id}`}>
                    <Clock className="w-3 h-3 flex-shrink-0" />
                    <span className="truncate">{label}</span>
                    <span className="text-[9px] text-muted-foreground/70 ml-auto flex-shrink-0">{s.updated_at ? new Date(s.updated_at).toLocaleDateString() : ''}</span>
                  </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Pinned Findings */}
          {findings.length > 0 && (
            <div className="w-full">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs text-muted-foreground">Pinned Findings ({findings.length})</p>
                <input value={findingsSearch} onChange={e => setFindingsSearch(e.target.value)} placeholder="Search findings..." className="h-6 px-2 rounded-lg text-[10px] bg-muted/20 border border-border/30 outline-none w-40 focus:border-primary/30" />
              </div>
              <div className="space-y-2">
                {findings.filter(f => !findingsSearch || f.title?.toLowerCase().includes(findingsSearch.toLowerCase()) || f.summary?.toLowerCase().includes(findingsSearch.toLowerCase())).slice(0, 8).map(f => (
                  <div key={f.finding_id} className="glass-card rounded-xl p-3 border border-border/30">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="text-xs font-medium text-foreground">{f.title}</p>
                        <p className="text-[10px] text-muted-foreground mt-0.5">{f.summary}</p>
                      </div>
                      <button onClick={() => removeFinding(f.finding_id)} className="text-muted-foreground/30 hover:text-destructive p-0.5"><span className="text-[9px]">x</span></button>
                    </div>
                    <p className="text-[8px] text-muted-foreground/70 mt-1">{f.created_at ? new Date(f.created_at).toLocaleString() : ''}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
      {voiceDock}
      {/* Inline Create-Alert editor — stays on the insight, supports multiple AND/OR conditions */}
      {alertDialogJsx}
      {presetDialogJsx}

      <Dialog open={!!shareTarget} onOpenChange={(o) => { if (!o) setShareTarget(null); }}>        <DialogContent className="max-w-md" data-testid="share-investigation-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Share2 className="w-4 h-4 text-violet-500" /> Share investigation</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-1">
            <p className="text-xs text-muted-foreground">
              Send <span className="text-foreground font-medium">{shareTarget?.title || shareTarget?.summary || 'this investigation'}</span> to a teammate. They'll get an in-app notification with a link to reopen it and keep drilling from the same context.
            </p>
            <input
              type="email"
              value={shareEmail}
              onChange={(e) => setShareEmail(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') submitShare(); }}
              placeholder="teammate@company.com"
              className="w-full h-9 px-3 rounded-lg border border-border/50 bg-background text-sm"
              data-testid="share-investigation-email"
            />
            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={() => setShareTarget(null)}>Cancel</Button>
              <Button size="sm" onClick={submitShare} disabled={sharing || !shareEmail.trim()} data-testid="share-investigation-submit">
                {sharing ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Share2 className="w-3.5 h-3.5 mr-1" />} Share
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      </>
    );
  }

  // Analysis workspace
  return (
    <>
    <div className="flex h-full" data-testid="explore-workspace">
      {/* Main analysis area */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top bar */}
        <div className="flex items-center gap-3 px-6 py-3 border-b border-border/50">
          <Sparkles className="w-4 h-4 text-primary" />
          <h2 className="text-sm font-heading font-semibold text-foreground">{getTerm('ai_explore')}</h2>
          <Select value={selectedModel} onValueChange={setSelectedModel}>
            <SelectTrigger className="w-44 h-7 text-[10px] rounded-lg bg-muted/30 border-border/30"><SelectValue /></SelectTrigger>
            <SelectContent>{visibleModels.map(m => (
              <SelectItem key={m.model_id} value={m.model_id} className="text-xs">
                <span className="flex items-center gap-1.5">
                  {m?.endorsement?.level === 'certified' && <BadgeCheck className="w-3 h-3 text-emerald-400" />}
                  {m?.endorsement?.level === 'promoted' && <ShieldCheck className="w-3 h-3 text-amber-400" />}
                  {m.name}
                </span>
              </SelectItem>
            ))}</SelectContent>
          </Select>
          <div className="ml-auto flex gap-2">
            <Button variant="ghost" size="sm" className="text-xs rounded-lg" onClick={generateExecSummary} disabled={summaryLoading || !sessionId}>{summaryLoading ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <FileText className="w-3 h-3 mr-1" />} Summary</Button>
            <Button variant="ghost" size="sm" className="text-xs rounded-lg" onClick={shareCurrentSession} disabled={!sessionId}><Share2 className="w-3 h-3 mr-1" /> Share</Button>
            <Button variant="ghost" size="sm" className="text-xs rounded-lg" onClick={newSession}>New {getTerm('ai_explore', 'singular')}</Button>
            {isReviewer && sessionId && messages.some(m => m.role === 'assistant') && (
              <Button variant="ghost" size="sm" className="text-xs rounded-lg text-rose-600 dark:text-rose-400 hover:text-rose-700" onClick={() => setShowReport(true)} data-testid="report-conversation-btn" title="Flag this whole conversation as wrong for AI review">
                <Flag className="w-3.5 h-3.5 mr-1" /> Report a problem
              </Button>
            )}
            {isReviewer && (
              <Button variant="ghost" size="sm" className="text-xs rounded-lg relative" onClick={() => setShowReviewQueue(true)} data-testid="review-queue-btn" title="Review reported conversations and apply AI-proposed fixes">
                <ClipboardCheck className="w-3.5 h-3.5 mr-1" /> Review queue
                {reviewOpenCount > 0 && <span className="ml-1 inline-flex items-center justify-center min-w-[16px] h-4 px-1 rounded-full bg-amber-500 text-[9px] font-bold text-black" data-testid="review-queue-badge">{reviewOpenCount}</span>}
              </Button>
            )}
            <Button variant="ghost" size="sm" className="text-xs rounded-lg" onClick={() => setAiExportOpen(true)} data-testid="explore-export-btn"><Download className="w-3.5 h-3.5 mr-1" /> Export</Button>
            <Button variant="ghost" size="sm" className={`text-xs rounded-lg ${historyOpen ? 'text-primary' : ''}`} onClick={() => { const n = !historyOpen; setHistoryOpen(n); if (n) refreshSessions(); }} data-testid="explore-history-toggle"><HistoryIcon className="w-3.5 h-3.5 mr-1" /> History</Button>
            <Button variant="ghost" size="icon" className="rounded-lg w-8 h-8" title={chimeMuted ? 'Answer chime is off — click to turn on' : 'Answer chime is on — click to mute'} onClick={() => { const m = !chimeMuted; setChimeMuted(m); setChimeMutedState(m); }} data-testid="chime-toggle">
              {chimeMuted ? <VolumeX className="w-4 h-4 text-muted-foreground" /> : <Volume2 className="w-4 h-4 text-primary" />}
            </Button>
            <Button variant="ghost" size="sm" className="text-xs rounded-lg" onClick={() => setContextOpen(!contextOpen)}><Info className="w-3.5 h-3.5 mr-1" /> Context</Button>
          </div>
        </div>
        <ReportConversationDialog open={showReport} onClose={() => setShowReport(false)} sessionId={sessionId} messages={messages} />
        <RouteExplainDialog state={routeExplain} onClose={() => setRouteExplain({ open: false, rd: null })} />
        {showReviewQueue && (
          <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 backdrop-blur-sm p-4 overflow-auto" data-testid="review-queue-overlay" onClick={() => setShowReviewQueue(false)}>
            <div className="w-full max-w-2xl mt-12" onClick={(e) => e.stopPropagation()}>
              <ReviewQueuePanel modelId={selectedModel} onClose={() => setShowReviewQueue(false)} />
            </div>
          </div>
        )}

        {/* AI Credit Warning */}
        {aiCredits && aiCredits.limit && (
          <div className={`px-6 py-1.5 border-b flex items-center gap-2 text-[10px] ${!aiCredits.allowed ? 'bg-red-500/10 border-red-500/20 text-red-400' : aiCredits.remaining < aiCredits.limit * 0.2 ? 'bg-amber-500/10 border-amber-500/20 text-amber-400' : 'hidden'}`}>
            <Zap className="w-3 h-3" />
            {!aiCredits.allowed ? 'AI credits exhausted. Contact your administrator.' : `${aiCredits.remaining} AI credits remaining this month (${Math.round((aiCredits.remaining / aiCredits.limit) * 100)}%)`}
          </div>
        )}

        {/* Shared session banner + presence */}
        {isSharedSession && (
          <div className="px-6 py-2 border-b border-indigo-500/20 bg-indigo-500/5 flex items-center gap-3">
            <Badge className="bg-indigo-500/15 text-indigo-400 border-indigo-500/20 text-[9px]">
              {wsConnected ? 'Live' : 'Collaborative Session'}
            </Badge>
            {wsConnected && <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" title="WebSocket connected" />}
            {/* Typing indicators */}
            {Object.values(typingUsers).filter(Boolean).length > 0 && (
              <span className="text-[9px] text-indigo-400 italic animate-pulse">
                {Object.values(typingUsers).filter(Boolean).join(', ')} typing...
              </span>
            )}
            <div className="flex items-center gap-1.5 ml-auto">
              {Object.entries(presence).filter(([id]) => id !== user?.user_id).map(([id, p]) => (
                <div key={id} className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-muted/30 border border-border/30" title={`${p.name || id} — last seen ${p.last_seen ? new Date(p.last_seen).toLocaleTimeString() : 'recently'}`}>
                  <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                  <span className="text-[9px] text-muted-foreground">{p.name || id}</span>
                </div>
              ))}
              {Object.keys(presence).filter(id => id !== user?.user_id).length === 0 && (
                <span className="text-[9px] text-muted-foreground/70">Only you are here</span>
              )}
            </div>
          </div>
        )}

        {/* Messages / Results */}
        <div className="flex-1 overflow-auto px-6 py-4 space-y-6">
          {/* Executive Summary renders at the BOTTOM of the chat (below) so it's visible without scrolling up */}

          {messages.map((msg, idx) => (
            <div key={msg.timestamp || `msg-${idx}`} className={msg.role === 'user' ? 'flex justify-end' : ''}>
              {msg._help ? (
                <ExploreHelpPanel onPick={(ex) => { setQuestion(ex); ask(ex); }} />
              ) : (certifiedOnly && msg.role !== 'user' && ['ad_hoc', 'mixed'].includes(msg.result?.trust_status) && !revealedAdhoc.has(idx)) ? (
                <div className="glass-card rounded-2xl p-4 border border-amber-500/25 flex items-center gap-3" data-testid={`adhoc-hidden-${idx}`}>
                  <ShieldCheck className="w-4 h-4 text-amber-400 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-amber-700 dark:text-amber-200/90 font-medium">Ad-hoc result hidden (certified-only mode)</p>
                    <p className="text-[11px] text-muted-foreground truncate">{msg.result?.trust_label || 'Not a certified measure'}{msg.result?.trust_formula ? ` — ${msg.result.trust_formula}` : ''}</p>
                  </div>
                  <button
                    onClick={() => setRevealedAdhoc(prev => { const n = new Set(prev); n.add(idx); return n; })}
                    className="flex-shrink-0 px-2.5 py-1 rounded-lg text-[11px] font-medium border border-amber-500/40 text-amber-700 dark:text-amber-200 hover:bg-amber-500/10 transition-colors"
                    data-testid={`adhoc-reveal-${idx}`}
                  >
                    Show anyway
                  </button>
                </div>
              ) : (
              <ExploreMessage
                msg={msg} idx={idx} messagesLength={messages.length}
                user={user} isSharedSession={isSharedSession}
                allFields={allFields} formatValue={formatValue}
                sessionId={sessionId} selectedModel={selectedModel}
                saveAsInsight={saveAsInsight} createAlert={createAlert}
                addToDashboard={openAddToDashboard}
                saveAsVisual={saveAsVisual}
                pinToKpi={pinToKpi}
                runRCA={runRCA} rcaLoading={rcaLoading}
                runInvestigate={runInvestigate} investigateLoading={investigateLoading} resetInvestigation={resetInvestigation}
                setExploreQueryView={setExploreQueryView}
                setQuestion={setQuestion} ask={ask}
                setAiRawView={setAiRawView}
                startFullInvestigation={startFullInvestigation}
                startDiscussion={startDiscussion}
                discEntry={discIndex[sessionId]}
                onVizChange={handleVizChange}
                onChangeGrain={changeGrain}
                onRerunQuery={rerunQuery}
                onReaskAI={reaskAI}
                onRevisedResult={appendRevisedResult}
                modelId={selectedModel}
                modelEditedAt={modelEditedAt}
                isAdmin={isAdmin}
                onTryAdvancedSql={openAdvancedSql}
                onBuildMeasure={openAiMeasure}
                onOpenAsOf={() => setAsOfPromptOpen(true)}
                onDisambiguate={(baseQuestion, assumption) => {
                  const q = baseQuestion || '';
                  if (!q.trim()) return;
                  forceClarifyRef.current = { assumption: assumption || '' };
                  setQuestion(q);
                  setTimeout(() => ask(q), 50);
                }}
                onModelEdited={() => setModelEditedAt(Date.now())}
              />
              )}
            </div>
          ))}

          {/* Root Cause Analysis — shown after messages */}
          {rcaLoading && (
            <div className="max-w-4xl glass-card rounded-2xl p-5 border border-amber-500/20">
              <div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin text-amber-400" /> Running root cause analysis...</div>
            </div>
          )}
          {rcaResult && (
            <div className="max-w-4xl space-y-3" data-testid="rca-result">
              <div className="glass-card rounded-2xl p-5 border border-amber-500/20 bg-amber-500/5">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-heading font-semibold text-foreground flex items-center gap-2"><Search className="w-4 h-4 text-amber-400" /> Root Cause Analysis</h3>
                  <button onClick={() => setRcaResult(null)} className="text-muted-foreground/30 hover:text-foreground text-xs">x</button>
                </div>
                {rcaResult.summary && <p className="text-xs text-foreground/80 mb-3">{humanizeFields(rcaResult.summary)}</p>}
                {(rcaResult.steps || []).map(step => (
                  <div key={step.step} className="mb-3">
                    <button onClick={() => setRcaExpanded(prev => { const s = new Set(prev); s.has(step.step) ? s.delete(step.step) : s.add(step.step); return s; })} className="w-full text-left flex items-center gap-2 py-1.5">
                      <ChevronRight className={`w-3.5 h-3.5 text-amber-400 transition-transform ${rcaExpanded.has(step.step) ? 'rotate-90' : ''}`} />
                      <Badge className="text-[8px] bg-amber-500/20 text-amber-400 border-amber-500/30">Step {step.step}</Badge>
                      <span className="text-xs font-medium text-foreground truncate">{humanizeFields(step.title)}</span>
                    </button>
                    {rcaExpanded.has(step.step) && (
                      <div className="ml-6 mt-1 space-y-2">
                        <p className="text-xs text-foreground/70">{humanizeFields(step.finding)}</p>
                        {step._liveData?.length > 0 && (
                          <div className="w-full rounded-lg border border-border/20 overflow-hidden" style={{ height: 350 }}>
                            {step.dimensions_to_query?.length > 0 && <ChartRenderer vizType="bar_chart" data={step._liveData} rowDims={step.dimensions_to_query} colDim="" showGrid={true} showLegend={false} vizConfig={{}} formatValue={formatValue} allFields={allFields} fieldOverrides={{}} onDrill={() => {}} marks={{}} />}
                          </div>
                        )}
                        {step.factors?.map((f, fi) => (
                          <div key={fi} className="flex items-start gap-2 p-2 rounded-lg bg-muted/10 border border-border/10">
                            <Badge className={`text-[7px] flex-shrink-0 mt-0.5 ${f.impact === 'high' ? 'bg-red-500/20 text-red-400' : f.impact === 'medium' ? 'bg-amber-500/20 text-amber-400' : 'bg-sky-500/20 text-sky-400'}`}>{f.impact}</Badge>
                            <div>
                              <p className="text-[11px] font-medium text-foreground">{humanizeFields(f.factor)}</p>
                              <p className="text-[10px] text-muted-foreground">{humanizeFields(f.explanation)}</p>
                              {f.drill_prompt && <button onClick={() => { setQuestion(f.drill_prompt); setTimeout(() => ask(f.drill_prompt), 100); }} className="text-[9px] text-primary hover:underline mt-0.5">{humanizeFields(f.drill_prompt)}</button>}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
                {rcaResult.recommendation && (
                  <div className="mt-3 p-2.5 rounded-lg bg-primary/10 border border-primary/20 text-xs text-foreground/80">
                    <span className="font-semibold text-primary">Recommendation:</span> {humanizeFields(rcaResult.recommendation)}
                  </div>
                )}
              </div>
            </div>
          )}

          {loading && (() => {
            const lastUserMsg = [...messages].reverse().find(m => m.role === 'user')?.content || question || '';
            const isFcPending = /\b(forecast|forecasting|project|projection|predict)\b/i.test(lastUserMsg);
            let nSeries = 0;
            if (isFcPending) {
              const lastRes = [...messages].reverse().find(m => m.role === 'assistant' && m.result);
              const r = lastRes?.result;
              const seriesField = r ? (r.color_by || ((r.dimensions || []).length > 1 ? r.dimensions[r.dimensions.length - 1] : null)) : null;
              const rows = lastRes?.liveData || [];
              if (seriesField && rows.length) nSeries = new Set(rows.map(row => String(row[seriesField]))).size;
            }
            const label = isFcPending ? (nSeries > 1 ? `Crunching ${nSeries} series…` : 'Crunching your forecast…') : 'Analyzing...';
            return (
              <div className="flex items-center gap-2 text-sm text-muted-foreground" data-testid="explore-loading">
                <Loader2 className="w-4 h-4 animate-spin text-primary" /> {label}
                <button onClick={stop} data-testid="explore-stop-inline" title="Stop analyzing"
                  className="ml-1 flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-md border border-destructive/40 text-destructive hover:bg-destructive/10 transition-colors">
                  <Square className="w-3 h-3 fill-current" /> Stop
                </button>
              </div>
            );
          })()}
          {false && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="w-4 h-4 animate-spin text-primary" /> Analyzing...
            </div>
          )}
          {summary && (
            <div ref={summaryRef} className="glass-card rounded-2xl p-5 border border-primary/20 bg-primary/5 max-w-4xl" data-testid="exec-summary">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-heading font-semibold text-foreground">Executive Summary</h3>
                <div className="flex gap-1">
                  <Button variant="outline" size="sm" className="text-[9px] h-6 rounded-lg" onClick={() => { navigator.clipboard.writeText(`${summary.title}\n\n${summary.key_finding}\n\n${summary.details?.join('\n')}\n\n${summary.recommendation}`); toast.success('Copied'); }}>Copy</Button>
                  <Button variant="outline" size="sm" className="text-[9px] h-6 rounded-lg" onClick={() => saveAsInsight({ answer: summary.title, narrative: summary.key_finding })}>Save as Insight</Button>
                  <button onClick={() => setSummary(null)} className="text-muted-foreground/30 hover:text-foreground ml-1 text-xs">x</button>
                </div>
              </div>
              <p className="text-base font-semibold text-foreground mb-2">{summary.title}</p>
              <p className="text-sm text-foreground/80 mb-3">{summary.key_finding}</p>
              {summary.details?.length > 0 && (
                <ul className="space-y-1 mb-3">{summary.details.map((d, i) => <li key={i} className="text-xs text-muted-foreground flex items-start gap-2"><span className="text-primary mt-0.5">-</span>{d}</li>)}</ul>
              )}
              {summary.recommendation && (
                <div className="p-2.5 rounded-lg bg-primary/10 border border-primary/20 text-xs text-foreground/80"><span className="font-semibold text-primary">Recommendation:</span> {summary.recommendation}</div>
              )}
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Diff-of-Intent banner (Iter B) — flag AI removals the user
            didn't ask for. Revert restores the previous chart config in
            the input so one more turn can put things back. */}
        {diffOfIntent && Date.now() - diffOfIntent.at < 30000 && (
          <div className="px-6 pt-2 pb-1 border-t border-border/30" data-testid="diff-of-intent-banner">
            <div className="max-w-3xl mx-auto flex items-start gap-2 p-2 rounded-lg bg-amber-500/10 border border-amber-500/30">
              <div className="text-amber-400 mt-0.5">⚠</div>
              <div className="flex-1 text-[11px] leading-relaxed">
                <p className="text-amber-700 dark:text-amber-300 font-medium">The AI removed things you didn&apos;t ask about:</p>
                <ul className="text-amber-700/90 dark:text-amber-200/80 mt-1 space-y-0.5">
                  {diffOfIntent.droppedDims.length > 0 && (
                    <li>Dimensions: <span className="font-mono">{diffOfIntent.droppedDims.join(', ')}</span></li>
                  )}
                  {diffOfIntent.droppedMsrs.length > 0 && (
                    <li>Measures: <span className="font-mono">{diffOfIntent.droppedMsrs.join(', ')}</span></li>
                  )}
                  {diffOfIntent.droppedFilters.length > 0 && (
                    <li>Filters: <span className="font-mono">{diffOfIntent.droppedFilters.map(f => `${f.field} ${f.operator} ${JSON.stringify(f.value)}`).join(', ')}</span></li>
                  )}
                </ul>
              </div>
              <div className="flex flex-col gap-1">
                <button
                  onClick={() => {
                    // Re-inject a prompt that instructs the model to
                    // restore. Cleaner than mutating past chart state
                    // because it goes through the same iterative
                    // refinement rule the LLM already respects.
                    const restoreBits = [];
                    if (diffOfIntent.droppedDims.length) restoreBits.push(`add ${diffOfIntent.droppedDims.join(' and ')} back`);
                    if (diffOfIntent.droppedMsrs.length) restoreBits.push(`bring back the ${diffOfIntent.droppedMsrs.join(' and ')} measure(s)`);
                    if (diffOfIntent.droppedFilters.length) restoreBits.push(`re-apply filter ${diffOfIntent.droppedFilters.map(f => `${f.field} ${f.operator} ${JSON.stringify(f.value)}`).join(' and ')}`);
                    setQuestion(restoreBits.join(', ') + ' — keep everything else the same.');
                    setDiffOfIntent(null);
                    if (inputRef.current) inputRef.current.focus();
                  }}
                  className="text-[10px] px-2 py-0.5 rounded bg-amber-500/20 hover:bg-amber-500/30 text-amber-700 dark:text-amber-200 border border-amber-500/40"
                  data-testid="diff-revert-btn"
                >Undo</button>
                <button
                  onClick={() => setDiffOfIntent(null)}
                  className="text-[10px] text-amber-700/70 dark:text-amber-300/60 hover:text-amber-800 dark:hover:text-amber-200"
                  data-testid="diff-dismiss-btn"
                >Dismiss</button>
              </div>
            </div>
          </div>
        )}

        {/* Sticky filters chip strip — filters pinned here re-apply on every follow-up regardless
            of what the LLM returns. Shows both LLM-returned filters (with a pin toggle) and
            pinned filters (always present). Location: persistent bar just above the input. */}
        {(() => {
          const lastResult = [...messages].reverse().find(m => m.result)?.result;
          const activeLlmFilters = Array.isArray(lastResult?.filters_applied) ? lastResult.filters_applied : [];
          // Union of last-turn filters + pinned filters (deduped by field|operator).
          const seen = new Set();
          const chips = [];
          for (const f of activeLlmFilters) {
            const k = `${f.field}|${f.operator}`;
            if (seen.has(k)) continue;
            seen.add(k);
            chips.push({ ...f, pinned: pinnedFilters.some(p => `${p.field}|${p.operator}` === k) });
          }
          for (const p of pinnedFilters) {
            const k = `${p.field}|${p.operator}`;
            if (seen.has(k)) continue;
            seen.add(k);
            chips.push({ ...p, pinned: true });
          }
          if (chips.length === 0 && !droppedPinnedNote) return null;
          const fmtValue = (v) => Array.isArray(v) ? v.join(', ') : String(v);
          return (
            <div className="px-6 pt-2 pb-1 border-t border-border/30" data-testid="explore-filter-chips">
              {droppedPinnedNote && Date.now() - droppedPinnedNote.at < 8000 && (
                <div className="max-w-3xl mx-auto mb-1.5 text-[10px] text-amber-400 flex items-center gap-1.5">
                  <span>📌</span>
                  <span>Re-applied {droppedPinnedNote.filters.length} pinned filter{droppedPinnedNote.filters.length > 1 ? 's' : ''} that the AI would have dropped ({droppedPinnedNote.filters.map(f => `${f.field} ${f.operator} ${fmtValue(f.value)}`).join(', ')})</span>
                  <button onClick={() => setDroppedPinnedNote(null)} className="ml-auto text-muted-foreground hover:text-foreground">×</button>
                </div>
              )}
              {chips.length > 0 && (
                <div className="max-w-3xl mx-auto flex flex-wrap items-center gap-1.5">
                  <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">Filters</span>
                  {chips.map((c, i) => (
                    <button
                      key={`${c.field}|${c.operator}|${i}`}
                      onClick={() => {
                        const k = `${c.field}|${c.operator}`;
                        setPinnedFilters(prev => {
                          const isPinned = prev.some(p => `${p.field}|${p.operator}` === k);
                          if (isPinned) {
                            toast.info(`Unpinned "${c.field} ${c.operator} ${fmtValue(c.value)}" — next refinement may drop it`);
                            return prev.filter(p => `${p.field}|${p.operator}` !== k);
                          }
                          toast.success(`Pinned "${c.field} ${c.operator} ${fmtValue(c.value)}" — will survive every refinement`);
                          return [...prev, { field: c.field, operator: c.operator, value: c.value }];
                        });
                      }}
                      className={`group inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] transition-all ${c.pinned ? 'bg-primary/10 border-primary/40 text-primary' : 'bg-muted/30 border-border/40 text-foreground/70 hover:border-primary/30'}`}
                      title={c.pinned ? 'Pinned — click to unpin' : 'Click the pin to keep this filter on every follow-up'}
                      data-testid={`filter-chip-${c.field}-${c.operator}`}
                    >
                      <span className={c.pinned ? 'opacity-100' : 'opacity-40 group-hover:opacity-70'}>📌</span>
                      <span className="font-mono">{c.field}</span>
                      <span className="opacity-60">{c.operator}</span>
                      <span className="font-medium">{fmtValue(c.value)}</span>
                    </button>
                  ))}
                  {pinnedFilters.length > 0 && (
                    <button
                      onClick={() => { setPinnedFilters([]); toast.info('Cleared all pinned filters'); }}
                      className="text-[10px] text-muted-foreground hover:text-destructive underline ml-1"
                      data-testid="clear-pinned-filters-btn"
                    >clear all pins</button>
                  )}
                </div>
              )}
            </div>
          );
        })()}

        {/* Input bar */}
        <div className="px-6 py-3 border-t border-border/50">
          <div className="max-w-3xl mx-auto">
            <div className="flex justify-end mb-1.5">
              <button
                onClick={toggleAutoImprove}
                className={`flex items-center gap-1.5 px-2 py-1 rounded-lg text-[10px] font-medium transition-all border ${autoImprove ? 'bg-emerald-500/15 border-emerald-500/40 text-emerald-700 dark:text-emerald-300' : 'bg-muted/20 border-border/30 text-muted-foreground hover:bg-muted/30'}`}
                data-testid="auto-improve-toggle-followup"
                title="When on, answers that score low on the AI's quality self-check are automatically re-asked once for a stronger response."
              >
                <Sparkles className="w-3 h-3" /> {autoImprove ? 'Auto-improve on' : 'Auto-improve weak answers'}
              </button>
            </div>
            <div className="flex items-center gap-2">
              <div className="relative flex-1">
                {slashOpen && (
                  <SlashCommandMenu query={slashQuery} context={buildSlashCtx()} recent={slashRecent}
                    onApply={applySlashCmd} onFill={fillSlashCmd} onAction={actionSlashCmd} onClose={closeSlash} />
                )}
                <input ref={inputRef} value={question} onChange={e => { setQuestion(e.target.value); handleSlashInput(e.target.value); if (wsRef.current?.readyState === WebSocket.OPEN && isSharedSession) wsRef.current.send(JSON.stringify({ type: 'typing', typing: !!e.target.value })); }} onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { if (slashOpen) return; e.preventDefault(); ask(); } }}
                  placeholder="Ask a follow-up question..."
                  className="w-full h-11 pl-4 pr-12 rounded-xl bg-muted/30 border border-border/50 text-sm text-foreground placeholder:text-muted-foreground/70 focus:outline-none focus:ring-2 focus:ring-primary/30 transition-all"
                  data-testid="explore-followup-input" />
                <button onClick={() => (loading ? stop() : ask())} disabled={!loading && !question.trim()} className={`absolute right-1.5 top-1/2 -translate-y-1/2 p-2 rounded-lg transition-all disabled:opacity-40 ${loading ? 'bg-destructive text-white hover:bg-destructive/90' : 'bg-primary text-primary-foreground hover:bg-primary/90'}`} data-testid={loading ? 'explore-stop-btn-followup' : 'explore-ask-btn-followup'} title={loading ? 'Stop analyzing' : 'Ask'}>
                  {loading ? <Square className="w-4 h-4 fill-current" /> : <ArrowRight className="w-4 h-4" />}
                </button>
                <button onClick={showHelp} className="absolute right-11 top-1/2 -translate-y-1/2 p-2 rounded-lg text-muted-foreground/80 hover:text-primary transition-colors" data-testid="explore-help-btn-followup" title="See everything you can do in AI Explore">
                  <HelpCircle className="w-4 h-4" />
                </button>
              </div>
              {voiceEnabled && (
                <button onClick={() => setVoiceOpen(true)} className="shrink-0 p-2 rounded-lg bg-primary/10 text-primary hover:bg-primary/20 transition-all" title={`Talk to ${brandName} (experimental)`} data-testid="voice-fab-followup">
                  <Mic className="w-4 h-4" />
                </button>
              )}
            </div>
            {asOfControl}
            {aiMeasureDialog}
            {messages.some(m => m.role === 'assistant' && m.result) && (
              <div className="flex items-center gap-1.5 mt-1.5 flex-wrap" data-testid="explore-command-hints">
                <span className="text-[9px] text-muted-foreground/70">Try (instant, no AI):</span>
                {['make it a line chart', 'by month', 'top 10', 'hide legend'].map(cmd => (
                  <button
                    key={cmd}
                    onClick={() => ask(cmd)}
                    disabled={loading}
                    className="text-[9px] px-1.5 py-0.5 rounded-md bg-muted/30 border border-border/30 text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors disabled:opacity-40"
                    data-testid={`explore-hint-${cmd.replace(/\s+/g, '-')}`}
                  >
                    {cmd}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Chat history panel (ChatGPT-style, collapsible) */}
      {historyOpen && (
        <div className="w-72 border-l border-border/50 flex flex-col overflow-hidden" style={{ background: 'var(--glass-bg)' }} data-testid="explore-history-panel">
          <div className="p-4 border-b border-border/50 flex items-center justify-between">
            <h3 className="text-xs font-heading font-semibold text-foreground flex items-center gap-1.5"><HistoryIcon className="w-3.5 h-3.5 text-primary" /> Chat history</h3>
            <button onClick={() => setHistoryOpen(false)} className="text-muted-foreground hover:text-foreground" data-testid="explore-history-close"><X className="w-4 h-4" /></button>
          </div>
          <div className="p-2 border-b border-border/40 space-y-2">
            <button onClick={() => { newSession(); }} className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-xs text-primary hover:bg-primary/10 transition-colors" data-testid="explore-history-new">
              <Plus className="w-3.5 h-3.5" /> New chat
            </button>
            <div className="relative">
              <Search className="w-3 h-3 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground/60" />
              <input value={historySearch} onChange={e => setHistorySearch(e.target.value)} placeholder="Search chats…"
                className="w-full h-7 pl-7 pr-2 rounded-lg text-[11px] bg-muted/20 border border-border/30 outline-none focus:border-primary/30 text-foreground"
                data-testid="explore-history-search" />
            </div>
          </div>
          <div className="flex-1 overflow-auto p-2 space-y-0.5" data-testid="explore-history-list">
            {(() => {
              const term = historySearch.trim().toLowerCase();
              const list = sessions
                .filter(s => s.model_id === selectedModel)
                .slice()
                .sort((a, b) => new Date(b.updated_at || 0) - new Date(a.updated_at || 0))
                .map(s => {
                  const firstQ = s.first_question || (s.messages || []).find(m => m.role === 'user')?.content;
                  return { ...s, _label: s.title || firstQ || 'Untitled chat' };
                })
                .filter(s => !term || s._label.toLowerCase().includes(term))
                .slice(0, 50);
              if (list.length === 0) {
                return <p className="px-3 py-6 text-[11px] text-muted-foreground text-center" data-testid="explore-history-empty">{term ? 'No chats match your search.' : 'No prior chats yet. Ask a question to start one.'}</p>;
              }
              return list.map(s => {
                const label = s._label.slice(0, 90);
                const active = s.session_id === sessionId;
                if (renamingId === s.session_id) {
                  return (
                    <div key={s.session_id} className="flex items-center gap-1 px-2 py-1.5 rounded-lg border border-primary/30" data-testid={`history-rename-${s.session_id}`}>
                      <input autoFocus value={renameDraft} onChange={e => setRenameDraft(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter') saveRename(s.session_id); if (e.key === 'Escape') { setRenamingId(null); setRenameDraft(''); } }}
                        className="flex-1 min-w-0 h-6 px-2 rounded-md text-xs bg-muted/30 border border-border/40 outline-none focus:border-primary/40 text-foreground"
                        data-testid={`history-rename-input-${s.session_id}`} />
                      <button onClick={() => saveRename(s.session_id)} title="Save" className="text-emerald-500 hover:text-emerald-400 p-1" data-testid={`history-rename-save-${s.session_id}`}><Check className="w-3.5 h-3.5" /></button>
                      <button onClick={() => { setRenamingId(null); setRenameDraft(''); }} title="Cancel" className="text-muted-foreground hover:text-foreground p-1"><X className="w-3.5 h-3.5" /></button>
                    </div>
                  );
                }
                if (deletingId === s.session_id) {
                  return (
                    <div key={s.session_id} className="flex items-center gap-1 px-2 py-1.5 rounded-lg border border-rose-500/30 bg-rose-500/5" data-testid={`history-delete-confirm-${s.session_id}`}>
                      <span className="flex-1 min-w-0 text-[11px] text-rose-300 truncate">Delete this chat?</span>
                      <button onClick={() => deleteSession(s.session_id)} title="Confirm delete" className="text-rose-500 hover:text-rose-400 p-1" data-testid={`history-delete-yes-${s.session_id}`}><Check className="w-3.5 h-3.5" /></button>
                      <button onClick={() => setDeletingId(null)} title="Cancel" className="text-muted-foreground hover:text-foreground p-1"><X className="w-3.5 h-3.5" /></button>
                    </div>
                  );
                }
                return (
                  <div key={s.session_id}
                    className={`group w-full rounded-lg transition-colors flex items-center ${active ? 'bg-primary/10 border border-primary/30' : 'hover:bg-muted/30 border border-transparent'}`}
                    data-testid={`history-session-${s.session_id}`}>
                    <button onClick={() => { loadSession(s.session_id); }} className="flex-1 min-w-0 text-left px-3 py-2 flex flex-col gap-0.5">
                      <span className={`text-xs truncate ${active ? 'text-foreground font-medium' : 'text-foreground/90'}`}>{label}</span>
                      <span className="text-[9px] text-muted-foreground/70">{s.updated_at ? new Date(s.updated_at).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}{typeof s.turn_count === 'number' ? ` · ${s.turn_count} turn${s.turn_count === 1 ? '' : 's'}` : ''}</span>
                    </button>
                    <button onClick={(e) => { e.stopPropagation(); setRenamingId(s.session_id); setRenameDraft(s.title || s._label); }}
                      title="Rename chat" className="opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-primary p-1.5 flex-shrink-0"
                      data-testid={`history-rename-btn-${s.session_id}`}><Pencil className="w-3 h-3" /></button>
                    <button onClick={(e) => { e.stopPropagation(); setDeletingId(s.session_id); }}
                      title="Delete chat" className="opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-rose-400 p-1.5 mr-1 flex-shrink-0"
                      data-testid={`history-delete-btn-${s.session_id}`}><Trash2 className="w-3 h-3" /></button>
                  </div>
                );
              });
            })()}
          </div>
        </div>
      )}

      {/* Context panel */}
      {contextOpen && (
        <div className="w-72 border-l border-border/50 flex flex-col overflow-auto" style={{ background: 'var(--glass-bg)' }} data-testid="explore-context-panel">
          <div className="p-4 border-b border-border/50">
            <h3 className="text-xs font-heading font-semibold text-foreground">Analysis Context</h3>
          </div>
          <div className="p-4 space-y-4 text-[10px]">
            <div>
              <p className="text-muted-foreground uppercase tracking-wider font-semibold mb-1">Model</p>
              <p className="text-foreground">{models.find(m => m.model_id === selectedModel)?.name || 'None selected'}</p>
            </div>
            {analysisContext && (
              <>
                <div className="pt-1 border-t border-border/40" />
                <p className="text-primary/80 uppercase tracking-wider font-semibold text-[9px]">Context sent to the AI</p>
                {editingPersona === 'model' ? (
                  <div data-testid="context-model-persona">
                    <p className="text-muted-foreground uppercase tracking-wider font-semibold mb-1">Semantic Model Persona</p>
                    <textarea value={personaDraft} onChange={e => setPersonaDraft(e.target.value)} rows={4}
                      className="w-full rounded-md bg-muted/40 border border-border/50 p-2 text-[10px] text-foreground outline-none focus:ring-1 focus:ring-primary/50 resize-y"
                      data-testid="context-model-persona-input" placeholder="Describe the domain context the AI should assume for this model…" />
                    <div className="flex gap-1.5 mt-1">
                      <button onClick={savePersona} disabled={savingPersona} className="px-2 py-1 rounded-md bg-primary text-primary-foreground text-[10px] disabled:opacity-50" data-testid="context-model-persona-save">{savingPersona ? 'Saving…' : 'Save'}</button>
                      <button onClick={() => setEditingPersona(null)} disabled={savingPersona} className="px-2 py-1 rounded-md border border-border/50 text-[10px] text-muted-foreground hover:text-foreground" data-testid="context-model-persona-cancel">Cancel</button>
                    </div>
                  </div>
                ) : (
                  <div data-testid="context-model-persona">
                    <div className="flex items-center justify-between mb-1">
                      <p className="text-muted-foreground uppercase tracking-wider font-semibold">Semantic Model Persona</p>
                      {canEditModelPersona && (
                        <button onClick={() => startEditPersona('model', analysisContext.model_persona)} className="text-[9px] text-primary/80 hover:text-primary" data-testid="context-model-persona-edit">{analysisContext.model_persona ? 'Edit' : 'Add'}</button>
                      )}
                    </div>
                    {analysisContext.model_persona
                      ? <p className="text-foreground/80 leading-relaxed whitespace-pre-wrap">{analysisContext.model_persona}</p>
                      : <p className="text-foreground/40 italic">Not set for this model</p>}
                  </div>
                )}
                {analysisContext.role_persona && (editingPersona === 'role' ? (
                  <div data-testid="context-role-persona">
                    <p className="text-muted-foreground uppercase tracking-wider font-semibold mb-1">Your Role Persona{analysisContext.role_persona.role ? ` (${analysisContext.role_persona.role})` : ''}</p>
                    <textarea value={personaDraft} onChange={e => setPersonaDraft(e.target.value)} rows={4}
                      className="w-full rounded-md bg-muted/40 border border-border/50 p-2 text-[10px] text-foreground outline-none focus:ring-1 focus:ring-primary/50 resize-y"
                      data-testid="context-role-persona-input" />
                    <div className="flex gap-1.5 mt-1">
                      <button onClick={savePersona} disabled={savingPersona} className="px-2 py-1 rounded-md bg-primary text-primary-foreground text-[10px] disabled:opacity-50" data-testid="context-role-persona-save">{savingPersona ? 'Saving…' : 'Save'}</button>
                      <button onClick={() => setEditingPersona(null)} disabled={savingPersona} className="px-2 py-1 rounded-md border border-border/50 text-[10px] text-muted-foreground hover:text-foreground" data-testid="context-role-persona-cancel">Cancel</button>
                    </div>
                  </div>
                ) : (
                  <div data-testid="context-role-persona">
                    <div className="flex items-center justify-between mb-1">
                      <p className="text-muted-foreground uppercase tracking-wider font-semibold">Your Role Persona{analysisContext.role_persona.role ? ` (${analysisContext.role_persona.role})` : ''}</p>
                      {canEditRolePersona && (
                        <button onClick={() => startEditPersona('role', analysisContext.role_persona.text)} className="text-[9px] text-primary/80 hover:text-primary" data-testid="context-role-persona-edit">Edit</button>
                      )}
                    </div>
                    <p className="text-foreground/80 leading-relaxed whitespace-pre-wrap">{analysisContext.role_persona.text}</p>
                  </div>
                ))}
                {analysisContext.user_persona && (
                  <div data-testid="context-user-persona">
                    <p className="text-muted-foreground uppercase tracking-wider font-semibold mb-1">Your Persona</p>
                    <p className="text-foreground/80 leading-relaxed whitespace-pre-wrap">{analysisContext.user_persona}</p>
                  </div>
                )}
                <div data-testid="context-glossary">
                  <p className="text-muted-foreground uppercase tracking-wider font-semibold mb-1">Business Glossary ({(analysisContext.glossary || []).length})</p>
                  {(analysisContext.glossary || []).length === 0 ? (
                    <p className="text-foreground/40 italic">No glossary terms apply</p>
                  ) : (
                    <div className="space-y-1 max-h-48 overflow-auto">
                      {analysisContext.glossary.map((g, i) => (
                        <div key={i} className="mb-1">
                          <button onClick={() => navigate(`/glossary?term=${encodeURIComponent(g.term)}`)} className="font-semibold text-foreground hover:text-primary hover:underline text-left" title="Open in Glossary" data-testid={`context-glossary-term-${i}`}>{g.term}</button>
                          <span className={`ml-1 text-[8px] px-1 py-0.5 rounded-full ${g.scope === 'model' ? 'bg-primary/15 text-primary' : 'bg-muted/40 text-muted-foreground'}`}>{g.scope}</span>
                          {g.definition && <span className="text-foreground/70">: {g.definition}</span>}
                          {g.synonyms?.length > 0 && <span className="text-foreground/40"> · aka {g.synonyms.join(', ')}</span>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                {analysisContext.learned && (
                  <div data-testid="context-learned">
                    <p className="text-muted-foreground uppercase tracking-wider font-semibold mb-1">Learned Grounding</p>
                    <p className="text-foreground/80">{analysisContext.learned.certified_examples || 0} saved SQL Library {(analysisContext.learned.certified_examples === 1) ? 'query' : 'queries'} · {analysisContext.learned.thumbs_up_examples || 0} thumbs-up example{analysisContext.learned.thumbs_up_examples === 1 ? '' : 's'} used to ground answers</p>
                  </div>
                )}
                <div className="pt-1 border-t border-border/40" />
              </>
            )}
            {allFields.length > 0 && (
              <div>
                <p className="text-muted-foreground uppercase tracking-wider font-semibold mb-1">Available Fields ({allFields.length})</p>
                <div className="space-y-0.5 max-h-48 overflow-auto">
                  {allFields.map(f => (
                    <div key={f.name} className="flex items-center gap-1.5 py-0.5">
                      <div className={`w-1.5 h-1.5 rounded-full ${f.semantic_type === 'measure' ? 'bg-primary' : 'bg-emerald-400'}`} />
                      <span className="text-foreground/70 truncate">{f.description || f.name}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {messages.length > 0 && (() => {
              const lastResult = [...messages].reverse().find(m => m.result)?.result;
              if (!lastResult) return null;
              return (
                <>
                  {lastResult.filters_applied?.length > 0 && (
                    <div>
                      <p className="text-muted-foreground uppercase tracking-wider font-semibold mb-1">Filters Applied</p>
                      {lastResult.filters_applied.map((f, i) => (
                        <p key={i} className="text-foreground/70">{f.field} {f.operator} {f.value}</p>
                      ))}
                    </div>
                  )}
                  {lastResult.definitions_used?.length > 0 && (
                    <div>
                      <p className="text-muted-foreground uppercase tracking-wider font-semibold mb-1">Definitions</p>
                      {lastResult.definitions_used.map((d, i) => (
                        <div key={i} className="mb-1"><span className="font-semibold text-foreground">{d.term}:</span> <span className="text-foreground/80">{d.definition}</span></div>
                      ))}
                    </div>
                  )}
                  {lastResult.assumptions?.length > 0 && (
                    <div>
                      <p className="text-muted-foreground uppercase tracking-wider font-semibold mb-1">Assumptions</p>
                      {lastResult.assumptions.map((a, i) => <p key={i} className="text-foreground/80">{a}</p>)}
                    </div>
                  )}
                  {isAdmin && lastResult.guard_note && (
                    <div data-testid="explore-guard-note">
                      <p className="text-amber-600 dark:text-amber-400 uppercase tracking-wider font-semibold mb-1 flex items-center gap-1">
                        <ShieldCheck className="w-3 h-3" /> Join guard
                      </p>
                      <p className="text-foreground/80">{lastResult.guard_note}</p>
                    </div>
                  )}
                </>
              );
            })()}
          </div>
        </div>
      )}

      {/* Query View Dialog */}
      {exploreQueryView && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50" onClick={() => setExploreQueryView(null)}>
          <div className="glass-card rounded-2xl shadow-2xl border border-border/50 w-full max-w-2xl mx-4 max-h-[80vh] overflow-auto" onClick={e => e.stopPropagation()}>
            <div className="p-4 border-b border-border/30 flex items-center justify-between">
              <h3 className="text-sm font-heading font-semibold flex items-center gap-2"><Code className="w-4 h-4 text-primary" /> Database Query</h3>
              <button onClick={() => setExploreQueryView(null)} className="text-muted-foreground hover:text-foreground" data-testid="close-query-view">&times;</button>
            </div>
            <div className="p-4 space-y-4 text-xs">
              {exploreQueryView._debugQuery ? (
                <>
                  {/* Source info */}
                  <div className="flex items-center gap-3">
                    {exploreQueryView._debugQuery.source_name && <div><p className="text-muted-foreground text-[9px] uppercase mb-0.5">Data Source</p><p className="text-foreground font-medium">{exploreQueryView._debugQuery.source_name}</p></div>}
                    <div><p className="text-muted-foreground text-[9px] uppercase mb-0.5">Type</p><Badge variant="outline" className="text-[9px]">{exploreQueryView._debugQuery.source_type || 'mongodb'}</Badge></div>
                    {exploreQueryView._debugQuery.dialect && exploreQueryView._debugQuery.dialect !== 'mongodb' && <div><p className="text-muted-foreground text-[9px] uppercase mb-0.5">Dialect</p><Badge variant="outline" className="text-[9px]">{exploreQueryView._debugQuery.dialect}</Badge></div>}
                    <div><p className="text-muted-foreground text-[9px] uppercase mb-0.5">Executed Via</p><Badge className={`text-[9px] ${exploreQueryView._debugQuery.executed_via && exploreQueryView._debugQuery.executed_via !== 'mongodb' && exploreQueryView._debugQuery.executed_via !== 'mongodb_fallback' ? 'bg-emerald-500/15 text-emerald-400 border-emerald-500/20' : 'bg-muted/30 text-muted-foreground border-border/30'}`}>{exploreQueryView._debugQuery.executed_via || 'mongodb'}{exploreQueryView._debugQuery.executed_via === 'mongodb_fallback' ? ' (fallback)' : ''}</Badge></div>
                  </div>

                  {/* SQL query (shown prominently for SQL sources) */}
                  {exploreQueryView._debugQuery.sql && (
                    <div>
                      <div className="flex items-center justify-between mb-1.5">
                        <p className="text-muted-foreground text-[9px] uppercase font-semibold">SQL Query</p>
                        <button onClick={() => { navigator.clipboard.writeText(exploreQueryView._debugQuery.sql); toast.success('SQL copied'); }} className="text-[9px] text-primary hover:text-primary/80 flex items-center gap-1" data-testid="copy-sql"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-3 h-3"><rect width="14" height="14" x="8" y="8" rx="2" ry="2"/><path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2"/></svg> Copy SQL</button>
                      </div>
                      <pre className="font-mono text-[11px] text-sky-300/90 bg-[#0d1117] p-4 rounded-xl overflow-auto max-h-48 whitespace-pre-wrap leading-relaxed border border-border/20" data-testid="sql-code">{exploreQueryView._debugQuery.sql}</pre>
                      {exploreQueryView._debugQuery.sql_table && <p className="text-[9px] text-muted-foreground/80 mt-1">Table: {exploreQueryView._debugQuery.sql_table}</p>}
                    </div>
                  )}

                  {/* Formula Audit — the exact grounded SQL each CERTIFIED measure compiled to */}
                  {exploreQueryView._measure_compiled_sql && Object.keys(exploreQueryView._measure_compiled_sql).length > 0 && (
                    <div data-testid="formula-audit">
                      <p className="text-muted-foreground text-[9px] uppercase font-semibold mb-1.5 flex items-center gap-1.5">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-3 h-3 text-emerald-400"><path d="m9 12 2 2 4-4"/><circle cx="12" cy="12" r="10"/></svg>
                        Formula Audit — certified measures
                      </p>
                      <div className="space-y-2">
                        {Object.entries(exploreQueryView._measure_compiled_sql).map(([name, sql]) => (
                          <div key={name} className="rounded-xl border border-emerald-500/30 bg-emerald-500/[0.06] p-3" data-testid={`formula-audit-${name}`}>
                            <p className="text-[11px] font-semibold text-emerald-900 dark:text-emerald-300 mb-1">{name}</p>
                            <pre className="font-mono text-[10px] text-emerald-900/90 dark:text-emerald-200/80 whitespace-pre-wrap leading-relaxed">{sql}</pre>
                          </div>
                        ))}
                      </div>
                      <p className="text-[9px] text-muted-foreground/80 mt-1">The governed definition each certified measure resolves to — fields grounded to their summary columns.</p>
                    </div>
                  )}


                  {/* MongoDB pipeline (collapsed by default if SQL is shown) */}
                  <details open={!exploreQueryView._debugQuery.sql}>
                    <summary className="text-[9px] text-muted-foreground uppercase font-semibold cursor-pointer hover:text-foreground transition-colors">
                      MongoDB Pipeline {exploreQueryView._debugQuery.sql ? '(internal)' : ''}
                    </summary>
                    <div className="mt-1.5">
                      <p className="text-[9px] text-muted-foreground mb-1">Collection: <span className="font-mono text-primary">{exploreQueryView._debugQuery.collection}</span></p>
                      <pre className="font-mono text-[10px] text-emerald-300/80 bg-[#0d1117] p-3 rounded-xl overflow-auto max-h-40 whitespace-pre-wrap leading-relaxed border border-border/20" data-testid="pipeline-code">{JSON.stringify(exploreQueryView._debugQuery.pipeline, null, 2)}</pre>
                    </div>
                  </details>
                  {/* Aggregate-router decision — surfaces which physical table
                      the router picked and why the alternatives lost. */}
                  {exploreQueryView._debugQuery.route_decision && exploreQueryView._debugQuery.route_decision.chosen_entity_id && (
                    <details open className="mt-2" data-testid="route-decision">
                      <summary className="cursor-pointer text-[10px] text-muted-foreground hover:text-foreground font-medium flex items-center gap-1.5">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-3 h-3"><polyline points="3 6 8 6 8 11 12 11 12 18 21 18"/><line x1="16" x2="21" y1="6" y2="6"/></svg>
                        Aggregate Router
                        <span className="ml-1 px-1.5 py-0.5 rounded text-[9px] font-mono bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">{exploreQueryView._debugQuery.route_decision.chosen_name}</span>
                        <button
                          type="button"
                          data-testid="explain-route-btn"
                          onClick={(e) => { e.preventDefault(); e.stopPropagation(); setRouteExplain({ open: true, rd: exploreQueryView._debugQuery.route_decision }); }}
                          className="ml-auto inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-medium bg-primary/10 text-primary border border-primary/20 hover:bg-primary/20 transition-colors"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-2.5 h-2.5"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><line x1="12" x2="12.01" y1="17" y2="17"/></svg>
                          Explain this route
                        </button>
                      </summary>
                      <div className="mt-1.5 space-y-2 text-[10px] pl-1">
                        {(() => {
                          const rd = exploreQueryView._debugQuery.route_decision;
                          const usingCanonical = rd.fallback_forced || rd.chosen_entity_id === rd.canonical_entity_id;
                          const reason = (rd.alternatives_rejected && rd.alternatives_rejected.length) ? rd.alternatives_rejected[0].reason : null;
                          return (
                            <div className={`px-2 py-1.5 rounded border text-[10px] leading-relaxed ${usingCanonical ? 'bg-sky-500/10 border-sky-500/20 text-sky-700 dark:text-sky-300' : 'bg-emerald-500/10 border-emerald-500/20 text-emerald-700 dark:text-emerald-300'}`} data-testid="route-reason">
                              {usingCanonical ? (
                                <>Querying the <span className="font-mono font-medium">{rd.chosen_name}</span> transaction fact{rd.chosen_table ? <> <span className="opacity-70 font-mono">({rd.chosen_table})</span></> : null}{reason ? <> — {reason}</> : <> — no summary could serve this query at the requested grain</>}.</>
                              ) : (
                                <>Accelerated by the <span className="font-mono font-medium">{rd.chosen_name}</span> summary{rd.chosen_table ? <> <span className="opacity-70 font-mono">({rd.chosen_table})</span></> : null} — a pre-aggregated table that matches the requested grain &amp; measures.</>
                              )}
                            </div>
                          );
                        })()}
                        {exploreQueryView._debugQuery.route_decision.alternatives_considered_ok?.length > 0 && (
                          <div>
                            <p className="text-muted-foreground text-[9px] uppercase mb-1">Considered (lowest cost wins)</p>
                            <div className="space-y-0.5">
                              {exploreQueryView._debugQuery.route_decision.alternatives_considered_ok.map((c) => (
                                <div key={c.entity_id} className={`flex items-center justify-between px-2 py-1 rounded font-mono text-[10px] ${c.entity_id === exploreQueryView._debugQuery.route_decision.chosen_entity_id ? 'bg-emerald-500/10 border border-emerald-500/20 text-emerald-700 dark:text-emerald-300' : 'bg-muted/20 border border-border/20 text-muted-foreground'}`}>
                                  <span>{c.name}{c.entity_id === exploreQueryView._debugQuery.route_decision.canonical_entity_id ? <span className="ml-1 opacity-60">· transaction fact</span> : <span className="ml-1 opacity-60">· summary</span>}</span>
                                  <span className="tabular-nums">{c.cost.toLocaleString()} rows est.</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        {exploreQueryView._debugQuery.route_decision.alternatives_rejected?.filter((r) => r.entity_id !== exploreQueryView._debugQuery.route_decision.canonical_entity_id).length > 0 && (
                          <div>
                            <p className="text-muted-foreground text-[9px] uppercase mb-1">Rejected</p>
                            <div className="space-y-0.5">
                              {exploreQueryView._debugQuery.route_decision.alternatives_rejected.filter((r) => r.entity_id !== exploreQueryView._debugQuery.route_decision.canonical_entity_id).map((r) => (
                                <div key={r.entity_id} className="px-2 py-1 rounded bg-red-500/5 border border-red-500/15 text-[10px]">
                                  <span className="font-mono text-red-400">{r.name}</span>
                                  <span className="ml-2 text-muted-foreground/70">— {r.reason}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        {exploreQueryView._debugQuery.route_decision.watermark && (
                          <p className="text-[9px] text-muted-foreground/80">Watermark: <span className="font-mono">{exploreQueryView._debugQuery.route_decision.watermark}</span></p>
                        )}
                      </div>
                    </details>
                  )}
                </>
              ) : (
                <div className="p-3 rounded-lg bg-amber-500/5 border border-amber-500/20 text-[10px] text-amber-400">
                  Database query not available for this response. The query is captured when AI returns dimensions and measures.
                </div>
              )}
              {/* Semantic interpretation */}
              <div className="border-t border-border/30 pt-3">
                <p className="text-muted-foreground text-[9px] uppercase mb-2 font-semibold">Semantic Query Details</p>
                <div className="grid grid-cols-2 gap-2">
                  <div><p className="text-muted-foreground text-[9px] mb-0.5">Model</p><p className="font-medium text-foreground">{_wsModelName || 'Current model'}</p></div>
                  <div><p className="text-muted-foreground text-[9px] mb-0.5">Output</p><p className="font-mono text-foreground">{exploreQueryView.chart_type || exploreQueryView.output_type || 'N/A'}</p></div>
                  <div><p className="text-muted-foreground text-[9px] mb-0.5">Dimensions</p><p className="font-mono text-foreground">{(exploreQueryView.dimensions || []).join(', ') || 'None'}</p></div>
                  <div><p className="text-muted-foreground text-[9px] mb-0.5">Measures</p><p className="font-mono text-foreground">{(exploreQueryView.measures || []).join(', ') || 'None'}</p></div>
                  {exploreQueryView.sort_by && <div><p className="text-muted-foreground text-[9px] mb-0.5">Sort</p><p className="font-mono text-foreground">{exploreQueryView.sort_by} ({exploreQueryView.sort_order || 'asc'})</p></div>}
                  {exploreQueryView.limit && <div><p className="text-muted-foreground text-[9px] mb-0.5">Limit</p><p className="font-mono text-foreground">{exploreQueryView.limit}</p></div>}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* AI Raw Response Dialog (admin only) */}
      {aiRawView && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50" onClick={() => setAiRawView(null)}>
          <div className="glass-card rounded-2xl shadow-2xl border border-border/50 w-full max-w-2xl mx-4 max-h-[80vh] overflow-auto" onClick={e => e.stopPropagation()}>
            <div className="p-4 border-b border-border/30 flex items-center justify-between">
              <h3 className="text-sm font-heading font-semibold flex items-center gap-2"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4 text-violet-400"><rect width="16" height="16" x="4" y="4" rx="2"/><rect width="6" height="6" x="9" y="9" rx="1"/><path d="M15 2v2"/><path d="M15 20v2"/><path d="M2 15h2"/><path d="M2 9h2"/><path d="M20 15h2"/><path d="M20 9h2"/><path d="M9 2v2"/><path d="M9 20v2"/></svg> AI Engine Response</h3>
              <button onClick={() => setAiRawView(null)} className="text-muted-foreground hover:text-foreground" data-testid="close-ai-raw">&times;</button>
            </div>
            <div className="p-4 space-y-3 text-xs">
              {aiRawView.answer && <div><p className="text-muted-foreground text-[9px] uppercase mb-1">Answer</p><p className="text-foreground">{aiRawView.answer}</p></div>}
              {aiRawView.narrative && <div><p className="text-muted-foreground text-[9px] uppercase mb-1">Narrative</p><p className="text-foreground/80">{aiRawView.narrative}</p></div>}
              {aiRawView.confidence && <div><p className="text-muted-foreground text-[9px] uppercase mb-1">Confidence</p><p className="font-mono text-foreground">{aiRawView.confidence}</p></div>}
              {aiRawView.output_type && <div><p className="text-muted-foreground text-[9px] uppercase mb-1">Output Type</p><p className="font-mono text-foreground">{aiRawView.output_type}</p></div>}
              {aiRawView.chart_type && <div><p className="text-muted-foreground text-[9px] uppercase mb-1">Chart Type</p><p className="font-mono text-foreground">{aiRawView.chart_type}</p></div>}
              {aiRawView.dimensions?.length > 0 && <div><p className="text-muted-foreground text-[9px] uppercase mb-1">Dimensions</p><p className="font-mono text-foreground">{aiRawView.dimensions.join(', ')}</p></div>}
              {aiRawView.measures?.length > 0 && <div><p className="text-muted-foreground text-[9px] uppercase mb-1">Measures</p><p className="font-mono text-foreground">{aiRawView.measures.join(', ')}</p></div>}
              {aiRawView.follow_up_suggestions?.length > 0 && <div><p className="text-muted-foreground text-[9px] uppercase mb-1">Suggested Follow-ups</p><ul className="text-foreground/80 space-y-0.5">{aiRawView.follow_up_suggestions.map((s, i) => <li key={i} className="text-[10px]">{i + 1}. {s}</li>)}</ul></div>}
              <div className="border-t border-border/30 pt-3"><p className="text-muted-foreground text-[9px] uppercase mb-1">Full AI Response (JSON)</p><pre className="font-mono text-[9px] text-emerald-300/80 bg-[#0d1117] p-3 rounded-lg overflow-auto max-h-52 whitespace-pre-wrap">{JSON.stringify(aiRawView, null, 2)}</pre></div>
            </div>
          </div>
        </div>
      )}
      <InvestigatePanel open={fullInvestigateOpen} onOpenChange={setFullInvestigateOpen} target={fullInvestigateTarget} />
      <StartDiscussionDialog
        open={discDialog.open}
        onOpenChange={(v) => setDiscDialog(d => ({ ...d, open: v }))}
        defaultQuestion={discDialog.msg?.result?.answer || discDialog.msg?.content || ''}
        screenshotData={discDialog.screenshot || ''}
        dataContext={discDialog.msg ? {
          ...(discDialog.point || {}),
          session_id: sessionId,
          model_id: selectedModel,
          dimensions: discDialog.msg.result?.dimensions,
          measures: discDialog.msg.result?.measures,
          chart_type: discDialog.msg.result?.chart_type,
          filters_applied: discDialog.msg.result?.filters_applied || [],
        } : {}}
        reportState={discDialog.msg ? {
          source: 'explore',
          prompt: discDialog.msg.content || '',
          narrative: discDialog.msg.result?.narrative || '',
          answer: discDialog.msg.result?.answer || '',
        } : {}}
        onCreated={(disc) => { api.getDiscussionIndex().then(r => setDiscIndex(r?.by_session || {})).catch(() => {}); navigate(`/threads?id=${disc.discussion_id}`); }}
      />

      {/* Add-to-existing-dashboard picker */}
      <Dialog open={dashboardPicker.open} onOpenChange={(v) => setDashboardPicker(dp => ({ ...dp, open: v }))}>
        <DialogContent className="max-w-md" data-testid="add-to-dashboard-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-sm">
              <LayoutDashboard className="w-4 h-4 text-primary" /> Add to dashboard
            </DialogTitle>
          </DialogHeader>
          <p className="text-[11px] text-muted-foreground -mt-1">
            Pick a dashboard on this model to add this visual to. Only dashboards built on the current model are shown.
          </p>
          {/* Visual title */}
          <div className="mt-1">
            <label className="text-[10px] text-muted-foreground">Visual title</label>
            <input
              value={addTitle}
              onChange={e => setAddTitle(e.target.value)}
              placeholder="Give this visual a title…"
              className="w-full mt-0.5 px-2.5 py-1.5 rounded-lg bg-muted/50 border border-border/50 text-xs text-foreground outline-none focus:border-primary/50"
              data-testid="add-visual-title"
            />
          </div>
          {/* Position toggle */}
          <div className="flex items-center gap-2 mt-1">
            <span className="text-[10px] text-muted-foreground">Add to</span>
            <div className="flex items-center gap-1">
              <button onClick={() => setAddPosition('top')} className={`px-2 py-1 rounded text-[10px] ${addPosition === 'top' ? 'bg-primary/20 text-primary' : 'bg-muted/50 text-muted-foreground'}`} data-testid="add-position-top">Top</button>
              <button onClick={() => setAddPosition('bottom')} className={`px-2 py-1 rounded text-[10px] ${addPosition === 'bottom' ? 'bg-primary/20 text-primary' : 'bg-muted/50 text-muted-foreground'}`} data-testid="add-position-bottom">Bottom</button>
            </div>
            <span className="text-[9px] text-muted-foreground/80">of the dashboard</span>
          </div>
          {/* New dashboard */}
          <button
            onClick={() => { const r = dashboardPicker.result; const vs = dashboardPicker.vizState; const t = addTitle; setDashboardPicker({ open: false }); saveAsInsight(r, vs, t); }}
            disabled={!!dashboardPicker.saving}
            className="w-full flex items-center gap-2 px-3 py-2 mt-1 rounded-lg border border-dashed border-primary/40 text-primary hover:bg-primary/10 transition-colors text-left disabled:opacity-50"
            data-testid="add-to-dashboard-new"
          >
            <Plus className="w-3.5 h-3.5 shrink-0" />
            <span className="text-xs font-medium">New dashboard…</span>
          </button>
          <div className="max-h-64 overflow-y-auto space-y-1.5 mt-1">
            {(dashboardPicker.dashboards || []).length === 0 ? (
              <div className="text-center py-5 text-[11px] text-muted-foreground" data-testid="add-to-dashboard-empty">
                No existing dashboards on this model yet.
              </div>
            ) : (
              (dashboardPicker.dashboards || []).map(rep => (
                <button
                  key={rep.report_id}
                  onClick={() => confirmAddToDashboard(rep.report_id)}
                  disabled={!!dashboardPicker.saving}
                  className="w-full flex items-center justify-between gap-2 px-3 py-2 rounded-lg bg-muted/40 hover:bg-primary/10 border border-border/40 hover:border-primary/40 transition-colors text-left disabled:opacity-50"
                  data-testid={`add-to-dashboard-option-${rep.report_id}`}
                >
                  <div className="min-w-0">
                    <p className="text-xs font-medium text-foreground truncate">{rep.name}</p>
                    <p className="text-[9px] text-muted-foreground truncate">{(rep.config?.visuals || []).length} visual(s)</p>
                  </div>
                  {dashboardPicker.saving === rep.report_id
                    ? <Loader2 className="w-3.5 h-3.5 animate-spin text-primary shrink-0" />
                    : <Plus className="w-3.5 h-3.5 text-primary shrink-0" />}
                </button>
              ))
            )}
          </div>
          {/* Duplicate-visual warning (non-blocking) */}
          {dashboardPicker.dupWarn && (
            <div className="mt-2 rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 space-y-2" data-testid="duplicate-visual-warning">
              <p className="text-[11px] text-amber-600 dark:text-amber-300">
                A matching visual{dashboardPicker.dupWarn.title ? ` (“${dashboardPicker.dupWarn.title}”)` : ''} already exists on <span className="font-medium">{dashboardPicker.dupWarn.name}</span>. Add it anyway?
              </p>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => confirmAddToDashboard(dashboardPicker.dupWarn.reportId, true)}
                  disabled={!!dashboardPicker.saving}
                  className="px-2.5 py-1 rounded-md text-[10px] font-medium bg-amber-500/20 text-amber-700 dark:text-amber-200 border border-amber-500/40 hover:bg-amber-500/30 disabled:opacity-50"
                  data-testid="duplicate-add-anyway"
                >
                  Add anyway
                </button>
                <button
                  onClick={() => setDashboardPicker(dp => ({ ...dp, dupWarn: null }))}
                  className="px-2.5 py-1 rounded-md text-[10px] text-muted-foreground hover:text-foreground"
                  data-testid="duplicate-cancel"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
    {voiceDock}
    {alertDialogJsx}
    {presetDialogJsx}
    </>
  );
}


function ExploreHelpPanel({ onPick }) {
  const insight = getTerm('insights', 'singular');
  const [q, setQ] = useState('');
  const sections = [
    {
      icon: Search, title: 'What you can ask',
      items: [
        'Totals & aggregates — "total written premium this year"',
        'Breakdowns — "written premium by line of business"',
        'Trends over time — "monthly earned premium for 2024"',
        'Comparisons — "premium by state, this year vs last"',
        'Rankings / Top-N — "top 10 agencies by loss ratio"',
        'Filtering — "claims in California over $50k"',
        'Ratios & calculations — "loss ratio by product"',
        'Follow-ups — ask in plain English to refine the last answer',
      ],
    },
    {
      icon: BarChart3, title: 'Chart types',
      items: [
        'Bar & horizontal bar', 'Line & area', 'Pie & donut',
        'Table', 'Combo / multi-series (color by a dimension)',
        'Say "show as a line chart" / "as a table" to switch',
      ],
    },
    {
      icon: Zap, title: 'View controls (just type them)',
      items: [
        'Sort — "sort by premium descending"',
        'Top-N — "top 5 only"',
        'Color/series — "break out by region"',
        'Change date grain — "by quarter" / "by month"',
        'Data labels, reference lines, colors & palette',
      ],
    },
    {
      icon: Sparkles, title: 'Do more with a result',
      items: [
        `Save as ${insight} (creates a new dashboard)`,
        'Add to dashboard (add this visual to an existing one)',
        'Create Alert — get pinged when a metric crosses a threshold',
        'Investigate Why — instant root-cause breakdown',
        'Investigate this — full, saved investigation',
        'Start a discussion & share the session with teammates',
      ],
    },
  ];
  const examples = [
    'Written premium by line of business',
    'Top 10 agencies by loss ratio',
    'Monthly earned premium for 2024',
    'Loss ratio by state this year vs last',
  ];
  const ql = q.trim().toLowerCase();
  const filteredSections = ql
    ? sections.map(s => ({ ...s, items: s.items.filter(it => it.toLowerCase().includes(ql) || s.title.toLowerCase().includes(ql)) })).filter(s => s.items.length)
    : sections;
  const filteredExamples = ql ? examples.filter(e => e.toLowerCase().includes(ql)) : examples;
  return (
    <div className="max-w-4xl glass-card rounded-2xl p-5 border border-primary/20" data-testid="explore-help-panel">
      <div className="flex items-center gap-2 mb-3">
        <Sparkles className="w-4 h-4 text-primary" />
        <p className="text-sm font-heading font-semibold text-foreground">Here's what I can do in AI Explore</p>
      </div>
      <div className="relative mb-3">
        <Search className="w-3.5 h-3.5 text-muted-foreground absolute left-2.5 top-1/2 -translate-y-1/2" />
        <input
          value={q}
          onChange={e => setQ(e.target.value)}
          placeholder="Search capabilities and examples…"
          className="w-full pl-8 pr-2.5 py-1.5 rounded-lg bg-muted/50 border border-border/50 text-xs text-foreground outline-none focus:border-primary/50"
          data-testid="help-search"
        />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {filteredSections.map((s) => (
          <div key={s.title}>
            <p className="text-[11px] font-semibold text-foreground flex items-center gap-1.5 mb-1.5">
              <s.icon className="w-3.5 h-3.5 text-primary" /> {s.title}
            </p>
            <ul className="space-y-1">
              {s.items.map((it, i) => (
                <li key={i} className="text-[11px] text-muted-foreground leading-snug flex gap-1.5">
                  <span className="text-primary/60 mt-[3px]">•</span><span>{it}</span>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
      {filteredSections.length === 0 && filteredExamples.length === 0 && (
        <p className="text-[11px] text-muted-foreground py-3 text-center" data-testid="help-search-empty">No matches for “{q}”.</p>
      )}
      {filteredExamples.length > 0 && (
        <div className="mt-4 pt-3 border-t border-border/30">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground/70 mb-2">Try one</p>
          <div className="flex flex-wrap gap-1.5">
            {filteredExamples.map((ex) => (
              <button
                key={ex}
                onClick={() => onPick(ex)}
                className="px-2.5 py-1 rounded-full bg-primary/10 hover:bg-primary/20 text-primary text-[10px] font-medium transition-colors"
                data-testid="explore-help-example"
              >
                {ex}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
